import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../../api/apiClient";
import Modal from "../../ui/Modal";
import Breadcrumb from "../../ui/Breadcrumb";
import ContextMenu from "../../ui/ContextMenu";
import ColorPicker from "../../ui/ColorPicker";
import ContrastIndicator from "../../ui/ContrastIndicator";
import { normalizeHex } from "../../ui/color";
import DbSteps from "../../ui/DbSteps";
import Skeleton from "../../ui/Skeleton";
import {
  IconAlertTriangle,
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBan,
  IconCards,
  IconCarousel,
  IconChevronDown,
  IconChevronRight,
  IconDroplet,
  IconCheck,
  IconCopy,
  IconEdit,
  IconFileText,
  IconFolder,
  IconGlobe,
  IconImage,
  IconLayers,
  IconLayoutGrid,
  IconLink,
  IconPlus,
  IconSparkles,
  IconTag,
  IconTable,
  IconTrash,
  IconType,
  IconVideo,
} from "../../ui/icons";

function useApiBaseUrl() {
  const raw = process.env.REACT_APP_API_BASE_URL;
  if (raw && raw.trim()) return raw.replace(/\/+$/, "");
  return "/api";
}

function mediaInlineUrl(apiBaseUrl, siteId, id) {
  const base = String(apiBaseUrl || "/api").replace(/\/+$/, "");
  const qs = new URLSearchParams({
    site_id: String(Number(siteId)),
    id: String(Number(id)),
  });
  return `${base}/media.php?${qs.toString()}`;
}

function parseMediaIdFromUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  try {
    const u = new URL(raw, "http://local");
    if (!String(u.pathname || "").endsWith("/media.php")) return null;
    const id = u.searchParams.get("id");
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function normalizeAssetsBaseUrl(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  return raw.replace(/\/+$/, "");
}

function isRelativeAssetPath(input) {
  const raw = String(input || "").trim();
  if (!raw) return false;
  if (/^(https?:)?\/\//i.test(raw)) return false;
  if (raw.startsWith("/") || raw.startsWith("data:")) return false;
  return true;
}

function mediaAssetRelativePath(asset) {
  const stored = String(asset?.stored_name || "").trim();
  if (stored) return `img/${stored}`;
  const rel = String(asset?.storage_relpath || "").trim();
  if (!rel) return "";
  const parts = rel.split("/").filter(Boolean);
  const file = parts[parts.length - 1] || "";
  return file ? `img/${file}` : "";
}

function mediaAssetSourceValue(asset, { apiBaseUrl, siteId, assetsBaseUrl }) {
  const preferred = mediaAssetRelativePath(asset);
  if (preferred && normalizeAssetsBaseUrl(assetsBaseUrl)) return preferred;
  return mediaInlineUrl(apiBaseUrl, siteId, Number(asset?.id));
}

function resolveAssetSourceUrl(value, assetsBaseUrl) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!isRelativeAssetPath(raw)) return raw;
  const base = normalizeAssetsBaseUrl(assetsBaseUrl);
  if (!base) return raw;
  return `${base}/${raw.replace(/^\/+/, "")}`;
}

function mediaAssetDisplayName(asset) {
  if (!asset) return "";
  return (
    String(asset.original_name || "").trim() ||
    String(asset.note || "").trim() ||
    (asset.id != null ? `Media #${asset.id}` : "")
  );
}

function isEventFromButton(e) {
  const t = e?.target;
  const ct = e?.currentTarget;
  const el = t && t.nodeType === 3 ? t.parentElement : t; // text node -> parent
  if (el && typeof el.closest === "function" && el.closest("button"))
    return true;
  if (ct && ct.tagName === "BUTTON") return true;
  if (ct && typeof ct.closest === "function" && ct.closest("button"))
    return true;
  return false;
}

function normalizeFlexDirection(input, fallback = "row") {
  const raw = String(input || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  if (
    raw === "row" ||
    raw === "row-reverse" ||
    raw === "column" ||
    raw === "column-reverse"
  )
    return raw;
  return String(fallback || "row");
}

function normalizeContainerSlot(input, fallback = 0) {
  const n = Number(input);
  if (!Number.isFinite(n)) return Number(fallback) || 0;
  return Math.max(0, Math.floor(n));
}

function normalizeContainerSlotOrder(input, fallback = 0) {
  const n = Number(input);
  if (!Number.isFinite(n)) return Number(fallback) || 0;
  return n;
}

function fingerprintProjectDraft(draft) {
  if (!draft) return "";
  const tagIds = Array.isArray(draft.tag_ids)
    ? draft.tag_ids.map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  tagIds.sort((a, b) => a - b);
  const categoryIds = Array.isArray(draft.category_ids)
    ? draft.category_ids.map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  categoryIds.sort((a, b) => a - b);
  return JSON.stringify({
    id: Number(draft.id),
    name: String(draft.name || ""),
    cover_image_url: String(draft.cover_image_url || ""),
    background_image_url: String(draft.background_image_url || ""),
    visibility: String(draft.visibility || "public"),
    tag_ids: tagIds,
    category_ids: categoryIds,
    content: draft.content || null,
  });
}

function cloneDraft(draft) {
  if (!draft) return null;
  try {
    return JSON.parse(JSON.stringify(draft));
  } catch {
    return { ...draft };
  }
}

function defaultContent() {
  return {
    version: 1,
    meta: { description: "", summary_lines: ["", "", ""] },
    sections: [],
  };
}

function newId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const MAX_CONTAINER_DEPTH = 5;

function normalizeContainerList(raw, { depth = 1 } = {}) {
  const list = Array.isArray(raw) ? raw : [];
  const out = list
    .filter((c) => c && typeof c === "object" && !Array.isArray(c))
    .map((c, idx) => {
      const id = String(c.id || "") || `ctr_${depth}_${idx}`;
      const name = String(c.name || "") || "Conteneur";
      const direction = normalizeFlexDirection(c.direction, "row");
      const extra_class = String(c.extra_class || "");
      const slot = normalizeContainerSlot(c.slot, 0);
      const slot_order = normalizeContainerSlotOrder(c.slot_order, idx);
      const blocks = Array.isArray(c.blocks) ? c.blocks : [];
      const containers = normalizeContainerList(c.containers, {
        depth: depth + 1,
      });
      return {
        ...c,
        id,
        name,
        direction,
        extra_class,
        slot,
        slot_order,
        blocks,
        containers,
      };
    });
  out.sort((a, b) => {
    const as = Number(a?.slot);
    const bs = Number(b?.slot);
    if (as !== bs) return as - bs;
    const ao = Number(a?.slot_order);
    const bo = Number(b?.slot_order);
    if (ao !== bo) return ao - bo;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  });
  return out;
}

function cloneContainersForMutation(list) {
  return (Array.isArray(list) ? list : []).map((c) => ({
    ...(c || {}),
    blocks: Array.isArray(c?.blocks) ? [...c.blocks] : [],
    slot: c?.slot,
    slot_order: c?.slot_order,
    containers: cloneContainersForMutation(c?.containers),
  }));
}

function findContainerPath(list, containerId) {
  const target = String(containerId || "");
  if (!target) return null;
  const arr = Array.isArray(list) ? list : [];
  for (let i = 0; i < arr.length; i += 1) {
    const c = arr[i];
    if (!c || typeof c !== "object") continue;
    if (String(c.id || "") === target) return [i];
    const childPath = findContainerPath(c.containers, target);
    if (childPath) return [i, ...childPath];
  }
  return null;
}

function getContainerMetaAtPath(list, path) {
  const p = Array.isArray(path) ? path : null;
  if (!p || p.length === 0) return null;
  let parentList = Array.isArray(list) ? list : [];
  for (let i = 0; i < p.length - 1; i += 1) {
    const c = parentList[p[i]];
    if (!c || typeof c !== "object") return null;
    parentList = Array.isArray(c.containers) ? c.containers : [];
  }
  const index = p[p.length - 1];
  const container = parentList[index] || null;
  return { parentList, index, container };
}

function findContainerById(list, containerId) {
  const path = findContainerPath(list, containerId);
  const meta = path ? getContainerMetaAtPath(list, path) : null;
  return meta?.container || null;
}

function countBlocksDeepInContainer(container) {
  const c = container && typeof container === "object" ? container : null;
  if (!c) return 0;
  const own = Array.isArray(c.blocks) ? c.blocks.length : 0;
  const children = Array.isArray(c.containers) ? c.containers : [];
  return (
    own +
    children.reduce((sum, child) => sum + countBlocksDeepInContainer(child), 0)
  );
}

function countContainersDeepInContainer(container) {
  const c = container && typeof container === "object" ? container : null;
  if (!c) return 0;
  const children = Array.isArray(c.containers) ? c.containers : [];
  return (
    children.length +
    children.reduce(
      (sum, child) => sum + countContainersDeepInContainer(child),
      0
    )
  );
}

function countBlocksDeepInContainerList(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr.reduce((sum, c) => sum + countBlocksDeepInContainer(c), 0);
}

function firstBlockInContainerList(list) {
  const arr = Array.isArray(list) ? list : [];
  for (const c of arr) {
    const blocks = Array.isArray(c?.blocks) ? c.blocks : [];
    if (blocks.length) return blocks[0];
    const child = firstBlockInContainerList(c?.containers);
    if (child) return child;
  }
  return null;
}

function cloneContainerSubtreeWithNewIds(
  container,
  { nameSuffix = " (copie)" } = {}
) {
  function deepClone(v) {
    try {
      return JSON.parse(JSON.stringify(v));
    } catch {
      return v && typeof v === "object" ? { ...(v || {}) } : v;
    }
  }

  function cloneOne(src, depth = 1) {
    const s = src && typeof src === "object" ? src : {};
    const next = deepClone(s);
    next.id = newId("ctr");
    const baseName = String(s.name || "").trim() || "Conteneur";
    next.name = depth === 1 ? `${baseName}${nameSuffix}` : baseName;
    next.direction = normalizeFlexDirection(s.direction, "row");
    next.extra_class = String(s.extra_class || "");
    next.slot = normalizeContainerSlot(s.slot, 0);
    next.slot_order = normalizeContainerSlotOrder(s.slot_order, 0);
    next.blocks = (Array.isArray(s.blocks) ? s.blocks : []).map((b) => {
      const bb = deepClone(b || {});
      bb.id = newId("blk");
      return bb;
    });
    next.containers =
      depth >= MAX_CONTAINER_DEPTH
        ? []
        : (Array.isArray(s.containers) ? s.containers : []).map((c) =>
            cloneOne(c, depth + 1)
          );
    return next;
  }

  return cloneOne(container, 1);
}

function toggleInlineHtmlTag(
  value,
  selectionStart,
  selectionEnd,
  { openTag, closeTag }
) {
  const text = String(value || "");
  const open = String(openTag || "");
  const close = String(closeTag || "");
  let start = Number(selectionStart ?? 0);
  let end = Number(selectionEnd ?? 0);
  if (!Number.isFinite(start) || start < 0) start = 0;
  if (!Number.isFinite(end) || end < 0) end = 0;
  if (start > end) [start, end] = [end, start];

  if (!open || !close)
    return { nextValue: text, nextStart: start, nextEnd: end };

  if (start === end) {
    const nextValue = `${text.slice(0, start)}${open}${close}${text.slice(
      end
    )}`;
    const caret = start + open.length;
    return { nextValue, nextStart: caret, nextEnd: caret };
  }

  const selected = text.slice(start, end);

  if (selected.startsWith(open) && selected.endsWith(close)) {
    const inner = selected.slice(open.length, selected.length - close.length);
    const nextValue = `${text.slice(0, start)}${inner}${text.slice(end)}`;
    const nextStart = start;
    const nextEnd = start + inner.length;
    return { nextValue, nextStart, nextEnd };
  }

  const beforeStart = start - open.length;
  const afterEnd = end + close.length;
  if (beforeStart >= 0 && afterEnd <= text.length) {
    const before = text.slice(beforeStart, start);
    const after = text.slice(end, afterEnd);
    if (before === open && after === close) {
      const nextValue = `${text.slice(0, beforeStart)}${text.slice(
        start,
        end
      )}${text.slice(afterEnd)}`;
      const nextStart = beforeStart;
      const nextEnd = beforeStart + (end - start);
      return { nextValue, nextStart, nextEnd };
    }
  }

  const nextValue = `${text.slice(
    0,
    start
  )}${open}${selected}${close}${text.slice(end)}`;
  const nextStart = start + open.length;
  const nextEnd = end + open.length;
  return { nextValue, nextStart, nextEnd };
}

function handleTextareaFormatShortcuts(
  e,
  applyNextValue,
  { enabled = true } = {}
) {
  if (!enabled) return false;
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return false;
  const key = String(e.key || "").toLowerCase();
  const cfg =
    key === "b"
      ? { openTag: "<strong>", closeTag: "</strong>" }
      : key === "i"
      ? { openTag: "<em>", closeTag: "</em>" }
      : key === "u"
      ? { openTag: "<u>", closeTag: "</u>" }
      : null;
  if (!cfg) return false;
  e.preventDefault();
  const el = e.currentTarget;
  const { nextValue, nextStart, nextEnd } = toggleInlineHtmlTag(
    el.value,
    el.selectionStart,
    el.selectionEnd,
    cfg
  );
  applyNextValue?.(nextValue);
  requestAnimationFrame(() => {
    try {
      el.setSelectionRange(nextStart, nextEnd);
    } catch {
      // ignore
    }
  });
  return true;
}

function normalizeProjectsPanelSettings(panelSettings) {
  const raw =
    panelSettings && typeof panelSettings === "object" ? panelSettings : {};
  const p =
    raw.projects && typeof raw.projects === "object" ? raw.projects : {};
  const shortcuts =
    p.shortcuts && typeof p.shortcuts === "object" ? p.shortcuts : {};
  const default_view = String(p.default_view || "editor");
  return {
    default_view: default_view === "info" ? "info" : "editor",
    auto_expand_all_sections: Boolean(p.auto_expand_all_sections),
    section_open_mode:
      String(p.section_open_mode || "multi") === "single" ? "single" : "multi",
    container_open_mode:
      String(p.container_open_mode || "multi") === "single"
        ? "single"
        : "multi",
    show_image_preview:
      p.show_image_preview == null ? true : Boolean(p.show_image_preview),
    draft_mode: p.draft_mode == null ? true : Boolean(p.draft_mode),
    show_contrast_indicator:
      p.show_contrast_indicator == null
        ? true
        : Boolean(p.show_contrast_indicator),
    show_dynamic_tokens:
      p.show_dynamic_tokens == null ? true : Boolean(p.show_dynamic_tokens),
    shortcuts: {
      format_biu:
        shortcuts.format_biu == null ? true : Boolean(shortcuts.format_biu),
      save: shortcuts.save == null ? true : Boolean(shortcuts.save),
      publish: shortcuts.publish == null ? true : Boolean(shortcuts.publish),
    },
  };
}

function IconDirRow(props) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M5 8h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M5 16h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M7 12h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M15 9l3 3-3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDirRowReverse(props) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M10 8h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M10 16h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M7 12h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M9 9l-3 3 3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDirCol(props) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M8 5v9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M16 5v9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M12 7v10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M9 15l3 3 3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDirColReverse(props) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M8 10v9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M16 10v9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M12 7v10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M9 9l3-3 3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconReorder(props) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M7 6h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M7 12h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M7 18h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M5 8V16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M5 6l-2 2 2 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 18l2-2-2-2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSteps(props) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M6 7h2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M11 7h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M6 12h2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M11 12h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M6 17h2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M11 17h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M4 6a1 1 0 1 0 0 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M4 11a1 1 0 1 0 0 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M4 16a1 1 0 1 0 0 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DirectionPicker({
  value,
  onChange,
  disabled,
  ariaLabel = "Direction",
}) {
  const v = normalizeFlexDirection(value, "column");
  const items = [
    { key: "row", icon: IconDirRow, title: "Ligne" },
    { key: "row-reverse", icon: IconDirRowReverse, title: "Ligne inversée" },
    { key: "column", icon: IconDirCol, title: "Colonne" },
    {
      key: "column-reverse",
      icon: IconDirColReverse,
      title: "Colonne inversée",
    },
  ];
  return (
    <div className="Seg4" role="group" aria-label={ariaLabel}>
      {items.map((it) => {
        const Ico = it.icon;
        return (
          <button
            key={it.key}
            type="button"
            className={`SegButton ${v === it.key ? "is-active" : ""}`}
            onClick={() => onChange?.(it.key)}
            disabled={disabled}
            title={it.title}
            aria-label={it.title}
          >
            <Ico />
          </button>
        );
      })}
    </div>
  );
}

const SECTION_TEMPLATES = [
  {
    key: "classic",
    label: "Classique",
    hint: "",
    icon: IconType,
    accent: "#7c3aed",
  },
  {
    key: "steps",
    label: "Étapes",
    hint: "",
    icon: IconSteps,
    accent: "#ef4444",
  },
  {
    key: "banner",
    label: "Bannière",
    hint: "",
    icon: IconImage,
    accent: "#38bdf8",
  },
  {
    key: "grid",
    label: "Grid",
    hint: "Colonnes / layout",
    icon: IconLayoutGrid,
    accent: "#06b6d4",
  },
  {
    key: "slider",
    label: "Slider",
    hint: "Carrousel",
    icon: IconCarousel,
    accent: "#f59e0b",
  },
  {
    key: "cards",
    label: "Cards",
    hint: "Cartes / features",
    icon: IconCards,
    accent: "#10b981",
  },
  {
    key: "hero",
    label: "Hero",
    hint: "Grand visuel",
    icon: IconSparkles,
    accent: "#ec4899",
  },
];

function ensureDashboardNotNeeded(content) {
  return content || defaultContent();
}

export default function ProjectsPage({
  siteId,
  site,
  lang,
  defaultLang,
  languages,
  initialProjectId,
  panelSettings,
  onUnsavedChange,
}) {
  const [state, setState] = useState({
    status: "loading",
    dbConfig: null,
    ensureOk: false,
    projects: [],
    tags: [],
    categories: [],
    styles: [],
    error: "",
  });

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectDraft, setProjectDraft] = useState(null);
  const [projectOpen, setProjectOpen] = useState({ status: "idle", id: null }); // idle | loading
  const [modal, setModal] = useState(null);
  const [savedFingerprint, setSavedFingerprint] = useState("");
  const [listQuery, setListQuery] = useState("");
  const [listSort, setListSort] = useState("updated_desc");
  const [listTagFilterIds, setListTagFilterIds] = useState([]);
  const [listCategoryFilterIds, setListCategoryFilterIds] = useState([]);
  const [listTagFilterOpen, setListTagFilterOpen] = useState(false);
  const [listCategoryFilterOpen, setListCategoryFilterOpen] = useState(false);
  const [dragProjectId, setDragProjectId] = useState(null);
  const [dragProjectOver, setDragProjectOver] = useState(null); // { id, pos: 'before' | 'after' }
  const [projectsReordering, setProjectsReordering] = useState(false);

  const effectiveLang =
    String(lang || "").trim() || String(defaultLang || "").trim() || "fr";
  const effectiveDefaultLang = String(defaultLang || "").trim() || "fr";
  const projectsUi = useMemo(
    () => normalizeProjectsPanelSettings(panelSettings),
    [panelSettings]
  );
  const currentLangLabel = useMemo(() => {
    const list = languages || [];
    const found = list.find((l) => String(l.lang_code) === effectiveLang);
    return found?.label || effectiveLang.toUpperCase();
  }, [languages, effectiveLang]);
  const defaultLangLabel = useMemo(() => {
    const list = languages || [];
    const found = list.find(
      (l) => String(l.lang_code) === effectiveDefaultLang
    );
    return found?.label || effectiveDefaultLang.toUpperCase();
  }, [languages, effectiveDefaultLang]);

  const saveLatestRef = useRef(null);
  const saveFromBar = useCallback(() => saveLatestRef.current?.(), []);
  const discardLatestRef = useRef(null);
  const discardFromBar = useCallback(() => discardLatestRef.current?.(), []);
  const lastUnsavedRef = useRef({ dirty: null, label: "" });
  const isDirty = useMemo(
    () =>
      Boolean(
        projectDraft &&
          savedFingerprint &&
          fingerprintProjectDraft(projectDraft) !== savedFingerprint
      ),
    [projectDraft, savedFingerprint]
  );
  const savedDraftRef = useRef(null);
  const selectedProjectIdRef = useRef(null);
  const lastDeepLinkIdRef = useRef(null);

  const listSelectedTags = useMemo(() => {
    const ids = new Set((listTagFilterIds || []).map((x) => Number(x)));
    return (state.tags || []).filter((t) => ids.has(Number(t.id)));
  }, [listTagFilterIds, state.tags]);

  const listSelectedCategories = useMemo(() => {
    const ids = new Set((listCategoryFilterIds || []).map((x) => Number(x)));
    return (state.categories || []).filter((c) => ids.has(Number(c.id)));
  }, [listCategoryFilterIds, state.categories]);

  const visibleProjects = useMemo(() => {
    const list = Array.isArray(state.projects) ? state.projects : [];
    const q = String(listQuery || "")
      .trim()
      .toLowerCase();
    const tagSet = new Set((listTagFilterIds || []).map((x) => Number(x)));
    const catSet = new Set((listCategoryFilterIds || []).map((x) => Number(x)));

    function projectTagIds(p) {
      if (!p) return [];
      if (Array.isArray(p.tag_ids)) return p.tag_ids.map((x) => Number(x));
      if (Array.isArray(p.tags)) return p.tags.map((t) => Number(t?.id));
      return [];
    }

    function projectCategoryIds(p) {
      if (!p) return [];
      if (Array.isArray(p.category_ids))
        return p.category_ids.map((x) => Number(x));
      if (Array.isArray(p.categories))
        return p.categories.map((c) => Number(c?.id));
      return [];
    }

    const filtered = list.filter((p) => {
      if (!p) return false;
      if (q) {
        const name = String(p.name || "").toLowerCase();
        const slug = String(p.slug || "").toLowerCase();
        if (!name.includes(q) && !slug.includes(q)) return false;
      }
      if (tagSet.size) {
        const ids = projectTagIds(p);
        if (!ids.some((id) => tagSet.has(Number(id)))) return false;
      }
      if (catSet.size) {
        const ids = projectCategoryIds(p);
        if (!ids.some((id) => catSet.has(Number(id)))) return false;
      }
      return true;
    });

    function updatedTs(p) {
      const v = p?.updated_at || p?.created_at || "";
      const t = Date.parse(String(v));
      return Number.isFinite(t) ? t : 0;
    }

    function manualOrder(p) {
      const n = Number(p?.sort_order);
      return Number.isFinite(n) ? n : 0;
    }

    const next = [...filtered];
    const sortKey = String(listSort || "updated_desc");
    next.sort((a, b) => {
      if (sortKey === "manual_asc")
        return manualOrder(a) - manualOrder(b) || updatedTs(b) - updatedTs(a);
      if (sortKey === "name_asc")
        return String(a?.name || "").localeCompare(String(b?.name || ""), "fr");
      if (sortKey === "name_desc")
        return String(b?.name || "").localeCompare(String(a?.name || ""), "fr");
      if (sortKey === "updated_asc") return updatedTs(a) - updatedTs(b);
      return updatedTs(b) - updatedTs(a); // updated_desc default
    });
    return next;
  }, [
    state.projects,
    listQuery,
    listSort,
    listTagFilterIds,
    listCategoryFilterIds,
  ]);

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  useEffect(() => {
    lastDeepLinkIdRef.current = null;
  }, [siteId]);

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: "loading", error: "" }));
    api
      .dbConnections(Number(siteId))
      .then(async (dbRes) => {
        if (cancelled) return;
        if (!dbRes.ok) {
          setState((s) => ({
            ...s,
            status: "error",
            error: dbRes.error || "Impossible de charger la config BDD",
          }));
          return;
        }

        const dbConfig = (dbRes.connections || [])[0] || null;
        setState((s) => ({ ...s, dbConfig, status: "ready" }));

        if (!dbConfig) {
          setState((s) => ({
            ...s,
            projects: [],
            tags: [],
            categories: [],
            styles: [],
            ensureOk: false,
            error: "",
          }));
          return;
        }

        // Tente de lister les projets (peut renvoyer 409 si tables manquantes)
        const listRes = await api.projectsList(Number(siteId), {
          lang: effectiveLang,
        });
        if (!listRes.ok) {
          setState((s) => ({
            ...s,
            projects: [],
            tags: [],
            categories: [],
            styles: [],
            ensureOk: false,
            error: listRes.status === 409 ? listRes.error : "",
          }));
          return;
        }
        setState((s) => ({
          ...s,
          projects: listRes.projects || [],
          tags: listRes.tags || [],
          categories: listRes.categories || [],
          styles: listRes.styles || [],
          ensureOk: true,
          error: "",
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          status: "error",
          error: "Impossible de charger",
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, effectiveLang]);

  useEffect(() => refresh(), [refresh]);

  const canUse = Boolean(siteId);
  const dbLoading = state.status === "loading";
  const dbOk = !dbLoading && Boolean(state.dbConfig && state.ensureOk);
  const dbNeedsAttention =
    !dbLoading &&
    Boolean(!state.dbConfig || (state.dbConfig && !state.ensureOk));
  const dbLabel = dbLoading
    ? "Chargement…"
    : !state.dbConfig
    ? "Connexion manquante"
    : state.ensureOk
    ? "OK"
    : "Tables manquantes";

  const projectTemplates = useMemo(() => SECTION_TEMPLATES, []);

  const reloadProjectsAndTags = useCallback(async () => {
    if (!siteId) return;
    const res = await api.projectsList(Number(siteId), { lang: effectiveLang });
    if (!res.ok) return;
    setState((s) => ({
      ...s,
      projects: res.projects || [],
      tags: res.tags || [],
      categories: res.categories || [],
      styles: res.styles || [],
      ensureOk: true,
    }));
  }, [siteId, effectiveLang]);

  async function ensureTables() {
    const res = await api.projectsEnsure(Number(siteId));
    if (!res.ok) return res;
    refresh();
    return res;
  }

  const canReorderProjects = useMemo(() => {
    if (projectsReordering) return false;
    if (String(listSort || "") !== "manual_asc") return false;
    if (String(listQuery || "").trim()) return false;
    if ((listTagFilterIds || []).length) return false;
    if ((listCategoryFilterIds || []).length) return false;
    return true;
  }, [
    projectsReordering,
    listSort,
    listQuery,
    listTagFilterIds,
    listCategoryFilterIds,
  ]);

  useEffect(() => {
    if (String(listSort) === "manual_asc") return;
    setDragProjectId(null);
    setDragProjectOver(null);
  }, [listSort]);

  const commitProjectOrder = useCallback(
    async (nextIds) => {
      if (!siteId) return;
      const ids = Array.isArray(nextIds)
        ? nextIds
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0)
        : [];
      if (!ids.length) return;
      setProjectsReordering(true);
      setState((s) => ({ ...s, error: "" }));
      const res = await api.projectsReorder(Number(siteId), ids);
      if (!res.ok) {
        setProjectsReordering(false);
        setDragProjectOver(null);
        setState((s) => ({
          ...s,
          error: res.error || "Réorganisation impossible",
        }));
        return;
      }
      const byId = new Map(ids.map((id, idx) => [Number(id), idx + 1]));
      setState((s) => ({
        ...s,
        projects: (s.projects || []).map((p) => {
          const v = byId.get(Number(p?.id));
          return v == null ? p : { ...p, sort_order: v };
        }),
      }));
      setProjectsReordering(false);
      setDragProjectOver(null);
    },
    [siteId]
  );

  const beginDragProject = useCallback(
    (e, projectId) => {
      if (!canReorderProjects) return;
      const id = Number(projectId);
      if (!Number.isFinite(id) || id <= 0) return;
      setDragProjectId(id);
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(
          "application/x-omniscope-project",
          JSON.stringify({ project_id: id })
        );
        e.dataTransfer.setData("text/plain", String(id));
      } catch {
        // ignore
      }
    },
    [canReorderProjects]
  );

  const resolveDragProjectId = useCallback(
    (e) => {
      if (dragProjectId != null) return Number(dragProjectId);
      try {
        const raw = e?.dataTransfer?.getData?.(
          "application/x-omniscope-project"
        );
        if (raw) {
          const data = JSON.parse(raw);
          const id = Number(data?.project_id);
          return Number.isFinite(id) && id > 0 ? id : null;
        }
      } catch {
        // ignore
      }
      return null;
    },
    [dragProjectId]
  );

  const handleProjectDrop = useCallback(
    async (e, targetProjectId = null, dropPos = "before") => {
      if (!canReorderProjects) return;
      const fromId = resolveDragProjectId(e);
      const toId = targetProjectId == null ? null : Number(targetProjectId);
      if (!fromId) return;
      if (toId != null && Number(fromId) === Number(toId)) return;
      e.preventDefault();
      e.stopPropagation();

      const current = (visibleProjects || [])
        .map((p) => Number(p?.id))
        .filter((n) => Number.isFinite(n) && n > 0);
      const fromIndex = current.findIndex(
        (id) => Number(id) === Number(fromId)
      );
      if (fromIndex < 0) return;

      const next = [...current];
      next.splice(fromIndex, 1);
      if (toId == null) {
        next.push(fromId);
      } else {
        const toIndex = next.findIndex((id) => Number(id) === Number(toId));
        if (toIndex < 0) return;
        const insertIndex = String(dropPos) === "after" ? toIndex + 1 : toIndex;
        next.splice(insertIndex, 0, fromId);
      }

      setDragProjectId(null);
      setDragProjectOver(null);
      await commitProjectOrder(next);
    },
    [
      canReorderProjects,
      resolveDragProjectId,
      visibleProjects,
      commitProjectOrder,
    ]
  );

  const handleProjectDragOver = useCallback(
    (e, projectId) => {
      if (!canReorderProjects) return;
      e.preventDefault();
      const rect = e.currentTarget?.getBoundingClientRect?.();
      const y = Number(e.clientY || 0);
      const middle = rect ? rect.top + rect.height / 2 : 0;
      const pos = y >= middle ? "after" : "before";
      setDragProjectOver({ id: Number(projectId), pos });
    },
    [canReorderProjects]
  );

  const openProject = useCallback(
    async (id) => {
      setProjectOpen({ status: "loading", id: Number(id) });
      try {
        const res = await api.projectGet({
          site_id: Number(siteId),
          id: Number(id),
          lang: effectiveLang,
        });
        if (!res.ok) {
          setProjectOpen({ status: "idle", id: null });
          setState((s) => ({
            ...s,
            error: res.error || "Impossible de charger le projet",
          }));
          return;
        }
        setState((s) => ({
          ...s,
          tags: res.tags || s.tags,
          categories: res.categories || s.categories,
          styles: res.styles || s.styles,
        }));
        setSelectedProjectId(Number(id));
        const p = res.project;
        const canUseDraft = Boolean(projectsUi.draft_mode);
        const draft =
          canUseDraft &&
          p &&
          p.draft &&
          typeof p.draft === "object" &&
          !Array.isArray(p.draft)
            ? p.draft
            : null;
        const hasDraft = canUseDraft
          ? Boolean(p?.has_draft) || Boolean(draft)
          : false;
        const draftTagIds = Array.isArray(draft?.tag_ids)
          ? draft.tag_ids
              .map((x) => Number(x))
              .filter((n) => Number.isFinite(n))
          : null;
        const draftCategoryIds = Array.isArray(draft?.category_ids)
          ? draft.category_ids
              .map((x) => Number(x))
              .filter((n) => Number.isFinite(n))
          : null;
        const nextDraft = {
          id: p.id,
          name: canUseDraft ? draft?.name ?? p.name : p.name,
          slug: canUseDraft ? draft?.slug ?? p.slug : p.slug,
          cover_image_url:
            (canUseDraft
              ? draft?.cover_image_url ?? p.cover_image_url
              : p.cover_image_url) || "",
          background_image_url:
            (canUseDraft
              ? draft?.background_image_url ?? p.background_image_url
              : p.background_image_url) || "",
          visibility:
            String(
              canUseDraft
                ? draft?.visibility ?? p.visibility
                : p.visibility || "public"
            ) || "public",
          tag_ids:
            canUseDraft && draftTagIds != null
              ? draftTagIds
              : (p.tags || []).map((t) => Number(t.id)),
          category_ids:
            canUseDraft && draftCategoryIds != null
              ? draftCategoryIds
              : (p.categories || []).map((c) => Number(c.id)),
          content:
            ensureDashboardNotNeeded(draft?.content ?? p.content) ||
            defaultContent(),
          has_translation:
            p.has_translation == null ? null : Boolean(p.has_translation),
          has_draft: hasDraft,
          draft_updated_at: canUseDraft ? p?.draft_updated_at || null : null,
        };
        setProjectDraft(nextDraft);
        setSavedFingerprint(fingerprintProjectDraft(nextDraft));
        savedDraftRef.current = cloneDraft(nextDraft);
        setProjectOpen({ status: "idle", id: null });
      } catch {
        setProjectOpen({ status: "idle", id: null });
        setState((s) => ({
          ...s,
          error: "Impossible de charger le projet",
        }));
      }
    },
    [siteId, effectiveLang, projectsUi.draft_mode]
  );

  useEffect(() => {
    const id = Number(initialProjectId);
    if (!Number.isFinite(id) || id <= 0) {
      lastDeepLinkIdRef.current = null;
      return;
    }
    if (!state.ensureOk) return;
    if (isDirty) return;
    if (lastDeepLinkIdRef.current === id) return;
    if (Number(selectedProjectIdRef.current) === Number(id)) {
      lastDeepLinkIdRef.current = id;
      return;
    }
    lastDeepLinkIdRef.current = id;
    openProject(id);
  }, [initialProjectId, state.ensureOk, isDirty, openProject]);

  useEffect(() => {
    const id = Number(selectedProjectIdRef.current);
    if (!Number.isFinite(id) || id <= 0) return;
    if (isDirty) return;
    openProject(id);
  }, [effectiveLang, isDirty, openProject]);

  useEffect(() => {
    const dirty = Boolean(
      projectDraft &&
        savedFingerprint &&
        fingerprintProjectDraft(projectDraft) !== savedFingerprint
    );
    const label = `Projets — ${currentLangLabel} (${effectiveLang})`;
    if (
      lastUnsavedRef.current.dirty !== dirty ||
      lastUnsavedRef.current.label !== label
    ) {
      lastUnsavedRef.current = { dirty, label };
      onUnsavedChange?.({
        dirty,
        label,
        onSave: saveFromBar,
        onDiscard: discardFromBar,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectDraft,
    savedFingerprint,
    currentLangLabel,
    effectiveLang,
    saveFromBar,
    discardFromBar,
  ]);

  useEffect(() => {
    return () => {
      onUnsavedChange?.({ dirty: false, label: "", onSave: null });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveProject() {
    if (!projectDraft) return;
    const payload = {
      site_id: Number(siteId),
      id: projectDraft.id,
      lang: effectiveLang,
      name: projectDraft.name,
      slug: projectDraft.slug,
      cover_image_url: projectDraft.cover_image_url,
      background_image_url: projectDraft.background_image_url,
      visibility: projectDraft.visibility || "public",
      tag_ids: projectDraft.tag_ids,
      category_ids: projectDraft.category_ids,
      content: projectDraft.content,
    };
    const canUseDraft = Boolean(projectsUi.draft_mode);
    const res = await api.projectUpdate({
      ...payload,
      draft: canUseDraft,
      publish: !canUseDraft,
    });
    if (!res.ok) {
      setState((s) => ({
        ...s,
        error: res.error || "Enregistrement impossible",
      }));
      return res;
    }
    setProjectDraft((p) =>
      p
        ? {
            ...p,
            has_translation: true,
            has_draft: canUseDraft ? true : false,
            draft_updated_at: canUseDraft ? new Date().toISOString() : null,
          }
        : p
    );
    setSavedFingerprint(fingerprintProjectDraft(projectDraft));
    savedDraftRef.current = cloneDraft(projectDraft);
    refresh();
    return res;
  }

  async function publishProject() {
    if (!projectDraft) return;
    const payload = {
      site_id: Number(siteId),
      id: projectDraft.id,
      lang: effectiveLang,
      name: projectDraft.name,
      slug: projectDraft.slug,
      cover_image_url: projectDraft.cover_image_url,
      background_image_url: projectDraft.background_image_url,
      visibility: projectDraft.visibility || "public",
      tag_ids: projectDraft.tag_ids,
      category_ids: projectDraft.category_ids,
      content: projectDraft.content,
      draft: false,
      publish: true,
    };
    const res = await api.projectUpdate(payload);
    if (!res.ok) {
      setState((s) => ({
        ...s,
        error: res.error || "Publication impossible",
      }));
      return res;
    }
    setProjectDraft((p) =>
      p
        ? {
            ...p,
            has_translation: true,
            has_draft: false,
            draft_updated_at: null,
          }
        : p
    );
    setSavedFingerprint(fingerprintProjectDraft(projectDraft));
    savedDraftRef.current = cloneDraft(projectDraft);
    refresh();
    return res;
  }

  saveLatestRef.current = projectsUi.draft_mode ? saveProject : publishProject;

  function discardChanges() {
    const saved = savedDraftRef.current;
    if (!saved) return;
    const next = cloneDraft(saved);
    setProjectDraft(next);
    setSavedFingerprint(fingerprintProjectDraft(next));
  }

  discardLatestRef.current = discardChanges;

  async function createProject({ name }) {
    const res = await api.projectCreate({
      site_id: Number(siteId),
      lang: effectiveLang,
      name,
      slug: "",
      cover_image_url: "",
      background_image_url: "",
      visibility: "public",
      tag_ids: [],
      category_ids: [],
      content: defaultContent(),
    });
    if (!res.ok) return res;
    refresh();
    await openProject(res.id);
    return res;
  }

  async function deleteProject(id) {
    const res = await api.projectDelete({
      site_id: Number(siteId),
      id: Number(id),
    });
    if (!res.ok) return res;
    if (Number(selectedProjectId) === Number(id)) {
      setSelectedProjectId(null);
      setProjectDraft(null);
      setSavedFingerprint("");
      savedDraftRef.current = null;
    }
    refresh();
    return res;
  }

  const closeEditor = useCallback(() => {
    setSelectedProjectId(null);
    setProjectDraft(null);
    setSavedFingerprint("");
    savedDraftRef.current = null;
  }, []);

  const requestBackToList = useCallback(() => {
    if (isDirty) {
      setModal({ type: "unsaved-leave-editor" });
      return;
    }
    closeEditor();
  }, [isDirty, closeEditor]);

  useEffect(() => {
    function onModuleHome(e) {
      const route = String(e?.detail?.route || "");
      if (route !== "/projects") return;
      if (!projectDraft) return;
      requestBackToList();
    }
    window.addEventListener("omniscope:moduleHome", onModuleHome);
    return () =>
      window.removeEventListener("omniscope:moduleHome", onModuleHome);
  }, [projectDraft, requestBackToList]);

  if (!canUse) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Projets</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb
            items={
              projectDraft
                ? [
                    { label: "Projets", onClick: requestBackToList },
                    { label: projectDraft.name || "Projet" },
                  ]
                : [{ label: "Projets" }]
            }
            badges={
              projectDraft ? (
                <span className="Badge">
                  {String(effectiveLang || "").startsWith("fr")
                    ? "Version française"
                    : String(effectiveLang || "").startsWith("en")
                    ? "Version anglaise"
                    : `Version ${currentLangLabel}`}
                </span>
              ) : null
            }
          />
          <div className="Badges">
            <button
              type="button"
              className={`Button Button--ghost ${
                dbOk ? "Button--good" : dbNeedsAttention ? "Button--danger" : ""
              }`}
              disabled={false}
              onClick={() =>
                setModal({ type: "db-status", ensuring: false, error: "" })
              }
              title={`BDD Projets — ${dbLabel}`}
            >
              <span className="ButtonInline">
                {dbLoading ? (
                  <Skeleton
                    variant="circle"
                    style={{ width: 16, height: 16 }}
                    aria-hidden="true"
                  />
                ) : dbOk ? (
                  <IconCheck />
                ) : dbNeedsAttention ? (
                  <IconAlertTriangle />
                ) : (
                  <IconTable />
                )}{" "}
                BDD Projets
              </span>
            </button>
            {!projectDraft ? (
              <button
                type="button"
                className={`Button Button--ghost ${
                  String(listSort) === "manual_asc" ? "Button--good" : ""
                }`}
                onClick={() => {
                  if (String(listSort) === "manual_asc") {
                    setListSort("updated_desc");
                    setDragProjectId(null);
                    setDragProjectOver(null);
                    return;
                  }
                  setListSort("manual_asc");
                  setListQuery("");
                  setListTagFilterIds([]);
                  setListCategoryFilterIds([]);
                }}
                disabled={!state.ensureOk || (state.projects || []).length < 2}
                title="Activer / désactiver l'ordre personnalisé"
              >
                <span className="ButtonInline">
                  <IconReorder />{" "}
                  {String(listSort) === "manual_asc"
                    ? "Désactiver l'ordre"
                    : "Changer l'ordre"}
                </span>
              </button>
            ) : null}
            {!projectDraft ? (
              <button
                type="button"
                className="Button"
                onClick={() =>
                  setModal({
                    type: "new-project",
                    name: "",
                    saving: false,
                    error: "",
                  })
                }
                disabled={!state.ensureOk}
              >
                <span className="ButtonInline">
                  <IconPlus /> Nouveau projet
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      {state.status === "loading" ? (
        <div
          className="Card"
          role="status"
          aria-live="polite"
          style={{ marginBottom: 14 }}
        >
          <div style={{ display: "grid", gap: 10 }}>
            <Skeleton variant="title" style={{ width: 190 }} />
            <Skeleton variant="line" style={{ width: "72%" }} />
            <Skeleton variant="line" style={{ width: "54%" }} />
          </div>
        </div>
      ) : null}

      {projectDraft ? (
        <ProjectEditor
          siteId={Number(siteId)}
          site={site || null}
          projects={state.projects || []}
          project={projectDraft}
          templates={projectTemplates}
          tags={state.tags || []}
          categories={state.categories || []}
          styles={state.styles || []}
          lang={effectiveLang}
          langLabel={currentLangLabel}
          defaultLang={effectiveDefaultLang}
          defaultLangLabel={defaultLangLabel}
          languages={languages || []}
          panelSettings={panelSettings}
          onChange={setProjectDraft}
          onSave={() => saveProject()}
          onPublish={() => publishProject()}
          onDeleteProject={async () => {
            await deleteProject(projectDraft.id);
          }}
          onManageTags={() => setModal({ type: "tags" })}
          onManageCategories={() => setModal({ type: "categories" })}
          onManageStyles={() => setModal({ type: "styles" })}
        />
      ) : projectOpen.status === "loading" ? (
        <div className="Card">
          <div style={{ display: "grid", gap: 12 }}>
            <Skeleton variant="title" style={{ width: 260 }} />
            <Skeleton variant="line" style={{ width: "62%" }} />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <Skeleton variant="block" style={{ height: 56 }} />
              <Skeleton variant="block" style={{ height: 56 }} />
            </div>
            <Skeleton variant="block" style={{ height: 180 }} />
            <Skeleton variant="block" style={{ height: 180 }} />
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {!state.dbConfig ? (
            <div className="Hint">
              Connexion manquante: clique sur “BDD Projets”.
            </div>
          ) : null}
          {state.dbConfig && !state.ensureOk ? (
            <div className="Hint">
              Tables Projets non initialisées: clique sur “BDD Projets”.
            </div>
          ) : null}

          {state.ensureOk ? (
            <>
              <div className="ProjectsToolbar" style={{ marginTop: 2 }}>
                <div className="ProjectsToolbarSearch Field">
                  <label className="SrOnly" htmlFor="projects-search">
                    Rechercher
                  </label>
                  <input
                    id="projects-search"
                    className="Input"
                    value={listQuery}
                    onChange={(e) => setListQuery(e.target.value)}
                    placeholder="Rechercher un projet…"
                  />
                </div>

                <div className="ProjectsToolbarSort Field">
                  <label className="SrOnly" htmlFor="projects-sort">
                    Trier
                  </label>
                  <select
                    id="projects-sort"
                    className="Select"
                    value={listSort}
                    onChange={(e) => setListSort(e.target.value)}
                  >
                    <option value="manual_asc">Ordre personnalisé</option>
                    <option value="updated_desc">
                      Dernière modif (récent)
                    </option>
                    <option value="updated_asc">Dernière modif (ancien)</option>
                    <option value="name_asc">Nom (A → Z)</option>
                    <option value="name_desc">Nom (Z → A)</option>
                  </select>
                </div>

                <div className="ProjectsToolbarButtons">
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() => setListTagFilterOpen(true)}
                  >
                    <span className="ButtonInline">
                      <IconTag /> Tags ({listTagFilterIds.length})
                    </span>
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() => setListCategoryFilterOpen(true)}
                  >
                    <span className="ButtonInline">
                      <IconFolder /> Catégories ({listCategoryFilterIds.length})
                    </span>
                  </button>
                  {listQuery.trim() ||
                  listTagFilterIds.length ||
                  listCategoryFilterIds.length ? (
                    <button
                      type="button"
                      className="Button Button--ghost Button--soft"
                      onClick={() => {
                        setListQuery("");
                        setListTagFilterIds([]);
                        setListCategoryFilterIds([]);
                        setListSort("updated_desc");
                      }}
                    >
                      Réinitialiser
                    </button>
                  ) : null}
                </div>
              </div>

              {listSelectedCategories.length || listSelectedTags.length ? (
                <div
                  className="Badges"
                  style={{ justifyContent: "flex-start", marginTop: 10 }}
                >
                  {listSelectedCategories.map((c) => (
                    <span key={`cat_${c.id}`} className="Badge">
                      {c.name}
                    </span>
                  ))}
                  {listSelectedTags.map((t) => (
                    <span
                      key={`tag_${t.id}`}
                      className="Badge"
                      style={{ borderColor: t.color, color: t.color }}
                    >
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : null}

              <div className="List" role="list" style={{ marginTop: 12 }}>
                {visibleProjects.map((p) => {
                  const dropBefore =
                    canReorderProjects &&
                    Number(dragProjectOver?.id) === Number(p.id) &&
                    dragProjectOver?.pos === "before";
                  const dropAfter =
                    canReorderProjects &&
                    Number(dragProjectOver?.id) === Number(p.id) &&
                    dragProjectOver?.pos === "after";
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className="ListItem ListItem--button"
                      onDragEnd={() => {
                        setDragProjectId(null);
                        setDragProjectOver(null);
                      }}
                      onDragOver={(e) => handleProjectDragOver(e, p.id)}
                      onDragLeave={() => {
                        if (Number(dragProjectOver?.id) === Number(p.id)) {
                          setDragProjectOver(null);
                        }
                      }}
                      onDrop={(e) =>
                        handleProjectDrop(
                          e,
                          p.id,
                          Number(dragProjectOver?.id) === Number(p.id)
                            ? dragProjectOver?.pos
                            : "before"
                        )
                      }
                      onClick={() => {
                        if (canReorderProjects) return;
                        openProject(p.id);
                      }}
                      style={{
                        cursor: canReorderProjects
                          ? "default"
                          : listSort === "manual_asc"
                          ? "not-allowed"
                          : "pointer",
                        borderTopColor: dropBefore
                          ? "color-mix(in srgb, var(--primary) 58%, transparent)"
                          : undefined,
                        borderBottomColor: dropAfter
                          ? "color-mix(in srgb, var(--primary) 58%, transparent)"
                          : undefined,
                        boxShadow:
                          dropBefore || dropAfter
                            ? "0 0 0 1px color-mix(in srgb, var(--primary) 42%, transparent)"
                            : undefined,
                      }}
                      title={
                        listSort === "manual_asc" && !canReorderProjects
                          ? "Désactive la recherche et les filtres pour réorganiser."
                          : undefined
                      }
                    >
                      <span
                        className="ButtonInline"
                        style={{
                          gap: 12,
                          alignItems: "flex-start",
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        {listSort === "manual_asc" ? (
                          <span
                            className="DragHandle"
                            draggable={canReorderProjects}
                            onDragStart={(e) => beginDragProject(e, p.id)}
                            onMouseDown={(e) => e.stopPropagation()}
                            title="Glisser pour réordonner"
                            aria-hidden="true"
                            style={{
                              cursor: canReorderProjects
                                ? "grab"
                                : "not-allowed",
                            }}
                          >
                            <IconReorder />
                          </span>
                        ) : null}
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span className="ProjectName">{p.name}</span>
                          <span
                            className="Badges"
                            style={{
                              marginTop: 10,
                              justifyContent: "flex-start",
                            }}
                          >
                            {(p.categories || []).slice(0, 2).map((c) => (
                              <span key={c.id} className="Badge">
                                {c.name}
                              </span>
                            ))}
                            {(p.categories || []).length > 2 ? (
                              <span className="Badge">
                                +{p.categories.length - 2}
                              </span>
                            ) : null}
                            {projectsUi.draft_mode && p.has_draft ? (
                              <span className="Badge">brouillon</span>
                            ) : null}
                            {effectiveLang !== effectiveDefaultLang &&
                            p.has_translation === false ? (
                              <span className="Badge">à traduire</span>
                            ) : null}
                            {String(p.visibility || "public") === "unlisted" ? (
                              <span className="Badge">non répertorié</span>
                            ) : null}
                            {String(p.visibility || "public") === "private" ? (
                              <span className="Badge">privé</span>
                            ) : null}
                            {(p.tags || []).slice(0, 3).map((t) => (
                              <span
                                key={t.id}
                                className="Badge"
                                style={{ borderColor: t.color, color: t.color }}
                              >
                                {t.name}
                              </span>
                            ))}
                            {(p.tags || []).length > 3 ? (
                              <span className="Badge">
                                +{p.tags.length - 3}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
                {listSort === "manual_asc" ? (
                  <div
                    style={{ height: 8 }}
                    onDragOver={(e) => {
                      if (!canReorderProjects) return;
                      e.preventDefault();
                    }}
                    onDrop={(e) => handleProjectDrop(e, null, "after")}
                  />
                ) : null}
                {visibleProjects.length === 0 ? (
                  <div className="Muted">Aucun projet créé.</div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      )}

      {modal?.type === "new-project" ? (
        <Modal
          title="Nouveau projet"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={async () => {
            if (modal.saving) return;
            setModal((m) => ({ ...m, saving: true, error: "" }));
            const res = await createProject({ name: modal.name });
            if (!res.ok) {
              setModal((m) => ({
                ...m,
                saving: false,
                error: res.error || "Erreur",
              }));
              return;
            }
            setModal(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: "" }));
                  const res = await createProject({ name: modal.name });
                  if (!res.ok) {
                    setModal((m) => ({
                      ...m,
                      saving: false,
                      error: res.error || "Erreur",
                    }));
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? "Création…" : "Créer"}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label" htmlFor="np-name">
              Nom
            </label>
            <input
              id="np-name"
              className="Input"
              value={modal.name}
              onChange={(e) =>
                setModal((m) => ({ ...m, name: e.target.value }))
              }
            />
          </div>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {listTagFilterOpen ? (
        <TagPickerModal
          tags={state.tags || []}
          selectedIds={listTagFilterIds}
          onToggle={(id) =>
            setListTagFilterIds((prev) => {
              const n = Number(id);
              const cur = Array.isArray(prev) ? prev : [];
              const set = new Set(cur.map((x) => Number(x)));
              if (set.has(n)) set.delete(n);
              else set.add(n);
              return Array.from(set);
            })
          }
          onClose={() => setListTagFilterOpen(false)}
          onManage={() => {
            setListTagFilterOpen(false);
            setModal({ type: "tags" });
          }}
        />
      ) : null}

      {listCategoryFilterOpen ? (
        <CategoryPickerModal
          categories={state.categories || []}
          selectedIds={listCategoryFilterIds}
          onToggle={(id) =>
            setListCategoryFilterIds((prev) => {
              const n = Number(id);
              const cur = Array.isArray(prev) ? prev : [];
              const set = new Set(cur.map((x) => Number(x)));
              if (set.has(n)) set.delete(n);
              else set.add(n);
              return Array.from(set);
            })
          }
          onClose={() => setListCategoryFilterOpen(false)}
          onManage={() => {
            setListCategoryFilterOpen(false);
            setModal({ type: "categories" });
          }}
        />
      ) : null}

      {modal?.type === "tags" ? (
        <TagsManager
          siteId={Number(siteId)}
          tags={state.tags || []}
          onClose={() => setModal(null)}
          onRefresh={reloadProjectsAndTags}
        />
      ) : null}

      {modal?.type === "categories" ? (
        <CategoriesManager
          siteId={Number(siteId)}
          categories={state.categories || []}
          onClose={() => setModal(null)}
          onRefresh={reloadProjectsAndTags}
        />
      ) : null}

      {modal?.type === "styles" ? (
        <StylesManager
          siteId={Number(siteId)}
          styles={state.styles || []}
          onClose={() => setModal(null)}
          onRefresh={reloadProjectsAndTags}
        />
      ) : null}

      {modal?.type === "db-status" ? (
        <ProjectsDbStatusModal
          siteId={Number(siteId)}
          dbConfig={state.dbConfig}
          ensureOk={state.ensureOk}
          ensureError={state.ensureOk ? "" : state.error}
          dbLoading={dbLoading}
          onEnsure={ensureTables}
          onClose={() => setModal(null)}
        />
      ) : null}

      {modal?.type === "unsaved-leave-editor" ? (
        <Modal
          title="Modifications non sauvegardées"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setModal(null)}
              >
                Rester
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--danger"
                onClick={() => {
                  setModal(null);
                  closeEditor();
                }}
              >
                Quitter sans sauvegarder
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  const res = await saveProject();
                  if (!res?.ok) return;
                  setModal(null);
                  closeEditor();
                }}
              >
                Sauvegarder puis quitter
              </button>
            </>
          }
        >
          <div className="Muted">
            Tu as des modifications non sauvegardées sur ce projet.
          </div>
        </Modal>
      ) : null}

      {modal?.type === "unsaved-switch-project" ? (
        <Modal
          title="Modifications non sauvegardées"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => {
                  discardChanges();
                  setModal(null);
                  const nextId = Number(modal.nextId);
                  openProject(nextId);
                }}
              >
                Annuler et ouvrir
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  const res = await saveProject();
                  if (!res?.ok) return;
                  const nextId = Number(modal.nextId);
                  setModal(null);
                  await openProject(nextId);
                }}
              >
                Sauvegarder puis ouvrir
              </button>
            </>
          }
        >
          <div className="Muted">
            Sauvegarde tes changements avant de changer de projet.
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function ProjectEditor({
  siteId,
  site,
  projects,
  project,
  templates,
  tags,
  categories,
  styles,
  lang,
  langLabel,
  defaultLang,
  defaultLangLabel,
  languages,
  panelSettings,
  onChange,
  onSave,
  onPublish,
  onDeleteProject,
  onManageTags,
  onManageCategories,
  onManageStyles,
}) {
  const projectsUi = useMemo(
    () => normalizeProjectsPanelSettings(panelSettings),
    [panelSettings]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [sectionDropTarget, setSectionDropTarget] = useState(null);
  const [sectionPickerOpen, setSectionPickerOpen] = useState(false);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [projectView, setProjectView] = useState(
    projectsUi.default_view || "editor"
  ); // 'info' | 'editor'
  const [collapsedSections, setCollapsedSections] = useState({});
  const [projectImagePicker, setProjectImagePicker] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [duplicateContainerModal, setDuplicateContainerModal] = useState(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [duplicateContentModal, setDuplicateContentModal] = useState(null);
  const [duplicateContentConfirmModal, setDuplicateContentConfirmModal] =
    useState(null);
  const [duplicateContentConfirm2Modal, setDuplicateContentConfirm2Modal] =
    useState(null);
  const [duplicateContentResultModal, setDuplicateContentResultModal] =
    useState(null);
  const [copyFromLangModal, setCopyFromLangModal] = useState(null);
  const [copyFromLangConfirmModal, setCopyFromLangConfirmModal] =
    useState(null);
  const [deleteProjectConfirmModal, setDeleteProjectConfirmModal] =
    useState(null);
  const [publishConfirmModal, setPublishConfirmModal] = useState(null);
  const [siteMedia, setSiteMedia] = useState({
    loading: false,
    error: "",
    assets: [],
  });
  const apiBaseUrl = useApiBaseUrl();
  const assetsBaseUrl = useMemo(
    () => normalizeAssetsBaseUrl(site?.assets_base_url),
    [site?.assets_base_url]
  );

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);
  const openCtxMenu = useCallback((e, items) => {
    if (!e) return;
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({
      x: Number(e.clientX) || 0,
      y: Number(e.clientY) || 0,
      items: Array.isArray(items) ? items : [],
    });
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const initial = {};
    try {
      const secs = (project?.content || defaultContent())?.sections || [];
      for (const s of secs) {
        if (!s?.id) continue;
        initial[String(s.id)] = !Boolean(projectsUi.auto_expand_all_sections);
      }
    } catch {
      // ignore
    }
    setCollapsedSections(initial);
    setDragIndex(null);
    setSectionDropTarget(null);
    setProjectView(projectsUi.default_view || "editor");
    setVisibilityModalOpen(false);
  }, [project?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setSiteMedia((s) => ({ ...(s || {}), loading: true, error: "" }));
    api
      .mediaList(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setSiteMedia({
            loading: false,
            error: res.error || "Chargement impossible",
            assets: [],
          });
          return;
        }
        setSiteMedia({ loading: false, error: "", assets: res.assets || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setSiteMedia({
          loading: false,
          error: "Chargement impossible",
          assets: [],
        });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const content = project.content || defaultContent();
  const sections = content.sections || [];
  const contentRef = useRef(content);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const mediaById = useMemo(() => {
    const m = new Map();
    for (const a of siteMedia.assets || []) {
      if (!a || a.id == null) continue;
      m.set(String(a.id), a);
    }
    return m;
  }, [siteMedia.assets]);

  const projectImageAssets = useMemo(() => {
    const pid = Number(project?.id);
    return (siteMedia.assets || [])
      .filter((a) => a && !Boolean(a.is_hidden))
      .filter((a) => String(a.kind || "") === "image")
      .filter((a) =>
        (a.links || []).some(
          (l) => l.type === "project" && Number(l.id) === pid
        )
      );
  }, [siteMedia.assets, project?.id]);

  function setContent(next) {
    onChange({ ...project, content: next });
  }

  function patchProjectMeta(patch) {
    const base =
      content && typeof content === "object" && !Array.isArray(content)
        ? content
        : defaultContent();
    const meta =
      base.meta && typeof base.meta === "object" && !Array.isArray(base.meta)
        ? base.meta
        : {};
    setContent({ ...base, meta: { ...meta, ...(patch || {}) } });
  }

  function deepCloneJson(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value && typeof value === "object" ? { ...(value || {}) } : value;
    }
  }

  const enabledLanguages = useMemo(() => {
    const list = Array.isArray(languages) ? languages : [];
    const seen = new Set();
    const out = [];
    for (const l of list) {
      if (!l || typeof l !== "object") continue;
      const code = String(l.lang_code || "").trim();
      if (!code) continue;
      if (seen.has(code)) continue;
      seen.add(code);
      const enabled = l.is_enabled == null ? true : Boolean(l.is_enabled);
      if (!enabled) continue;
      out.push({
        code,
        label: String(l.label || "").trim() || code.toUpperCase(),
      });
    }
    return out;
  }, [languages]);

  const langLabelByCode = useMemo(() => {
    const m = new Map();
    for (const l of enabledLanguages) m.set(String(l.code), String(l.label));
    return m;
  }, [enabledLanguages]);

  function labelForLang(code) {
    const c = String(code || "").trim();
    return langLabelByCode.get(c) || (c ? c.toUpperCase() : "");
  }

  const canOpenDuplicateContent = useMemo(() => {
    const hasOtherProject = (projects || []).some(
      (p) => p && Number(p.id) !== Number(project.id)
    );
    const hasOtherLang = enabledLanguages.length > 1;
    return hasOtherProject || hasOtherLang;
  }, [projects, project.id, enabledLanguages.length]);

  function normalizeDuplicateContentOptions(raw) {
    const sectionIds = Array.isArray(raw?.sectionIds)
      ? raw.sectionIds.map((x) => String(x || "")).filter(Boolean)
      : [];
    return {
      copyDescription: Boolean(raw?.copyDescription),
      copyLinks: Boolean(raw?.copyLinks),
      copySummary: Boolean(raw?.copySummary),
      sectionIds,
    };
  }

  function openDuplicateContentModal() {
    const baseLang = String(lang || "").trim();
    const targetLang = enabledLanguages.some((l) => l.code === baseLang)
      ? baseLang
      : String(enabledLanguages[0]?.code || baseLang || "");
    const sectionIds = (sections || [])
      .map((s) => String(s?.id || ""))
      .filter(Boolean);
    setDuplicateContentModal({
      targetProjectId: String(project.id),
      targetLang,
      loading: false,
      error: "",
      copyDescription: true,
      copyLinks: true,
      copySummary: true,
      sectionIds,
    });
  }

  function openCopyFromLanguageModal() {
    if (!project?.id) return;
    const defaultSource = enabledLanguages.some(
      (l) => l.code === String(defaultLang)
    )
      ? String(defaultLang)
      : "";
    const fallbackSource =
      enabledLanguages.find((l) => l.code !== String(lang))?.code ||
      String(enabledLanguages[0]?.code || "");
    const sourceLang = defaultSource || fallbackSource;
    setCopyFromLangModal({
      type: "copy-from-lang",
      sourceLang,
      loadingSource: false,
      sourceContent: null,
      sourceSections: [],
      error: "",
      copyDescription: true,
      copyLinks: true,
      copySummary: true,
      sectionIds: [],
    });
  }

  const copyFromLangOpen = copyFromLangModal?.type === "copy-from-lang";
  const copyFromLangSourceLang = copyFromLangOpen
    ? String(copyFromLangModal?.sourceLang || "").trim()
    : "";

  useEffect(() => {
    if (!copyFromLangOpen) return;
    const sourceLang = String(copyFromLangSourceLang || "").trim();
    if (!sourceLang) return;

    if (String(sourceLang) === String(lang)) {
      const sourceSafe =
        ensureDashboardNotNeeded(deepCloneJson(contentRef.current)) ||
        defaultContent();
      const srcSections = Array.isArray(sourceSafe.sections)
        ? sourceSafe.sections
        : [];
      setCopyFromLangModal((m) => {
        if (!m || m.type !== "copy-from-lang") return m;
        if (String(m.sourceLang || "") !== String(sourceLang)) return m;
        const nextIds =
          Array.isArray(m.sectionIds) && m.sectionIds.length
            ? m.sectionIds
            : srcSections.map((s) => String(s?.id || "")).filter(Boolean);
        return {
          ...m,
          loadingSource: false,
          error: "",
          sourceContent: sourceSafe,
          sourceSections: srcSections,
          sectionIds: nextIds,
        };
      });
      return;
    }

    let cancelled = false;
    setCopyFromLangModal((m) =>
      m && m.type === "copy-from-lang"
        ? {
            ...m,
            loadingSource: true,
            error: "",
            sourceContent: null,
            sourceSections: [],
          }
        : m
    );
    api
      .projectGet({
        site_id: Number(siteId),
        id: Number(project.id),
        lang: sourceLang,
      })
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setCopyFromLangModal((m) =>
            m && m.type === "copy-from-lang"
              ? {
                  ...m,
                  loadingSource: false,
                  error: res?.error || "Chargement impossible",
                }
              : m
          );
          return;
        }
        const sourceSafe =
          ensureDashboardNotNeeded(deepCloneJson(res.project?.content)) ||
          defaultContent();
        const srcSections = Array.isArray(sourceSafe.sections)
          ? sourceSafe.sections
          : [];
        setCopyFromLangModal((m) => {
          if (!m || m.type !== "copy-from-lang") return m;
          if (String(m.sourceLang || "") !== String(sourceLang)) return m;
          const nextIds =
            Array.isArray(m.sectionIds) && m.sectionIds.length
              ? m.sectionIds
              : srcSections.map((s) => String(s?.id || "")).filter(Boolean);
          return {
            ...m,
            loadingSource: false,
            error: "",
            sourceContent: sourceSafe,
            sourceSections: srcSections,
            sectionIds: nextIds,
          };
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCopyFromLangModal((m) =>
          m && m.type === "copy-from-lang"
            ? { ...m, loadingSource: false, error: "Chargement impossible" }
            : m
        );
      });
    return () => {
      cancelled = true;
    };
  }, [copyFromLangOpen, copyFromLangSourceLang, siteId, project.id, lang]);

  async function performDuplicateContentTo(targetProject, rawOptions) {
    const tp = targetProject || {};
    const targetId = Number(tp.id);
    if (!Number.isFinite(targetId) || targetId <= 0) return;

    const targetLang =
      String(rawOptions?.targetLang || "").trim() || String(lang || "").trim();

    const options = normalizeDuplicateContentOptions(rawOptions);
    const hasSelection =
      options.copyDescription ||
      options.copyLinks ||
      options.copySummary ||
      options.sectionIds.length > 0;
    if (!hasSelection) return;

    const sourceSafe =
      ensureDashboardNotNeeded(deepCloneJson(content)) || defaultContent();
    const targetSafe =
      ensureDashboardNotNeeded(deepCloneJson(tp.content)) || defaultContent();

    const sourceMeta =
      sourceSafe?.meta && typeof sourceSafe.meta === "object"
        ? sourceSafe.meta
        : {};
    const targetMeta =
      targetSafe?.meta && typeof targetSafe.meta === "object"
        ? targetSafe.meta
        : {};

    const nextMeta = { ...targetMeta };
    if (options.copyDescription)
      nextMeta.description = String(sourceMeta.description || "");
    if (options.copyLinks)
      nextMeta.links = Array.isArray(sourceMeta.links)
        ? deepCloneJson(sourceMeta.links)
        : [];
    if (options.copySummary)
      nextMeta.summary_lines = Array.isArray(sourceMeta.summary_lines)
        ? sourceMeta.summary_lines.slice(0, 3).map((x) => String(x || ""))
        : ["", "", ""];

    const nextContent = {
      ...targetSafe,
      version: Math.max(
        Number(targetSafe?.version) || 1,
        Number(sourceSafe?.version) || 1
      ),
      meta: nextMeta,
    };

    if (options.sectionIds.length > 0) {
      const sourceSections = Array.isArray(sourceSafe.sections)
        ? sourceSafe.sections
        : [];
      nextContent.sections = sourceSections
        .filter((s) => options.sectionIds.includes(String(s?.id || "")))
        .map((s) => deepCloneJson(s));
    }

    const payload = {
      site_id: Number(siteId),
      id: Number(targetId),
      lang: targetLang,
      name: tp.name,
      slug: tp.slug,
      cover_image_url: tp.cover_image_url,
      background_image_url: tp.background_image_url,
      visibility: String(tp.visibility || "public"),
      tag_ids: Array.isArray(tp.tag_ids)
        ? tp.tag_ids
        : (tp.tags || []).map((t) => Number(t.id)),
      category_ids: Array.isArray(tp.category_ids)
        ? tp.category_ids
        : (tp.categories || []).map((c) => Number(c.id)),
      content: nextContent,
    };

    const res = await api.projectUpdate(payload);
    if (!res?.ok) {
      setDuplicateContentResultModal({
        ok: false,
        targetName: tp.name || `Projet #${targetId}`,
        error: res?.error || "Duplication impossible",
      });
      return;
    }
    setDuplicateContentResultModal({
      ok: true,
      targetName: tp.name || `Projet #${targetId}`,
      error: "",
    });
  }

  async function prepareDuplicateContentTo(targetProjectId, rawOptions) {
    const targetId = Number(targetProjectId);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    const targetLang =
      String(rawOptions?.targetLang || "").trim() || String(lang || "").trim();
    if (
      Number(targetId) === Number(project.id) &&
      targetLang === String(lang)
    ) {
      setDuplicateContentModal((m) =>
        m
          ? {
              ...m,
              loading: false,
              error: "Choisis une autre langue (ou un autre projet).",
            }
          : m
      );
      return;
    }

    const options = normalizeDuplicateContentOptions(rawOptions);
    const hasSelection =
      options.copyDescription ||
      options.copyLinks ||
      options.copySummary ||
      options.sectionIds.length > 0;
    if (!hasSelection) {
      setDuplicateContentModal((m) =>
        m
          ? {
              ...m,
              loading: false,
              error: "Sélectionne au moins un élément à copier.",
            }
          : m
      );
      return;
    }

    setDuplicateContentModal((m) =>
      m ? { ...m, loading: true, error: "" } : m
    );
    const res = await api.projectGet({
      site_id: Number(siteId),
      id: Number(targetId),
      lang: targetLang,
    });
    if (!res?.ok) {
      setDuplicateContentModal((m) =>
        m
          ? {
              ...m,
              loading: false,
              error: res?.error || "Chargement impossible",
            }
          : m
      );
      return;
    }

    const tp = res.project || {};
    const tc = ensureDashboardNotNeeded(tp.content) || defaultContent();
    const secs = Array.isArray(tc.sections) ? tc.sections : [];
    if (secs.length > 0 && options.sectionIds.length > 0) {
      setDuplicateContentModal(null);
      setDuplicateContentConfirmModal({
        targetProject: tp,
        targetLang,
        sectionsCount: secs.length,
        selectedSectionsCount: options.sectionIds.length,
        options,
      });
      return;
    }
    setDuplicateContentModal(null);
    await performDuplicateContentTo(tp, { ...options, targetLang });
  }

  function computeNextContentFromCopy(sourceContent, rawOptions) {
    const options = normalizeDuplicateContentOptions(rawOptions);
    const targetSafe =
      ensureDashboardNotNeeded(deepCloneJson(contentRef.current)) ||
      defaultContent();
    const sourceSafe =
      ensureDashboardNotNeeded(deepCloneJson(sourceContent)) ||
      defaultContent();

    const sourceMeta =
      sourceSafe?.meta && typeof sourceSafe.meta === "object"
        ? sourceSafe.meta
        : {};
    const targetMeta =
      targetSafe?.meta && typeof targetSafe.meta === "object"
        ? targetSafe.meta
        : {};

    const nextMeta = { ...targetMeta };
    if (options.copyDescription)
      nextMeta.description = String(sourceMeta.description || "");
    if (options.copyLinks)
      nextMeta.links = Array.isArray(sourceMeta.links)
        ? deepCloneJson(sourceMeta.links)
        : [];
    if (options.copySummary)
      nextMeta.summary_lines = Array.isArray(sourceMeta.summary_lines)
        ? sourceMeta.summary_lines.slice(0, 3).map((x) => String(x || ""))
        : ["", "", ""];

    const nextContent = {
      ...targetSafe,
      version: Math.max(
        Number(targetSafe?.version) || 1,
        Number(sourceSafe?.version) || 1
      ),
      meta: nextMeta,
    };

    if (options.sectionIds.length > 0) {
      const sourceSections = Array.isArray(sourceSafe.sections)
        ? sourceSafe.sections
        : [];
      nextContent.sections = sourceSections
        .filter((s) => options.sectionIds.includes(String(s?.id || "")))
        .map((s) => deepCloneJson(s));
    }

    return nextContent;
  }

  function applyCopyFromLanguageModal() {
    if (!copyFromLangModal || copyFromLangModal.type !== "copy-from-lang")
      return;
    const sourceContent = copyFromLangModal.sourceContent;
    if (!sourceContent) return;
    const next = computeNextContentFromCopy(sourceContent, copyFromLangModal);
    setContent(next);
    setCopyFromLangModal(null);
  }

  function requestCopyFromLanguageApply() {
    if (!copyFromLangModal || copyFromLangModal.type !== "copy-from-lang")
      return;
    if (copyFromLangModal.loadingSource) return;
    const sourceContent = copyFromLangModal.sourceContent;
    if (!sourceContent) {
      setCopyFromLangModal((m) =>
        m && m.type === "copy-from-lang"
          ? { ...m, error: "Choisis une version source." }
          : m
      );
      return;
    }

    const options = normalizeDuplicateContentOptions(copyFromLangModal);
    const hasSelection =
      options.copyDescription ||
      options.copyLinks ||
      options.copySummary ||
      options.sectionIds.length > 0;
    if (!hasSelection) {
      setCopyFromLangModal((m) =>
        m && m.type === "copy-from-lang"
          ? { ...m, error: "Sélectionne au moins un élément à copier." }
          : m
      );
      return;
    }

    const currentSections = Array.isArray(contentRef.current?.sections)
      ? contentRef.current.sections
      : [];
    if (options.sectionIds.length > 0 && currentSections.length > 0) {
      setCopyFromLangConfirmModal({
        sourceLang: String(copyFromLangModal.sourceLang || ""),
        sectionsCount: currentSections.length,
        selectedSectionsCount: options.sectionIds.length,
      });
      return;
    }

    applyCopyFromLanguageModal();
  }

  function requestDuplicateContainer({ fromSectionId, containerId }) {
    const sid = String(fromSectionId || "");
    const cid = String(containerId || "");
    if (!sid || !cid) return;
    setDuplicateContainerModal({
      fromSectionId: sid,
      fromContainerId: cid,
      targetProjectId: String(project.id),
      targetSectionId: sid,
      loadingTarget: false,
      saving: false,
      error: "",
      targetProject: null,
    });
  }

  const duplicateTargetProjectId =
    duplicateContainerModal?.targetProjectId == null
      ? ""
      : String(duplicateContainerModal.targetProjectId);

  useEffect(() => {
    if (!duplicateTargetProjectId) return;
    const targetId = Number(duplicateTargetProjectId);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    if (targetId === Number(project.id)) return;

    let cancelled = false;
    setDuplicateContainerModal((m) =>
      m ? { ...m, loadingTarget: true, error: "", targetProject: null } : null
    );
    api
      .projectGet({ site_id: Number(siteId), id: targetId, lang })
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setDuplicateContainerModal((m) =>
            m
              ? {
                  ...m,
                  loadingTarget: false,
                  error: res?.error || "Chargement impossible",
                }
              : null
          );
          return;
        }
        const p = res.project || {};
        const nextContent =
          ensureDashboardNotNeeded(p.content) || defaultContent();
        const secs = Array.isArray(nextContent.sections)
          ? nextContent.sections
          : [];
        setDuplicateContainerModal((m) => {
          if (!m) return m;
          if (Number(m.targetProjectId) !== targetId) return m;
          const current = String(m.targetSectionId || "");
          const nextSectionId =
            current && secs.some((s) => String(s?.id) === current)
              ? current
              : String(secs[0]?.id || "");
          return {
            ...m,
            loadingTarget: false,
            error: "",
            targetProject: { ...p, content: nextContent },
            targetSectionId: nextSectionId,
          };
        });
      })
      .catch(() => {
        if (cancelled) return;
        setDuplicateContainerModal((m) =>
          m
            ? { ...m, loadingTarget: false, error: "Chargement impossible" }
            : null
        );
      });
    return () => {
      cancelled = true;
    };
  }, [duplicateTargetProjectId, siteId, lang, project.id]);

  async function confirmDuplicateContainer() {
    if (!duplicateContainerModal) return;
    const fromSectionId = String(duplicateContainerModal.fromSectionId || "");
    const fromContainerId = String(
      duplicateContainerModal.fromContainerId || ""
    );
    const targetProjectId = Number(duplicateContainerModal.targetProjectId);
    const targetSectionId = String(
      duplicateContainerModal.targetSectionId || ""
    );

    if (!targetSectionId) {
      setDuplicateContainerModal((m) =>
        m ? { ...m, error: "Choisis une section de destination." } : m
      );
      return;
    }

    const sourceSection =
      (content.sections || []).find((s) => String(s?.id) === fromSectionId) ||
      null;
    if (!sourceSection) {
      setDuplicateContainerModal((m) =>
        m ? { ...m, error: "Conteneur source introuvable." } : m
      );
      return;
    }
    const sourceContainers = normalizeContainerList(sourceSection.containers);
    const sourceContainer = findContainerById(
      sourceContainers,
      fromContainerId
    );
    if (!sourceContainer) {
      setDuplicateContainerModal((m) =>
        m ? { ...m, error: "Conteneur source introuvable." } : m
      );
      return;
    }

    const cloned = cloneContainerSubtreeWithNewIds(sourceContainer);

    if (targetProjectId === Number(project.id)) {
      const nextSections = (content.sections || []).map((s) => {
        if (String(s?.id) !== targetSectionId) return s;
        const nextContainers = [
          ...normalizeContainerList(s.containers),
          cloned,
        ];
        return { ...s, containers: nextContainers };
      });
      setContent({ ...content, sections: nextSections });
      setDuplicateContainerModal(null);
      return;
    }

    setDuplicateContainerModal((m) =>
      m ? { ...m, saving: true, error: "" } : m
    );
    const res = await api.projectGet({
      site_id: Number(siteId),
      id: Number(targetProjectId),
      lang,
    });
    if (!res?.ok) {
      setDuplicateContainerModal((m) =>
        m ? { ...m, saving: false, error: res?.error || "Copie impossible" } : m
      );
      return;
    }
    const p = res.project || {};
    const nextContent = ensureDashboardNotNeeded(p.content) || defaultContent();
    const secs = Array.isArray(nextContent.sections)
      ? nextContent.sections
      : [];
    const secIdx = secs.findIndex((s) => String(s?.id) === targetSectionId);
    if (secIdx < 0) {
      setDuplicateContainerModal((m) =>
        m ? { ...m, saving: false, error: "Section cible introuvable." } : m
      );
      return;
    }
    const nextSections = secs.map((s) => {
      if (String(s?.id) !== targetSectionId) return s;
      return {
        ...s,
        containers: [...normalizeContainerList(s.containers), cloned],
      };
    });

    const payload = {
      site_id: Number(siteId),
      id: Number(targetProjectId),
      lang,
      name: p.name,
      slug: p.slug,
      cover_image_url: p.cover_image_url,
      background_image_url: p.background_image_url,
      visibility: String(p.visibility || "public"),
      tag_ids: Array.isArray(p.tag_ids)
        ? p.tag_ids
        : (p.tags || []).map((t) => Number(t.id)),
      category_ids: Array.isArray(p.category_ids)
        ? p.category_ids
        : (p.categories || []).map((c) => Number(c.id)),
      content: { ...nextContent, sections: nextSections },
    };
    const saveRes = await api.projectUpdate(payload);
    if (!saveRes?.ok) {
      setDuplicateContainerModal((m) =>
        m
          ? {
              ...m,
              saving: false,
              error: saveRes?.error || "Copie impossible",
            }
          : m
      );
      return;
    }
    setDuplicateContainerModal(null);
  }

  function addSection(templateKey) {
    const tpl = templates.find((t) => t.key === templateKey);
    const key = String(templateKey || "classic");
    const baseSettings = {};
    if (key === "grid") baseSettings.columns = 2;
    if (key === "cards") baseSettings.columns = 3;
    if (key === "slider") {
      baseSettings.autoplay = true;
      baseSettings.interval_ms = 3500;
    }
    if (key === "hero") {
      baseSettings.cta_label = "";
      baseSettings.cta_url = "";
      baseSettings.layout = "left";
    }
    if (key === "steps") {
      baseSettings.steps = [
        { id: newId("step"), title: "Étape 1", description: "" },
        { id: newId("step"), title: "Étape 2", description: "" },
      ];
    }
    if (key === "classic") {
      baseSettings.direction = "column";
    }
    if (key === "banner") {
      baseSettings.height_px = 420;
      baseSettings.direction = "column";
    }

    let blocks = [
      {
        id: newId("blk"),
        type: "text",
        tag: "p",
        align: "left",
        color: "",
        text: "Texte…",
        style_id: null,
        style_class: "",
        extra_class: "",
      },
    ];
    if (key === "hero") {
      blocks = [
        {
          id: newId("blk"),
          type: "text",
          tag: "h2",
          align: "left",
          color: "",
          text: "Titre du projet",
          style_id: null,
          style_class: "",
          extra_class: "",
        },
        {
          id: newId("blk"),
          type: "text",
          tag: "p",
          align: "left",
          color: "",
          text: "Texte…",
          style_id: null,
          style_class: "",
          extra_class: "",
        },
      ];
    } else if (key === "slider") {
      blocks = [
        { id: newId("blk"), type: "image", url: "", alt: "", caption: "" },
        { id: newId("blk"), type: "image", url: "", alt: "", caption: "" },
      ];
    } else if (key === "grid") {
      blocks = [
        {
          id: newId("blk"),
          type: "text",
          tag: "p",
          align: "left",
          color: "",
          text: "Colonne 1…",
          style_id: null,
          style_class: "",
          extra_class: "",
        },
        {
          id: newId("blk"),
          type: "text",
          tag: "p",
          align: "left",
          color: "",
          text: "Colonne 2…",
          style_id: null,
          style_class: "",
          extra_class: "",
        },
      ];
    } else if (key === "steps") {
      blocks = [];
    } else if (key === "banner") {
      blocks = [];
    }

    const sectionId = newId("sec");
    const section = {
      id: sectionId,
      template: templateKey,
      title: tpl?.label || "Section",
      background_type: key === "banner" ? "image" : "none",
      background_color: "#0b1220",
      background_image_url: "",
      background: "",
      settings: baseSettings,
      blocks,
      containers: [],
    };
    setContent({ ...content, sections: [...sections, section] });
    setCollapsedSections((prev) => ({
      ...(prev || {}),
      [String(sectionId)]: true,
    }));
  }

  function moveSection(from, to) {
    const next = [...sections];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setContent({ ...content, sections: next });
  }

  function updateSection(id, patch) {
    setContent({
      ...content,
      sections: sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    });
  }

  function deleteSection(id) {
    setContent({ ...content, sections: sections.filter((s) => s.id !== id) });
  }

  function cloneSectionWithNewIds(input) {
    const s = input && typeof input === "object" ? input : {};
    let out = null;
    try {
      out = JSON.parse(JSON.stringify(s));
    } catch {
      out = { ...s };
    }
    out.id = newId("sec");
    out.title = String(out.title || "").trim()
      ? `${out.title} (copie)`
      : "Section (copie)";
    if (Array.isArray(out.blocks)) {
      out.blocks = out.blocks.map((b) => ({ ...(b || {}), id: newId("blk") }));
    }
    if (Array.isArray(out.containers)) {
      out.containers = out.containers.map((c) => ({
        ...(c || {}),
        id: newId("ctr"),
        name: String(c?.name || "").trim()
          ? `${c.name} (copie)`
          : "Conteneur (copie)",
        extra_class: String(c?.extra_class || ""),
        blocks: Array.isArray(c?.blocks)
          ? c.blocks.map((b) => ({ ...(b || {}), id: newId("blk") }))
          : [],
      }));
    }
    if (
      out.settings &&
      typeof out.settings === "object" &&
      !Array.isArray(out.settings) &&
      Array.isArray(out.settings.steps)
    ) {
      out.settings.steps = out.settings.steps.map((st) => ({
        ...(st || {}),
        id: newId("step"),
      }));
    }
    return out;
  }

  function duplicateSection(sectionId) {
    const id = String(sectionId || "");
    const idx = sections.findIndex((s) => String(s.id) === id);
    if (idx < 0) return;
    const copy = cloneSectionWithNewIds(sections[idx]);
    const next = [...sections];
    next.splice(idx + 1, 0, copy);
    setContent({ ...content, sections: next });
    setCollapsedSections((prev) => ({
      ...(prev || {}),
      [String(copy.id)]: true,
    }));
  }

  function toggleTag(tagId) {
    const id = Number(tagId);
    const set = new Set(project.tag_ids || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange({ ...project, tag_ids: Array.from(set) });
  }

  function toggleCategory(categoryId) {
    const id = Number(categoryId);
    const set = new Set(project.category_ids || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange({ ...project, category_ids: Array.from(set) });
  }

  const selectedTags = useMemo(() => {
    const ids = new Set((project.tag_ids || []).map((x) => Number(x)));
    return (tags || []).filter((t) => ids.has(Number(t.id)));
  }, [project.tag_ids, tags]);

  const selectedCategories = useMemo(() => {
    const ids = new Set((project.category_ids || []).map((x) => Number(x)));
    return (categories || []).filter((c) => ids.has(Number(c.id)));
  }, [project.category_ids, categories]);

  const currentVisibility = String(project.visibility || "public");
  const visibilityLabel =
    currentVisibility === "unlisted"
      ? "Non répertorié"
      : currentVisibility === "private"
      ? "Privée"
      : "Public";

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError("");
    const res = await onSave();
    if (!res?.ok) setError(res?.error || "Erreur");
    setSaving(false);
    return res;
  }, [onSave]);

  const handlePublish = useCallback(async () => {
    setSaving(true);
    setError("");
    const res = await onPublish?.();
    if (!res?.ok) setError(res?.error || "Erreur");
    setSaving(false);
    return res;
  }, [onPublish]);

  useEffect(() => {
    function onKeyDown(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (String(e.key || "").toLowerCase() !== "s") return;
      if (e.shiftKey && !projectsUi.shortcuts.publish) return;
      if (!e.shiftKey && !projectsUi.shortcuts.save) return;
      e.preventDefault();
      if (saving) return;
      if (e.shiftKey) {
        if (!projectsUi.draft_mode) return;
        setPublishConfirmModal({ saving: false, error: "" });
        return;
      }
      handleSave();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    handleSave,
    saving,
    projectsUi.shortcuts.save,
    projectsUi.shortcuts.publish,
    projectsUi.draft_mode,
  ]);

  return (
    <div className="Editor Editor--projects">
      <div
        className="Row"
        style={{ marginBottom: 12, justifyContent: "flex-end" }}
      >
        <div className="Badges" style={{ justifyContent: "flex-end" }}>
          {projectsUi.draft_mode && project?.has_draft ? (
            <span className="Badge" title="Brouillon enregistré">
              brouillon
            </span>
          ) : null}
          {lang &&
          defaultLang &&
          lang !== defaultLang &&
          project.has_translation === false ? (
            <span className="Badge">fallback ({defaultLang})</span>
          ) : null}
          {projectsUi.draft_mode ? (
            <button
              type="button"
              className="Button Button--good"
              disabled={saving}
              onClick={() =>
                setPublishConfirmModal({ saving: false, error: "" })
              }
              title="Publier (Cmd/Ctrl+Shift+S)"
            >
              Publier
            </button>
          ) : null}
          {lang && defaultLang && lang !== defaultLang ? (
            <button
              type="button"
              className="Button Button--ghost"
              disabled={saving}
              onClick={openCopyFromLanguageModal}
              title={`Copier le contenu depuis ${defaultLangLabel}`}
            >
              <span className="ButtonInline">
                <IconCopy /> Copier depuis {defaultLangLabel}
              </span>
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginBottom: 12,
        }}
      >
        <div
          className="ViewToggle ProjectEditorViewToggle"
          role="tablist"
          aria-label="Vue projet"
          style={{ width: "100%" }}
        >
          <button
            type="button"
            className={`Button Button--ghost ${
              projectView === "info" ? "is-active" : ""
            }`}
            onClick={() => setProjectView("info")}
            disabled={saving}
            aria-label="Afficher les infos projet"
            aria-selected={projectView === "info"}
            role="tab"
          >
            <span className="ButtonInline">
              <IconFileText /> Infos projet
            </span>
          </button>
          <button
            type="button"
            className={`Button Button--ghost ${
              projectView === "editor" ? "is-active" : ""
            }`}
            onClick={() => {
              setProjectView("editor");
              if (!projectsUi.auto_expand_all_sections) return;
              const next = {};
              for (const s of sections || []) {
                if (!s?.id) continue;
                next[String(s.id)] = false;
              }
              setCollapsedSections(next);
            }}
            disabled={saving}
            aria-label="Afficher l’éditeur de sections"
            aria-selected={projectView === "editor"}
            role="tab"
          >
            <span className="ButtonInline">
              <IconEdit /> Éditeur de sections
            </span>
          </button>
        </div>
      </div>

      {error ? (
        <div className="Error" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {projectView === "info" ? (
        <>
          <div className="Card" style={{ marginBottom: 14 }}>
            <div className="CardHeader">
              <div className="TitleMd">Infos projet</div>
              <div className="Hint">Nom, slug et tags.</div>
            </div>

            <div className="FormGrid">
              <div className="Field">
                <label className="Label" htmlFor="pr-name">
                  Nom
                </label>
                <input
                  id="pr-name"
                  className="Input"
                  value={project.name}
                  onChange={(e) =>
                    onChange({ ...project, name: e.target.value })
                  }
                />
              </div>
              <div className="Field">
                <label className="Label" htmlFor="pr-slug">
                  Slug
                </label>
                <input
                  id="pr-slug"
                  className="Input"
                  value={project.slug}
                  onChange={(e) =>
                    onChange({ ...project, slug: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="Field" style={{ marginTop: 8 }}>
              <label className="Label" htmlFor="pr-desc">
                Description
              </label>
              <textarea
                id="pr-desc"
                className="Textarea"
                rows={5}
                value={String(content?.meta?.description || "")}
                onKeyDown={(e) =>
                  handleTextareaFormatShortcuts(
                    e,
                    (nextValue) => patchProjectMeta({ description: nextValue }),
                    { enabled: Boolean(projectsUi?.shortcuts?.format_biu) }
                  )
                }
                onChange={(e) => {
                  patchProjectMeta({ description: e.target.value });
                }}
                disabled={saving}
              />
            </div>

            <div className="Field" style={{ marginTop: 8 }}>
              <label className="Label">Résumé (3 phrases)</label>
              <div style={{ display: "grid", gap: 8 }}>
                {[0, 1, 2].map((idx) => (
                  <input
                    key={idx}
                    className="Input"
                    value={String(content?.meta?.summary_lines?.[idx] || "")}
                    onChange={(e) => {
                      const base =
                        content &&
                        typeof content === "object" &&
                        !Array.isArray(content)
                          ? content
                          : defaultContent();
                      const meta =
                        base.meta &&
                        typeof base.meta === "object" &&
                        !Array.isArray(base.meta)
                          ? base.meta
                          : {};
                      const current = Array.isArray(meta.summary_lines)
                        ? meta.summary_lines.slice(0, 3)
                        : [];
                      while (current.length < 3) current.push("");
                      current[idx] = e.target.value;
                      setContent({
                        ...base,
                        meta: { ...meta, summary_lines: current },
                      });
                    }}
                    placeholder={`Phrase ${idx + 1}`}
                    disabled={saving}
                  />
                ))}
              </div>
            </div>

            <details>
              <summary className="Muted" style={{ cursor: "pointer" }}>
                Options (images)
              </summary>
              <div style={{ marginTop: 12 }}>
                <div className="FormGrid">
                  <div className="Field">
                    <label className="Label" htmlFor="pr-cover">
                      Image projet
                    </label>
                    <div className="InputRow">
                      <input
                        id="pr-cover"
                        className="Input"
                        value={String(project.cover_image_url || "")}
                        onChange={(e) =>
                          onChange({
                            ...project,
                            cover_image_url: e.target.value,
                          })
                        }
                        disabled={saving}
                        placeholder="Aucune image"
                      />
                      <button
                        type="button"
                        className="Button Button--ghost Button--resources"
                        onClick={() =>
                          setProjectImagePicker({ target: "cover" })
                        }
                        disabled={saving}
                      >
                        <span className="ButtonInline">
                          <IconFolder /> Source
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="Field">
                    <label className="Label" htmlFor="pr-bg">
                      Background
                    </label>
                    <div className="InputRow">
                      <input
                        id="pr-bg"
                        className="Input"
                        value={String(project.background_image_url || "")}
                        onChange={(e) =>
                          onChange({
                            ...project,
                            background_image_url: e.target.value,
                          })
                        }
                        disabled={saving}
                        placeholder="Aucune image"
                      />
                      <button
                        type="button"
                        className="Button Button--ghost Button--resources"
                        onClick={() =>
                          setProjectImagePicker({ target: "background" })
                        }
                        disabled={saving}
                      >
                        <span className="ButtonInline">
                          <IconFolder /> Source
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </div>
          <div className="Card" style={{ marginBottom: 14 }}>
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Liens</div>
              <div className="Badges">
                <button
                  type="button"
                  className="Button Button--ghost Button--soft"
                  onClick={() => {
                    const current = Array.isArray(content?.meta?.links)
                      ? content.meta.links
                      : [];
                    const next = [
                      ...current,
                      { id: newId("link"), label: "", url: "" },
                    ];
                    patchProjectMeta({ links: next });
                  }}
                  disabled={saving}
                >
                  <span className="ButtonInline">
                    <IconPlus /> Ajouter un lien
                  </span>
                </button>
              </div>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {(Array.isArray(content?.meta?.links) ? content.meta.links : [])
                .slice(0, 30)
                .map((l, idx) => {
                  const item = l && typeof l === "object" ? l : {};
                  const id = String(item.id || "") || `link_${idx}`;
                  return (
                    <div key={id} className="InputRow">
                      <input
                        className="Input"
                        value={String(item.label || "")}
                        placeholder="Label"
                        onChange={(e) => {
                          const current = Array.isArray(content?.meta?.links)
                            ? content.meta.links
                            : [];
                          const next = current.map((x, i) =>
                            i === idx
                              ? { ...(x || {}), label: e.target.value }
                              : x
                          );
                          patchProjectMeta({ links: next });
                        }}
                        disabled={saving}
                      />
                      <input
                        className="Input"
                        value={String(item.url || "")}
                        placeholder="URL"
                        onChange={(e) => {
                          const current = Array.isArray(content?.meta?.links)
                            ? content.meta.links
                            : [];
                          const next = current.map((x, i) =>
                            i === idx
                              ? { ...(x || {}), url: e.target.value }
                              : x
                          );
                          patchProjectMeta({ links: next });
                        }}
                        disabled={saving}
                      />
                      <button
                        type="button"
                        className="Button Button--ghost Button--danger Button--icon"
                        onClick={() => {
                          const current = Array.isArray(content?.meta?.links)
                            ? content.meta.links
                            : [];
                          const next = current.filter((_, i) => i !== idx);
                          patchProjectMeta({ links: next });
                        }}
                        disabled={saving}
                        title="Supprimer"
                        aria-label="Supprimer"
                      >
                        <IconTrash />
                      </button>
                    </div>
                  );
                })}
              {!(
                Array.isArray(content?.meta?.links) && content.meta.links.length
              ) ? (
                <div className="Muted">Aucun lien.</div>
              ) : null}
            </div>
          </div>

          <div className="Card" style={{ marginBottom: 14 }}>
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Catégories</div>
              <div className="Badges">
                <button
                  type="button"
                  className="Button Button--ghost Button--soft"
                  onClick={() => setCategoryPickerOpen(true)}
                  disabled={saving}
                >
                  Choisir…
                </button>
              </div>
            </div>
            <div className="Badges" style={{ justifyContent: "flex-start" }}>
              {selectedCategories.map((c) => (
                <span key={c.id} className="Badge">
                  {c.name}
                </span>
              ))}
              {selectedCategories.length === 0 ? (
                <span className="Muted">Aucune catégorie sélectionnée.</span>
              ) : null}
            </div>
          </div>

          <div className="Card" style={{ marginBottom: 14 }}>
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Tags</div>
              <div className="Badges">
                <button
                  type="button"
                  className="Button Button--ghost Button--soft"
                  onClick={() => setTagPickerOpen(true)}
                  disabled={saving}
                >
                  Choisir…
                </button>
              </div>
            </div>
            <div className="Badges" style={{ justifyContent: "flex-start" }}>
              {selectedTags.map((t) => (
                <span
                  key={t.id}
                  className="Badge"
                  style={{
                    borderColor: t.color,
                    color: t.color,
                    background: `color-mix(in srgb, ${t.color} 18%, transparent)`,
                  }}
                >
                  {t.name}
                </span>
              ))}
              {selectedTags.length === 0 ? (
                <span className="Muted">Aucun tag sélectionné.</span>
              ) : null}
            </div>
          </div>

          <div className="Card" style={{ marginBottom: 14 }}>
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Actions</div>
            </div>
            <div className="Badges" style={{ justifyContent: "flex-start" }}>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setArchiveModalOpen(true)}
                disabled={saving}
              >
                <span className="ButtonInline">
                  <IconBan /> Archiver
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setVisibilityModalOpen(true)}
                disabled={saving}
              >
                <span className="ButtonInline">
                  <IconGlobe /> Visibilité: {visibilityLabel}
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={openDuplicateContentModal}
                disabled={saving || !canOpenDuplicateContent}
                title={
                  !canOpenDuplicateContent
                    ? "Aucune cible disponible"
                    : "Dupliquer le contenu vers un autre projet ou une autre langue"
                }
              >
                <span className="ButtonInline">
                  <IconCopy /> Dupliquer le contenu…
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--danger"
                onClick={() =>
                  setDeleteProjectConfirmModal({ saving: false, error: "" })
                }
                disabled={saving}
                title="Supprimer le projet"
              >
                <span className="ButtonInline">
                  <IconTrash /> Supprimer
                </span>
              </button>
            </div>
          </div>
        </>
      ) : null}

      {projectView === "editor" ? (
        <div className="Card SectionsCard">
          <div className="Row" style={{ marginBottom: 10 }}>
            <div className="TitleMd">Sections</div>
            <div className="Badges">
              <button
                type="button"
                className="Button Button--ghost"
                onClick={onManageStyles}
                disabled={saving}
                title="Styles réutilisables pour les sections"
              >
                <span className="ButtonInline">
                  <IconLayers /> Styles
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--soft"
                onClick={() => setSectionPickerOpen(true)}
                disabled={saving}
              >
                <span className="ButtonInline">
                  <IconPlus /> Ajouter une section
                </span>
              </button>
            </div>
          </div>

          {sections.length === 0 ? (
            <div className="Muted">Aucune section. Ajoute un template.</div>
          ) : null}

          <div className="Sections">
            {sections.map((s, idx) => (
              <div
                key={s.id}
                className={`SectionDropTarget ${
                  Number(sectionDropTarget?.index) === idx &&
                  sectionDropTarget?.pos === "before"
                    ? "is-drop-before"
                    : Number(sectionDropTarget?.index) === idx &&
                      sectionDropTarget?.pos === "after"
                    ? "is-drop-after"
                    : ""
                }`}
                onDragOver={(e) => {
                  if (dragIndex == null) return;
                  e.preventDefault();
                  const r = e.currentTarget.getBoundingClientRect();
                  const y = Number(e.clientY) || 0;
                  const pos = y <= r.top + r.height / 2 ? "before" : "after";
                  if (
                    Number(sectionDropTarget?.index) !== idx ||
                    sectionDropTarget?.pos !== pos
                  ) {
                    setSectionDropTarget({ index: idx, pos });
                  }
                }}
                onDrop={(e) => {
                  if (dragIndex == null) return;
                  e.preventDefault();
                  const pos =
                    Number(sectionDropTarget?.index) === idx
                      ? String(sectionDropTarget?.pos || "before")
                      : "before";
                  let insertIndex = pos === "after" ? idx + 1 : idx;
                  if (dragIndex < insertIndex) insertIndex -= 1;
                  if (
                    Number.isFinite(insertIndex) &&
                    insertIndex >= 0 &&
                    insertIndex !== dragIndex
                  ) {
                    moveSection(dragIndex, insertIndex);
                  }
                  setDragIndex(null);
                  setSectionDropTarget(null);
                }}
              >
                <SectionEditorCard
                  section={s}
                  index={idx}
                  templates={templates}
                  projectTitle={project.name}
                  siteId={Number(siteId)}
                  projectId={Number(project.id)}
                  assetsBaseUrl={assetsBaseUrl}
                  styles={styles || []}
                  mediaById={mediaById}
                  projectImageAssets={projectImageAssets}
                  projectsUi={projectsUi}
                  saving={saving}
                  onSectionDragStart={() => setDragIndex(idx)}
                  onSectionDragEnd={() => {
                    setDragIndex(null);
                    setSectionDropTarget(null);
                  }}
                  onUpdate={updateSection}
                  onDuplicate={duplicateSection}
                  onDelete={deleteSection}
                  onRequestDuplicateContainer={requestDuplicateContainer}
                  collapsed={Boolean(collapsedSections?.[String(s.id)])}
                  onToggleCollapsed={() => {
                    const sid = String(s.id);
                    setCollapsedSections((prev) => {
                      const cur = prev || {};
                      const currentlyCollapsed = Boolean(cur?.[sid]);
                      const nextCollapsed = !currentlyCollapsed;
                      if (
                        projectsUi.section_open_mode === "single" &&
                        nextCollapsed === false
                      ) {
                        const next = {};
                        for (const sec of sections || []) {
                          if (!sec?.id) continue;
                          next[String(sec.id)] = true;
                        }
                        next[sid] = false;
                        return next;
                      }
                      return { ...cur, [sid]: nextCollapsed };
                    });
                  }}
                  onContextMenu={openCtxMenu}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {sectionPickerOpen ? (
        <SectionPickerModal
          templates={templates}
          onClose={() => setSectionPickerOpen(false)}
          onPick={(key) => {
            addSection(key);
            setSectionPickerOpen(false);
          }}
        />
      ) : null}

      {tagPickerOpen ? (
        <TagPickerModal
          tags={tags || []}
          selectedIds={project.tag_ids || []}
          onToggle={(id) => toggleTag(id)}
          onClose={() => setTagPickerOpen(false)}
          onManage={() => {
            setTagPickerOpen(false);
            onManageTags?.();
          }}
        />
      ) : null}

      {categoryPickerOpen ? (
        <CategoryPickerModal
          categories={categories || []}
          selectedIds={project.category_ids || []}
          onToggle={(id) => toggleCategory(id)}
          onClose={() => setCategoryPickerOpen(false)}
          onManage={() => {
            setCategoryPickerOpen(false);
            onManageCategories?.();
          }}
        />
      ) : null}

      {projectImagePicker ? (
        <ProjectResourcesImagePickerModal
          siteId={Number(siteId)}
          projectId={Number(project.id)}
          apiBaseUrl={apiBaseUrl}
          assetsBaseUrl={assetsBaseUrl}
          assets={projectImageAssets}
          onClose={() => setProjectImagePicker(null)}
          onPick={(asset) => {
            const url = mediaAssetSourceValue(asset, {
              apiBaseUrl,
              siteId: Number(siteId),
              assetsBaseUrl,
            });
            if (projectImagePicker.target === "cover")
              onChange({ ...project, cover_image_url: url });
            if (projectImagePicker.target === "background")
              onChange({ ...project, background_image_url: url });
            setProjectImagePicker(null);
          }}
        />
      ) : null}

      {duplicateContainerModal ? (
        <Modal
          title="Dupliquer le conteneur"
          icon={<IconCopy />}
          onClose={() => setDuplicateContainerModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setDuplicateContainerModal(null)}
                disabled={duplicateContainerModal.saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={() => confirmDuplicateContainer()}
                disabled={
                  duplicateContainerModal.saving ||
                  duplicateContainerModal.loadingTarget ||
                  !String(duplicateContainerModal.targetSectionId || "")
                }
              >
                {duplicateContainerModal.saving ? "Duplication…" : "Dupliquer"}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Projet cible</label>
            <select
              className="Select"
              value={String(duplicateContainerModal.targetProjectId || "")}
              onChange={(e) => {
                const nextId = String(e.target.value || "");
                setDuplicateContainerModal((m) =>
                  m
                    ? {
                        ...m,
                        targetProjectId: nextId,
                        targetSectionId:
                          Number(nextId) === Number(project.id)
                            ? String(m.fromSectionId || "")
                            : "",
                        targetProject: null,
                        error: "",
                      }
                    : m
                );
              }}
              disabled={duplicateContainerModal.saving}
            >
              {[
                { id: project.id, name: project.name, isCurrent: true },
                ...(Array.isArray(projects) ? projects : [])
                  .filter((p) => p && Number(p.id) !== Number(project.id))
                  .map((p) => ({ id: p.id, name: p.name, isCurrent: false })),
              ].map((p) => (
                <option key={String(p.id)} value={String(p.id)}>
                  {p.name}
                  {p.isCurrent ? " (actuel)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="Field">
            <label className="Label">Section cible</label>
            <select
              className="Select"
              value={String(duplicateContainerModal.targetSectionId || "")}
              onChange={(e) =>
                setDuplicateContainerModal((m) =>
                  m
                    ? { ...m, targetSectionId: String(e.target.value || "") }
                    : m
                )
              }
              disabled={
                duplicateContainerModal.saving ||
                duplicateContainerModal.loadingTarget ||
                !(Number(duplicateContainerModal.targetProjectId) ===
                Number(project.id)
                  ? sections.length
                  : (
                      duplicateContainerModal.targetProject?.content
                        ?.sections || []
                    ).length)
              }
            >
              {(Number(duplicateContainerModal.targetProjectId) ===
              Number(project.id)
                ? sections
                : duplicateContainerModal.targetProject?.content?.sections || []
              ).map((s, idx) => (
                <option key={String(s.id)} value={String(s.id)}>
                  #{idx + 1} — {s.title || "Section"}
                </option>
              ))}
            </select>
            {duplicateContainerModal.loadingTarget ? (
              <div className="Muted" style={{ marginTop: 6 }}>
                Chargement du projet…
              </div>
            ) : null}
            {Number(duplicateContainerModal.targetProjectId) ===
              Number(project.id) && sections.length === 0 ? (
              <div className="Muted" style={{ marginTop: 6 }}>
                Ajoute d’abord une section sur ce projet.
              </div>
            ) : null}
          </div>

          <div className="Muted">
            Le conteneur est ajouté à la fin de la section.
          </div>

          {duplicateContainerModal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {duplicateContainerModal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {archiveModalOpen ? (
        <Modal
          title="Archiver"
          icon={<IconBan />}
          onClose={() => setArchiveModalOpen(false)}
          footer={
            <button
              type="button"
              className="Button Button--ghost"
              onClick={() => setArchiveModalOpen(false)}
            >
              Fermer
            </button>
          }
        >
          <div className="Muted">Fonction à venir.</div>
        </Modal>
      ) : null}

      {visibilityModalOpen ? (
        <Modal
          title="Visibilité du projet"
          icon={<IconGlobe />}
          onClose={() => setVisibilityModalOpen(false)}
          footer={
            <button
              type="button"
              className="Button Button--ghost"
              onClick={() => setVisibilityModalOpen(false)}
            >
              Fermer
            </button>
          }
        >
          <div className="Muted" style={{ marginBottom: 10 }}>
            Choisis le niveau d’accès côté front.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <button
              type="button"
              className={`Button ${
                currentVisibility === "public"
                  ? "Button--good"
                  : "Button--ghost"
              }`}
              onClick={() => {
                onChange({ ...project, visibility: "public" });
                setVisibilityModalOpen(false);
              }}
              disabled={saving}
            >
              <span className="ButtonInline" style={{ width: "100%" }}>
                Public
              </span>
            </button>
            <button
              type="button"
              className={`Button ${
                currentVisibility === "unlisted"
                  ? "Button--good"
                  : "Button--ghost"
              }`}
              onClick={() => {
                onChange({ ...project, visibility: "unlisted" });
                setVisibilityModalOpen(false);
              }}
              disabled={saving}
            >
              <span className="ButtonInline" style={{ width: "100%" }}>
                Non répertorié
              </span>
            </button>
            <button
              type="button"
              className={`Button ${
                currentVisibility === "private"
                  ? "Button--good"
                  : "Button--ghost"
              }`}
              onClick={() => {
                onChange({ ...project, visibility: "private" });
                setVisibilityModalOpen(false);
              }}
              disabled={saving}
            >
              <span className="ButtonInline" style={{ width: "100%" }}>
                Privée
              </span>
            </button>
          </div>
          <div className="Hint" style={{ marginTop: 10 }}>
            Public: visible dans la liste. Non répertorié: accessible par URL
            directe. Privée: inaccessible côté front.
          </div>
        </Modal>
      ) : null}

      {deleteProjectConfirmModal ? (
        <Modal
          title="Suppression"
          icon={<IconAlertTriangle />}
          onClose={() => setDeleteProjectConfirmModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setDeleteProjectConfirmModal(null)}
                disabled={deleteProjectConfirmModal.saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                onClick={async () => {
                  try {
                    setDeleteProjectConfirmModal((m) =>
                      m ? { ...m, saving: true, error: "" } : m
                    );
                    const res = await onDeleteProject?.();
                    if (res?.ok === false) {
                      setDeleteProjectConfirmModal((m) =>
                        m
                          ? {
                              ...m,
                              saving: false,
                              error: res?.error || "Suppression impossible",
                            }
                          : m
                      );
                      return;
                    }
                    setDeleteProjectConfirmModal(null);
                  } catch {
                    setDeleteProjectConfirmModal((m) =>
                      m
                        ? {
                            ...m,
                            saving: false,
                            error: "Suppression impossible",
                          }
                        : m
                    );
                  }
                }}
                disabled={deleteProjectConfirmModal.saving}
              >
                {deleteProjectConfirmModal.saving
                  ? "Suppression…"
                  : "Supprimer"}
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer <strong>{project.name || "ce projet"}</strong> ?
          </div>
          {deleteProjectConfirmModal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {deleteProjectConfirmModal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {publishConfirmModal ? (
        <Modal
          title="Publier le brouillon"
          icon={<IconAlertTriangle />}
          onClose={() => setPublishConfirmModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setPublishConfirmModal(null)}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--good"
                onClick={async () => {
                  setPublishConfirmModal((m) => (m ? { ...m, error: "" } : m));
                  const res = await handlePublish();
                  if (!res?.ok) {
                    setPublishConfirmModal((m) =>
                      m
                        ? {
                            ...m,
                            error: res?.error || "Publication impossible",
                          }
                        : m
                    );
                    return;
                  }
                  setPublishConfirmModal(null);
                }}
                disabled={saving}
              >
                Publier
              </button>
            </>
          }
        >
          <div className="Muted">
            {projectsUi.shortcuts.save
              ? "Cmd/Ctrl+S enregistre en brouillon. "
              : ""}
            {projectsUi.shortcuts.publish
              ? "Cmd/Ctrl+Shift+S publie ce brouillon."
              : ""}
          </div>
          {publishConfirmModal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {publishConfirmModal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {duplicateContentModal ? (
        <Modal
          title="Dupliquer le contenu"
          icon={<IconCopy />}
          onClose={() => setDuplicateContentModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setDuplicateContentModal(null)}
                disabled={duplicateContentModal.loading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={
                  duplicateContentModal.loading ||
                  !String(duplicateContentModal.targetProjectId || "") ||
                  !String(duplicateContentModal.targetLang || "") ||
                  (Number(duplicateContentModal.targetProjectId) ===
                    Number(project.id) &&
                    String(duplicateContentModal.targetLang) ===
                      String(lang)) ||
                  !Boolean(
                    duplicateContentModal.copyDescription ||
                      duplicateContentModal.copyLinks ||
                      duplicateContentModal.copySummary ||
                      (duplicateContentModal.sectionIds || []).length
                  )
                }
                onClick={() =>
                  prepareDuplicateContentTo(
                    duplicateContentModal.targetProjectId,
                    duplicateContentModal
                  )
                }
              >
                {duplicateContentModal.loading ? "Duplication…" : "Dupliquer"}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Projet cible</label>
            <select
              className="Select"
              value={String(duplicateContentModal.targetProjectId || "")}
              onChange={(e) =>
                setDuplicateContentModal((m) =>
                  m
                    ? {
                        ...m,
                        targetProjectId: String(e.target.value || ""),
                        error: "",
                      }
                    : m
                )
              }
              disabled={duplicateContentModal.loading}
            >
              {[
                { id: project.id, name: project.name, isCurrent: true },
                ...(projects || [])
                  .filter((p) => p && Number(p.id) !== Number(project.id))
                  .map((p) => ({ id: p.id, name: p.name, isCurrent: false })),
              ].map((p) => (
                <option key={String(p.id)} value={String(p.id)}>
                  {p.name}
                  {p.isCurrent ? " (actuel)" : ""}
                </option>
              ))}
            </select>
          </div>
          {enabledLanguages.length > 1 ? (
            <div className="Field">
              <label className="Label">Langue cible</label>
              <select
                className="Select"
                value={String(duplicateContentModal.targetLang || "")}
                onChange={(e) =>
                  setDuplicateContentModal((m) =>
                    m
                      ? {
                          ...m,
                          targetLang: String(e.target.value || ""),
                          error: "",
                        }
                      : m
                  )
                }
                disabled={duplicateContentModal.loading}
              >
                <option value="" disabled>
                  Choisir…
                </option>
                {enabledLanguages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
              {Number(duplicateContentModal.targetProjectId) ===
                Number(project.id) &&
              String(duplicateContentModal.targetLang || "") ===
                String(lang) ? (
                <div className="Hint" style={{ marginTop: 8 }}>
                  Choisis une autre langue (ou un autre projet) pour dupliquer.
                </div>
              ) : (
                <div className="Hint" style={{ marginTop: 8 }}>
                  Seules les options cochées seront copiées.
                </div>
              )}
            </div>
          ) : (
            <div className="Hint" style={{ marginTop: 8 }}>
              Seules les options cochées seront copiées.
            </div>
          )}
          <div className="Field">
            <label className="Label">À copier</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label className="ButtonInline" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(duplicateContentModal.copyDescription)}
                  onChange={(e) =>
                    setDuplicateContentModal((m) =>
                      m
                        ? { ...m, copyDescription: Boolean(e.target.checked) }
                        : m
                    )
                  }
                  disabled={duplicateContentModal.loading}
                />
                <span>Description</span>
              </label>
              <label className="ButtonInline" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(duplicateContentModal.copyLinks)}
                  onChange={(e) =>
                    setDuplicateContentModal((m) =>
                      m ? { ...m, copyLinks: Boolean(e.target.checked) } : m
                    )
                  }
                  disabled={duplicateContentModal.loading}
                />
                <span>Liens</span>
              </label>
              <label className="ButtonInline" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(duplicateContentModal.copySummary)}
                  onChange={(e) =>
                    setDuplicateContentModal((m) =>
                      m ? { ...m, copySummary: Boolean(e.target.checked) } : m
                    )
                  }
                  disabled={duplicateContentModal.loading}
                />
                <span>Résumé (3 phrases)</span>
              </label>
            </div>
          </div>
          <div className="Field">
            <label className="Label">Sections à copier</label>
            <div
              className="Card"
              style={{
                padding: 12,
                borderRadius: 12,
                border: "1px solid var(--border)",
                background:
                  "color-mix(in srgb, var(--panel2) 70%, transparent)",
              }}
            >
              <div
                className="ButtonInline"
                style={{
                  justifyContent: "space-between",
                  width: "100%",
                  marginBottom: 10,
                }}
              >
                <div className="Muted" style={{ fontSize: 12 }}>
                  {(duplicateContentModal.sectionIds || []).length}{" "}
                  sélectionnée(s)
                </div>
                <div className="ButtonInline" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() =>
                      setDuplicateContentModal((m) =>
                        m
                          ? {
                              ...m,
                              sectionIds: (sections || [])
                                .map((s) => String(s?.id || ""))
                                .filter(Boolean),
                            }
                          : m
                      )
                    }
                    disabled={duplicateContentModal.loading}
                  >
                    Tout
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() =>
                      setDuplicateContentModal((m) =>
                        m ? { ...m, sectionIds: [] } : m
                      )
                    }
                    disabled={duplicateContentModal.loading}
                  >
                    Aucun
                  </button>
                </div>
              </div>
              <div
                style={{
                  maxHeight: 180,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  paddingRight: 6,
                }}
              >
                {(sections || []).map((s) => {
                  const sid = String(s?.id || "");
                  const checked = (
                    duplicateContentModal.sectionIds || []
                  ).includes(sid);
                  const title = String(s?.title || "").trim() || "Sans titre";
                  return (
                    <label
                      key={sid}
                      className="ButtonInline"
                      style={{ gap: 10, alignItems: "center" }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = Boolean(e.target.checked);
                          setDuplicateContentModal((m) => {
                            if (!m) return m;
                            const current = Array.isArray(m.sectionIds)
                              ? m.sectionIds
                                  .map((x) => String(x || ""))
                                  .filter(Boolean)
                              : [];
                            const set = new Set(current);
                            if (nextChecked) set.add(sid);
                            else set.delete(sid);
                            return { ...m, sectionIds: Array.from(set) };
                          });
                        }}
                        disabled={duplicateContentModal.loading}
                      />
                      <span>{title}</span>
                      <span
                        className="Muted"
                        style={{ marginLeft: "auto", fontSize: 12 }}
                      >
                        {String(s?.template || "")}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="Hint" style={{ marginTop: 10 }}>
                {(duplicateContentModal.sectionIds || []).length > 0
                  ? "Les sections sélectionnées remplaceront celles du projet cible."
                  : "Les sections du projet cible seront conservées."}
              </div>
            </div>
          </div>
          {duplicateContentModal.error ? (
            <div className="Error" role="alert">
              {duplicateContentModal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {copyFromLangModal?.type === "copy-from-lang" ? (
        <Modal
          title="Copier le contenu"
          icon={<IconCopy />}
          onClose={() => {
            setCopyFromLangConfirmModal(null);
            setCopyFromLangModal(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => {
                  setCopyFromLangConfirmModal(null);
                  setCopyFromLangModal(null);
                }}
                disabled={copyFromLangModal.loadingSource}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={requestCopyFromLanguageApply}
                disabled={
                  copyFromLangModal.loadingSource ||
                  !String(copyFromLangModal.sourceLang || "") ||
                  !Boolean(
                    copyFromLangModal.copyDescription ||
                      copyFromLangModal.copyLinks ||
                      copyFromLangModal.copySummary ||
                      (copyFromLangModal.sectionIds || []).length
                  )
                }
              >
                Copier
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Version source</label>
            <select
              className="Select"
              value={String(copyFromLangModal.sourceLang || "")}
              onChange={(e) =>
                setCopyFromLangModal((m) =>
                  m && m.type === "copy-from-lang"
                    ? {
                        ...m,
                        sourceLang: String(e.target.value || ""),
                        error: "",
                        sectionIds: [],
                      }
                    : m
                )
              }
              disabled={copyFromLangModal.loadingSource}
            >
              <option value="" disabled>
                Choisir…
              </option>
              {enabledLanguages
                .filter((l) => String(l.code) !== String(lang))
                .map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              {enabledLanguages.some((l) => String(l.code) === String(lang)) ? (
                <option value={String(lang)}>
                  {labelForLang(lang)} (actuelle)
                </option>
              ) : null}
            </select>
            <div className="Hint" style={{ marginTop: 8 }}>
              Cible: <strong>{labelForLang(lang) || String(lang || "")}</strong>
            </div>
            {copyFromLangModal.loadingSource ? (
              <div className="Muted" style={{ marginTop: 8 }}>
                Chargement de la version…
              </div>
            ) : null}
          </div>

          <div className="Field">
            <label className="Label">À copier</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label className="ButtonInline" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(copyFromLangModal.copyDescription)}
                  onChange={(e) =>
                    setCopyFromLangModal((m) =>
                      m && m.type === "copy-from-lang"
                        ? {
                            ...m,
                            copyDescription: Boolean(e.target.checked),
                            error: "",
                          }
                        : m
                    )
                  }
                  disabled={copyFromLangModal.loadingSource}
                />
                <span>Description</span>
              </label>
              <label className="ButtonInline" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(copyFromLangModal.copyLinks)}
                  onChange={(e) =>
                    setCopyFromLangModal((m) =>
                      m && m.type === "copy-from-lang"
                        ? {
                            ...m,
                            copyLinks: Boolean(e.target.checked),
                            error: "",
                          }
                        : m
                    )
                  }
                  disabled={copyFromLangModal.loadingSource}
                />
                <span>Liens</span>
              </label>
              <label className="ButtonInline" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(copyFromLangModal.copySummary)}
                  onChange={(e) =>
                    setCopyFromLangModal((m) =>
                      m && m.type === "copy-from-lang"
                        ? {
                            ...m,
                            copySummary: Boolean(e.target.checked),
                            error: "",
                          }
                        : m
                    )
                  }
                  disabled={copyFromLangModal.loadingSource}
                />
                <span>Résumé (3 phrases)</span>
              </label>
            </div>
          </div>

          <div className="Field">
            <label className="Label">Sections à copier</label>
            <div
              className="Card"
              style={{
                padding: 12,
                borderRadius: 12,
                border: "1px solid var(--border)",
                background:
                  "color-mix(in srgb, var(--panel2) 70%, transparent)",
              }}
            >
              <div
                className="ButtonInline"
                style={{
                  justifyContent: "space-between",
                  width: "100%",
                  marginBottom: 10,
                }}
              >
                <div className="Muted" style={{ fontSize: 12 }}>
                  {(copyFromLangModal.sectionIds || []).length} sélectionnée(s)
                </div>
                <div className="ButtonInline" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() =>
                      setCopyFromLangModal((m) =>
                        m && m.type === "copy-from-lang"
                          ? {
                              ...m,
                              sectionIds: (m.sourceSections || [])
                                .map((s) => String(s?.id || ""))
                                .filter(Boolean),
                              error: "",
                            }
                          : m
                      )
                    }
                    disabled={copyFromLangModal.loadingSource}
                  >
                    Tout
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() =>
                      setCopyFromLangModal((m) =>
                        m && m.type === "copy-from-lang"
                          ? { ...m, sectionIds: [], error: "" }
                          : m
                      )
                    }
                    disabled={copyFromLangModal.loadingSource}
                  >
                    Aucun
                  </button>
                </div>
              </div>
              <div
                style={{
                  maxHeight: 180,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  paddingRight: 6,
                }}
              >
                {(copyFromLangModal.sourceSections || []).map((s) => {
                  const sid = String(s?.id || "");
                  const checked = (copyFromLangModal.sectionIds || []).includes(
                    sid
                  );
                  const title = String(s?.title || "").trim() || "Sans titre";
                  return (
                    <label
                      key={sid}
                      className="ButtonInline"
                      style={{ gap: 10, alignItems: "center" }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = Boolean(e.target.checked);
                          setCopyFromLangModal((m) => {
                            if (!m || m.type !== "copy-from-lang") return m;
                            const current = Array.isArray(m.sectionIds)
                              ? m.sectionIds
                                  .map((x) => String(x || ""))
                                  .filter(Boolean)
                              : [];
                            const set = new Set(current);
                            if (nextChecked) set.add(sid);
                            else set.delete(sid);
                            return {
                              ...m,
                              sectionIds: Array.from(set),
                              error: "",
                            };
                          });
                        }}
                        disabled={copyFromLangModal.loadingSource}
                      />
                      <span>{title}</span>
                      <span
                        className="Muted"
                        style={{ marginLeft: "auto", fontSize: 12 }}
                      >
                        {String(s?.template || "")}
                      </span>
                    </label>
                  );
                })}
                {(copyFromLangModal.sourceSections || []).length === 0 &&
                !copyFromLangModal.loadingSource ? (
                  <div className="Muted">
                    Aucune section dans cette version.
                  </div>
                ) : null}
              </div>
              <div className="Hint" style={{ marginTop: 10 }}>
                {(copyFromLangModal.sectionIds || []).length > 0
                  ? "Les sections sélectionnées remplaceront celles de la version actuelle."
                  : "Les sections de la version actuelle seront conservées."}
              </div>
            </div>
          </div>

          {copyFromLangModal.error ? (
            <div className="Error" role="alert">
              {copyFromLangModal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {copyFromLangConfirmModal ? (
        <Modal
          title="Écraser les sections ?"
          icon={<IconAlertTriangle />}
          onClose={() => setCopyFromLangConfirmModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setCopyFromLangConfirmModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                onClick={() => {
                  setCopyFromLangConfirmModal(null);
                  applyCopyFromLanguageModal();
                }}
              >
                Continuer
              </button>
            </>
          }
        >
          <div className="Muted">
            La version actuelle contient déjà{" "}
            <strong>{copyFromLangConfirmModal.sectionsCount || 0}</strong>{" "}
            section(s). Cette action va les remplacer par{" "}
            <strong>
              {copyFromLangConfirmModal.selectedSectionsCount || 0}
            </strong>{" "}
            section(s) depuis{" "}
            <strong>
              {labelForLang(copyFromLangConfirmModal.sourceLang) ||
                copyFromLangConfirmModal.sourceLang}
            </strong>
            .
          </div>
        </Modal>
      ) : null}

      {duplicateContentConfirmModal ? (
        <Modal
          title="Écraser le contenu ?"
          icon={<IconAlertTriangle />}
          onClose={() => setDuplicateContentConfirmModal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setDuplicateContentConfirmModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                onClick={async () => {
                  const next = duplicateContentConfirmModal;
                  setDuplicateContentConfirmModal(null);
                  setDuplicateContentConfirm2Modal(next);
                }}
              >
                Continuer
              </button>
            </>
          }
        >
          <div className="Muted">
            Le projet{" "}
            <strong>
              {duplicateContentConfirmModal.targetProject?.name || "cible"}
            </strong>{" "}
            {duplicateContentConfirmModal.targetLang ? (
              <>
                (langue{" "}
                <strong>
                  {labelForLang(duplicateContentConfirmModal.targetLang)}
                </strong>
                )
              </>
            ) : null}{" "}
            contient déjà{" "}
            <strong>{duplicateContentConfirmModal.sectionsCount || 0}</strong>{" "}
            section(s). Cette action va les remplacer par{" "}
            <strong>
              {duplicateContentConfirmModal.selectedSectionsCount || 0}
            </strong>{" "}
            section(s).
          </div>
        </Modal>
      ) : null}

      {duplicateContentConfirm2Modal ? (
        <Modal
          title="Dernière confirmation"
          icon={<IconAlertTriangle />}
          onClose={() => setDuplicateContentConfirm2Modal(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setDuplicateContentConfirm2Modal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                onClick={async () => {
                  const tp = duplicateContentConfirm2Modal.targetProject;
                  const options = duplicateContentConfirm2Modal.options;
                  const targetLang = duplicateContentConfirm2Modal.targetLang;
                  setDuplicateContentConfirm2Modal(null);
                  await performDuplicateContentTo(tp, {
                    ...options,
                    targetLang,
                  });
                }}
              >
                Écraser et dupliquer
              </button>
            </>
          }
        >
          <div className="Muted">
            Tu vas supprimer{" "}
            <strong>{duplicateContentConfirm2Modal.sectionsCount || 0}</strong>{" "}
            section(s) du projet{" "}
            <strong>
              {duplicateContentConfirm2Modal.targetProject?.name || "cible"}
            </strong>{" "}
            et copier{" "}
            <strong>
              {duplicateContentConfirm2Modal.selectedSectionsCount || 0}
            </strong>{" "}
            section(s) à la place.
          </div>
        </Modal>
      ) : null}

      {duplicateContentResultModal ? (
        <Modal
          title={
            duplicateContentResultModal.ok ? "Duplication terminée" : "Erreur"
          }
          icon={
            duplicateContentResultModal.ok ? (
              <IconCheck />
            ) : (
              <IconAlertTriangle />
            )
          }
          onClose={() => setDuplicateContentResultModal(null)}
          footer={
            <button
              type="button"
              className="Button Button--ghost"
              onClick={() => setDuplicateContentResultModal(null)}
            >
              Fermer
            </button>
          }
        >
          {duplicateContentResultModal.ok ? (
            <div className="Muted">
              Contenu dupliqué vers{" "}
              <strong>{duplicateContentResultModal.targetName}</strong>.
            </div>
          ) : (
            <div className="Error" role="alert">
              {duplicateContentResultModal.error || "Erreur"}
            </div>
          )}
        </Modal>
      ) : null}

      <ContextMenu menu={ctxMenu} onClose={closeCtxMenu} />
    </div>
  );
}

function ProjectsDbStatusModal({
  siteId,
  dbConfig,
  ensureOk,
  ensureError,
  dbLoading = false,
  onEnsure,
  onClose,
}) {
  const [ensuring, setEnsuring] = useState(false);
  const [ensureLocalError, setEnsureLocalError] = useState("");
  const [test, setTest] = useState({ status: "idle", error: "" }); // idle | loading | ok | error

  useEffect(() => {
    let cancelled = false;
    if (!dbConfig || !ensureOk) {
      setTest({ status: "idle", error: "" });
      return () => {};
    }
    setTest({ status: "loading", error: "" });
    api
      .projectsDbTest({ site_id: Number(siteId), use_stored: true })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setTest({
            status: "error",
            error: res.error || "Connexion impossible",
          });
          return;
        }
        setTest({ status: "ok", error: "" });
      })
      .catch(() => {
        if (cancelled) return;
        setTest({ status: "error", error: "Connexion impossible" });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, dbConfig, ensureOk]);

  const step1 = dbLoading ? "pending" : dbConfig ? "ok" : "bad";
  const step2 =
    step1 === "ok"
      ? ensureOk
        ? "ok"
        : "bad"
      : step1 === "pending"
      ? "disabled"
      : "disabled";
  const step3 =
    step2 === "ok"
      ? test.status === "ok"
        ? "ok"
        : test.status === "error"
        ? "bad"
        : test.status === "loading"
        ? "pending"
        : "pending"
      : "disabled";

  return (
    <Modal
      title="BDD Projets"
      icon={
        step2 === "ok" && step3 === "ok" ? <IconCheck /> : <IconAlertTriangle />
      }
      onClose={onClose}
      footer={
        <button
          type="button"
          className="Button Button--ghost"
          onClick={onClose}
        >
          Fermer
        </button>
      }
    >
      <DbSteps
        steps={[
          {
            key: "db",
            title: "Base de données",
            status: step1,
            detail: dbConfig ? (
              `${dbConfig.host}:${dbConfig.port} · ${dbConfig.db_name}`
            ) : dbLoading ? (
              <Skeleton variant="line" style={{ width: 220, marginTop: 2 }} />
            ) : (
              "Aucune base configurée pour ce site."
            ),
            actions: (
              <a className="Button Button--ghost" href="#/databases">
                <span className="ButtonInline">
                  <IconGlobe /> Ouvrir Bases de données
                </span>
              </a>
            ),
          },
          {
            key: "tables",
            title: "Tables Projets",
            status: step2,
            detail:
              step2 === "ok"
                ? "OK"
                : step2 === "bad"
                ? ensureLocalError || ensureError || "Tables manquantes"
                : "—",
            actions: dbConfig ? (
              <button
                type="button"
                className="Button"
                disabled={ensuring || dbLoading}
                onClick={async () => {
                  if (ensuring) return;
                  setEnsuring(true);
                  setEnsureLocalError("");
                  const res = await onEnsure?.();
                  if (!res?.ok) {
                    setEnsureLocalError(res?.error || "Erreur");
                    setEnsuring(false);
                    return;
                  }
                  setEnsuring(false);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus />{" "}
                  {ensuring ? "Vérification…" : "Initialiser / Vérifier"}
                </span>
              </button>
            ) : null,
          },
          {
            key: "conn",
            title: "Connexion",
            status: step3,
            detail:
              step3 === "ok"
                ? "Connecté"
                : step3 === "bad"
                ? test.error || "Connexion impossible"
                : step3 === "pending"
                ? "Test en cours…"
                : "—",
          },
        ]}
      />
    </Modal>
  );
}

function SectionEditorCard({
  section,
  index,
  templates,
  projectTitle,
  siteId,
  projectId,
  assetsBaseUrl,
  styles,
  mediaById,
  projectImageAssets,
  projectsUi,
  saving,
  onSectionDragStart,
  onSectionDragEnd,
  onUpdate,
  onDuplicate,
  onDelete,
  onRequestDuplicateContainer,
  collapsed,
  onToggleCollapsed,
  onContextMenu,
}) {
  const tpl = templates.find((t) => t.key === section.template) || null;
  const TplIcon = tpl?.icon || IconType;
  const accent = tpl?.accent || "#7c3aed";
  const hint = tpl?.hint || "";
  const [blockPickerOpen, setBlockPickerOpen] = useState(false);
  const [blockPickerContainerId, setBlockPickerContainerId] = useState(null);
  const [collapsedBlocks, setCollapsedBlocks] = useState({});
  const [collapsedContainers, setCollapsedContainers] = useState({});
  const [editingContainerId, setEditingContainerId] = useState(null);
  const [containerNameDraft, setContainerNameDraft] = useState("");
  const [containerClassesModal, setContainerClassesModal] = useState(null);
  const [dragContainer, setDragContainer] = useState(null);
  const [dragBlock, setDragBlock] = useState(null);
  const [imagePicker, setImagePicker] = useState(null);
  const [listDraftById, setListDraftById] = useState({});
  const [deleteContainerConfirm, setDeleteContainerConfirm] = useState(null);
  const [bannerHeightDraft, setBannerHeightDraft] = useState("");
  const [editingSectionTitle, setEditingSectionTitle] = useState(false);
  const [sectionTitleDraft, setSectionTitleDraft] = useState("");
  const sectionTitleInputRef = useRef(null);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [blockNameDraft, setBlockNameDraft] = useState("");
  const blockNameInputRef = useRef(null);
  const [renamingStepId, setRenamingStepId] = useState(null);
  const stepTitleInputRef = useRef(null);
  const apiBaseUrl = useApiBaseUrl();
  const ui = projectsUi || normalizeProjectsPanelSettings(null);
  const formatShortcutsEnabled = Boolean(ui?.shortcuts?.format_biu);
  const showImagePreview = Boolean(ui?.show_image_preview);
  const showContrastIndicator = Boolean(ui?.show_contrast_indicator);
  const showDynamicTokens = Boolean(ui?.show_dynamic_tokens);

  useEffect(() => {
    if (collapsed) setBlockPickerOpen(false);
    if (collapsed) setImagePicker(null);
  }, [collapsed]);

  useEffect(() => {
    if (!editingSectionTitle) return;
    const raf = requestAnimationFrame(() => {
      sectionTitleInputRef.current?.focus?.();
      sectionTitleInputRef.current?.select?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [editingSectionTitle]);

  useEffect(() => {
    if (!editingBlockId) return;
    const raf = requestAnimationFrame(() => {
      blockNameInputRef.current?.focus?.();
      blockNameInputRef.current?.select?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [editingBlockId]);

  useEffect(() => {
    if (!renamingStepId) return;
    const raf = requestAnimationFrame(() => {
      stepTitleInputRef.current?.focus?.();
      stepTitleInputRef.current?.select?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [renamingStepId]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const nextBlocks = {};
    const nextContainers = {};
    function visitContainers(list) {
      const arr = Array.isArray(list) ? list : [];
      for (const c of arr) {
        if (!c || typeof c !== "object") continue;
        if (c?.id) nextContainers[String(c.id)] = true;
        for (const b of c?.blocks || []) nextBlocks[String(b.id)] = true;
        if (Array.isArray(c?.containers)) visitContainers(c.containers);
      }
    }
    if (templateKey === "steps") {
      for (const st of steps) nextBlocks[String(st.id)] = true;
      for (const b of section.blocks || []) nextBlocks[String(b.id)] = true;
      visitContainers(section.containers);
    } else {
      for (const b of section.blocks || []) nextBlocks[String(b.id)] = true;
      visitContainers(section.containers);
    }
    setCollapsedBlocks(nextBlocks);
    setCollapsedContainers(nextContainers);
    setListDraftById({});
    setDeleteContainerConfirm(null);
    setBannerHeightDraft(String((settings && settings.height_px) ?? 420));
    setEditingSectionTitle(false);
    setSectionTitleDraft("");
    setEditingContainerId(null);
    setContainerNameDraft("");
    setEditingBlockId(null);
    setBlockNameDraft("");
    setRenamingStepId(null);
    setContainerClassesModal(null);
    setDragContainer(null);
    setDragBlock(null);
    setBlockPickerContainerId(null);
  }, [section.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const templateKey = String(section.template || "").trim() || "classic";
  const settings = useMemo(() => {
    const raw = section.settings;
    if (!raw || typeof raw !== "object") return {};
    if (Array.isArray(raw)) return {};
    return raw;
  }, [section.settings]);

  const steps = useMemo(() => {
    const raw = settings.steps;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((s) => s && typeof s === "object")
      .map((s, idx) => ({
        id: String(s.id || "") || `step_${idx}`,
        title: String(s.title || ""),
        description: String(s.description || ""),
      }));
  }, [settings.steps]);

  function updateSettings(patch) {
    const next = { ...(settings || {}), ...(patch || {}) };
    onUpdate(section.id, { settings: next });
  }

  function updateSteps(nextSteps) {
    updateSettings({ steps: nextSteps });
  }

  function beginRenameSection() {
    if (collapsed) onToggleCollapsed?.();
    setEditingSectionTitle(true);
    setSectionTitleDraft(String(section.title || ""));
  }

  function commitRenameSection(nextTitle) {
    const t = String(nextTitle || "").trim();
    onUpdate(section.id, { title: t });
    setEditingSectionTitle(false);
  }

  function beginRenameBlock(block) {
    if (!block?.id) return;
    setEditingBlockId(String(block.id));
    setBlockNameDraft(String(block.name || ""));
  }

  function commitRenameBlock(blockId, containerId, nextName) {
    const t = String(nextName || "").trim();
    updateBlock(blockId, { name: t }, containerId);
    setEditingBlockId(null);
    setBlockNameDraft("");
  }

  function duplicateBlock(blockId, containerId, index) {
    const bid = String(blockId || "");
    const cid = containerId == null ? null : String(containerId);
    const list = cid
      ? findContainerById(containers, cid)?.blocks || []
      : section.blocks || [];
    const i = list.findIndex((b) => String(b?.id) === bid);
    if (i < 0) return;
    let copy = null;
    try {
      copy = JSON.parse(JSON.stringify(list[i]));
    } catch {
      copy = { ...(list[i] || {}) };
    }
    copy.id = newId("blk");
    const nextList = [...list];
    const insertAt = Number.isFinite(Number(index)) ? Number(index) + 1 : i + 1;
    nextList.splice(Math.max(0, Math.min(insertAt, nextList.length)), 0, copy);
    if (!cid) onUpdate(section.id, { blocks: nextList });
    else updateContainer(cid, { blocks: nextList });
    setCollapsedBlocks((prev) => ({
      ...(prev || {}),
      [String(copy.id)]: true,
    }));
  }

  function openSectionMenu(e) {
    if (!onContextMenu) return;
    if (!isEventFromButton(e)) return;
    onContextMenu(e, [
      {
        key: "rename",
        label: "Renommer",
        icon: <IconEdit />,
        onClick: beginRenameSection,
        hint: "Entrée",
      },
      {
        key: "duplicate",
        label: "Dupliquer",
        icon: <IconCopy />,
        onClick: () => onDuplicate?.(section.id),
      },
      { type: "sep", key: "sep" },
      {
        key: "delete",
        label: "Supprimer",
        icon: <IconTrash />,
        variant: "danger",
        onClick: () => onDelete(section.id),
      },
    ]);
  }

  function beginRenameContainer(containerId) {
    const cid = String(containerId || "");
    const c = findContainerById(containers, cid);
    if (!cid || !c) return;
    setEditingContainerId(cid);
    setContainerNameDraft(String(c.name || ""));
  }

  function openContainerClasses(containerId) {
    const cid = String(containerId || "");
    if (!cid) return;
    const c = findContainerById(containers, cid);
    if (!c) return;
    setContainerClassesModal({
      containerId: cid,
      value: String(c.extra_class || ""),
    });
  }

  function beginRenameStep(stepId) {
    const id = String(stepId || "");
    if (!id) return;
    setCollapsedBlocks((s) => ({ ...(s || {}), [String(id)]: false }));
    setRenamingStepId(id);
  }

  function openContainerMenu(e, containerId) {
    if (!onContextMenu) return;
    if (String(e?.type || "") !== "contextmenu" && !isEventFromButton(e))
      return;
    const cid = String(containerId || "");
    const path = findContainerPath(containers, cid);
    const meta = path ? getContainerMetaAtPath(containers, path) : null;
    const idx = meta ? meta.index : -1;
    const siblingsCount = meta ? meta.parentList.length : 0;
    const depth = path ? path.length : 1;
    const isRightClick = String(e?.type || "") === "contextmenu";
    const anchorRect = (() => {
      if (isRightClick) return null;
      const t =
        e?.target && e.target.nodeType === 3
          ? e.target.parentElement
          : e?.target;
      const btn =
        t?.closest?.("button") ||
        (e?.currentTarget?.tagName === "BUTTON" ? e.currentTarget : null);
      return btn?.getBoundingClientRect?.() || null;
    })();
    const baseX = anchorRect ? anchorRect.right + 8 : Number(e?.clientX) || 0;
    const baseY = anchorRect ? anchorRect.top : Number(e?.clientY) || 0;

    function openMenuAt(x, y, items) {
      onContextMenu(
        {
          preventDefault() {},
          stopPropagation() {},
          clientX: x,
          clientY: y,
          target: document.body,
        },
        items
      );
    }

    const current = findContainerById(containers, cid);
    const currentDir = normalizeFlexDirection(current?.direction, "row");
    const currentDirLabel =
      currentDir === "row"
        ? "Ligne"
        : currentDir === "row-reverse"
        ? "Ligne (inverse)"
        : currentDir === "column"
        ? "Colonne"
        : currentDir === "column-reverse"
        ? "Colonne (inverse)"
        : "Ligne";
    const directionIcon =
      currentDir === "row"
        ? IconDirRow
        : currentDir === "row-reverse"
        ? IconDirRowReverse
        : currentDir === "column"
        ? IconDirCol
        : currentDir === "column-reverse"
        ? IconDirColReverse
        : IconDirRow;
    const CurrentDirIcon = directionIcon;

    const mainItems = [
      {
        key: "up",
        label: "Monter",
        icon: <span aria-hidden="true">↑</span>,
        disabled: idx <= 0,
        onClick: () => moveContainerById(cid, -1),
      },
      {
        key: "down",
        label: "Descendre",
        icon: <span aria-hidden="true">↓</span>,
        disabled: idx < 0 || idx >= siblingsCount - 1,
        onClick: () => moveContainerById(cid, +1),
      },
      { type: "sep", key: "sep_move" },
      {
        key: "add",
        label: "Ajouter un élément",
        icon: <IconPlus />,
        variant: "good",
        onClick: () => {
          setBlockPickerContainerId(cid);
          setBlockPickerOpen(true);
        },
      },
      {
        key: "add-container",
        label: "Ajouter un sous-conteneur",
        icon: <IconLayoutGrid />,
        disabled: depth >= MAX_CONTAINER_DEPTH,
        onClick: () => addContainer(cid),
      },
      { type: "sep", key: "sep_add" },
      {
        key: "direction",
        label: `Orientation — ${currentDirLabel}`,
        icon: <CurrentDirIcon />,
        onClick: () =>
          openMenuAt(baseX + 230, baseY, [
            {
              key: "back",
              label: "Retour",
              icon: <span aria-hidden="true">←</span>,
              onClick: () => openMenuAt(baseX, baseY, mainItems),
            },
            {
              key: "row",
              label: "Ligne",
              icon: <IconDirRow />,
              onClick: () => updateContainer(cid, { direction: "row" }),
            },
            {
              key: "row-rev",
              label: "Ligne (inverse)",
              icon: <IconDirRowReverse />,
              onClick: () => updateContainer(cid, { direction: "row-reverse" }),
            },
            {
              key: "col",
              label: "Colonne",
              icon: <IconDirCol />,
              onClick: () => updateContainer(cid, { direction: "column" }),
            },
            {
              key: "col-rev",
              label: "Colonne (inverse)",
              icon: <IconDirColReverse />,
              onClick: () =>
                updateContainer(cid, { direction: "column-reverse" }),
            },
          ]),
      },
      {
        key: "rename",
        label: "Renommer",
        icon: <IconEdit />,
        onClick: () => beginRenameContainer(cid),
      },
      {
        key: "classes",
        label: "Classes CSS",
        icon: <IconTag />,
        onClick: () => openContainerClasses(cid),
      },
      {
        key: "duplicate",
        label: "Dupliquer",
        icon: <IconCopy />,
        onClick: () =>
          onRequestDuplicateContainer?.({
            fromSectionId: section.id,
            containerId: cid,
          }),
      },
      { type: "sep", key: "sep_delete" },
      {
        key: "delete",
        label: "Supprimer",
        icon: <IconTrash />,
        variant: "danger",
        onClick: () => requestDeleteContainer(cid),
      },
    ];
    if (anchorRect) openMenuAt(baseX, baseY, mainItems);
    else onContextMenu(e, mainItems);
  }

  function openBlockMenu(e, block, containerId, index) {
    if (!onContextMenu) return;
    if (!isEventFromButton(e)) return;
    const cid = containerId == null ? null : String(containerId);
    const bid = String(block?.id || "");
    onContextMenu(
      e,
      [
        {
          key: "rename",
          label: "Renommer",
          icon: <IconEdit />,
          onClick: () => beginRenameBlock(block),
        },
        {
          key: "duplicate",
          label: "Dupliquer",
          icon: <IconCopy />,
          onClick: () => duplicateBlock(bid, cid, index),
        },
        cid
          ? {
              key: "out",
              label: "Sortir du conteneur",
              icon: <IconLayoutGrid />,
              onClick: () =>
                moveBlockTo({
                  fromContainerId: cid,
                  blockId: bid,
                  toContainerId: null,
                  toIndex: rootBlocks.length,
                }),
            }
          : null,
        { type: "sep", key: "sep_delete" },
        {
          key: "delete",
          label: "Supprimer",
          icon: <IconTrash />,
          variant: "danger",
          onClick: () => deleteBlock(bid, cid),
        },
      ].filter(Boolean)
    );
  }

  function openStepMenu(e, stepId) {
    if (!onContextMenu) return;
    if (!isEventFromButton(e)) return;
    const id = String(stepId || "");
    onContextMenu(e, [
      {
        key: "rename",
        label: "Renommer",
        icon: <IconEdit />,
        onClick: () => beginRenameStep(id),
      },
      {
        key: "duplicate",
        label: "Dupliquer",
        icon: <IconCopy />,
        onClick: () => {
          const idx = steps.findIndex((x) => String(x.id) === id);
          if (idx < 0) return;
          const copy = { ...steps[idx], id: newId("step") };
          const next = [...steps];
          next.splice(idx + 1, 0, copy);
          updateSteps(next);
          setCollapsedBlocks((prev) => ({
            ...(prev || {}),
            [String(copy.id)]: true,
          }));
        },
      },
      { type: "sep", key: "sep_delete" },
      {
        key: "delete",
        label: "Supprimer",
        icon: <IconTrash />,
        variant: "danger",
        onClick: () => updateSteps(steps.filter((x) => String(x.id) !== id)),
      },
    ]);
  }

  const bgType = useMemo(() => {
    const raw = String(section.background_type || "").trim();
    if (
      raw === "none" ||
      raw === "color" ||
      raw === "image" ||
      raw === "background"
    )
      return raw;
    if ((section.background_color || "").trim()) return "color";
    if ((section.background_image_url || "").trim()) return "image";
    if ((section.background || "").trim()) return "background";
    return "none";
  }, [
    section.background_type,
    section.background_color,
    section.background_image_url,
    section.background,
  ]);

  function oneLine(text) {
    return String(text || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  const mediaIndex = mediaById instanceof Map ? mediaById : new Map();

  function resolveMediaName({ mediaId, mediaName, url }) {
    const fromProp = String(mediaName || "").trim();
    if (fromProp) return fromProp;
    const mid = Number(mediaId) || parseMediaIdFromUrl(url);
    if (Number.isFinite(mid) && mid > 0) {
      const asset = mediaIndex.get(String(mid)) || null;
      const n = mediaAssetDisplayName(asset);
      if (n) return n;
    }
    return String(url || "").trim();
  }

  function resolveMediaSrc({ mediaId, url }) {
    const mid = Number(mediaId) || parseMediaIdFromUrl(url);
    if (Number.isFinite(mid) && mid > 0) {
      const asset = mediaIndex.get(String(mid)) || null;
      const demoSrc = String(asset?.demo_src || "").trim();
      if (demoSrc) return demoSrc;
      return mediaInlineUrl(apiBaseUrl, Number(siteId), mid);
    }
    return resolveAssetSourceUrl(url, assetsBaseUrl);
  }

  function blockSummary(block) {
    if (!block) return "—";
    const type = String(block.type || "");
    if (type === "text") {
      const t = oneLine(block.text);
      return t || "Texte vide";
    }
    if (type === "list") {
      const items = Array.isArray(block.items)
        ? block.items.map(oneLine).filter(Boolean)
        : [];
      const first = items[0] || "";
      const rest = items.length > 1 ? ` +${items.length - 1}` : "";
      return first ? `• ${first}${rest}` : "Liste";
    }
    if (type === "image") {
      const url = oneLine(
        resolveMediaName({
          mediaId: block.media_id,
          mediaName: block.media_name,
          url: block.url,
        })
      );
      const alt = oneLine(block.alt);
      if (url && alt) return `${url} — alt: ${alt}`;
      return url || alt || "Image";
    }
    if (type === "video") {
      const url = oneLine(block.url);
      return url || "Vidéo";
    }
    if (type === "embed") {
      const url = oneLine(block.url);
      return url || "Média";
    }
    if (type === "table") {
      const caption = oneLine(block.caption);
      const csvLine = oneLine(String(block.csv || "").split(/\r?\n/)[0] || "");
      return caption || csvLine || "Tableau";
    }
    return oneLine(type) || "Bloc";
  }

  const effectiveBgHex = useMemo(() => {
    if (bgType === "color") {
      const h = normalizeHex(section.background_color);
      if (h) return h;
    }
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(
        "--bg"
      );
      const h = normalizeHex(v);
      return h || "#0b0f14";
    } catch {
      return "#0b0f14";
    }
  }, [bgType, section.background_color]);

  function commitBannerHeight(nextRaw) {
    const raw = String(nextRaw ?? "").trim();
    if (!raw) {
      setBannerHeightDraft(String(settings.height_px ?? 420));
      return;
    }
    const n = Number(raw);
    const v =
      Number.isFinite(n) && n > 0
        ? Math.round(n)
        : Number(settings.height_px ?? 420);
    updateSettings({ height_px: v });
    setBannerHeightDraft(String(v));
  }

  const containers = useMemo(() => {
    return normalizeContainerList(section.containers);
  }, [section.containers]);

  const allContainerIds = useMemo(() => {
    const ids = [];
    function visit(list) {
      const arr = Array.isArray(list) ? list : [];
      for (const c of arr) {
        const id = String(c?.id || "");
        if (id) ids.push(id);
        if (Array.isArray(c?.containers)) visit(c.containers);
      }
    }
    visit(containers);
    return ids;
  }, [containers]);

  const classicCounts = useMemo(() => {
    const rootCount = (section.blocks || []).length;
    const containerCount = countBlocksDeepInContainerList(containers);
    const total = rootCount + containerCount;
    const firstBlock =
      (section.blocks || [])[0] ||
      firstBlockInContainerList(containers) ||
      null;
    return { rootCount, containerCount, total, firstBlock };
  }, [section.blocks, containers]);

  const rootBlocks = section.blocks || [];

  function updateContainers(nextContainers) {
    onUpdate(section.id, { containers: nextContainers });
  }

  function updateContainer(containerId, patch) {
    const id = String(containerId || "");
    if (!id) return;
    function walk(list) {
      const arr = Array.isArray(list) ? list : [];
      let changed = false;
      const next = arr.map((c) => {
        if (!c || typeof c !== "object") return c;
        if (String(c.id || "") === id) {
          changed = true;
          return { ...c, ...(patch || {}) };
        }
        const children = Array.isArray(c.containers) ? c.containers : [];
        const nextChildren = walk(children);
        if (nextChildren !== children) {
          changed = true;
          return { ...c, containers: nextChildren };
        }
        return c;
      });
      return changed ? next : arr;
    }
    updateContainers(walk(containers));
  }

  function addContainer(parentContainerId = null) {
    const id = newId("ctr");
    const nextContainer = {
      id,
      name: "Conteneur",
      direction: "row",
      extra_class: "",
      slot: 0,
      slot_order: 0,
      blocks: [],
      containers: [],
    };
    const parentId = parentContainerId == null ? "" : String(parentContainerId);
    if (!parentId) {
      nextContainer.slot = 0;
      nextContainer.slot_order = containers.length;
      updateContainers([...containers, nextContainer]);
    } else {
      const path = findContainerPath(containers, parentId);
      if (!path) return;
      if (path.length >= MAX_CONTAINER_DEPTH) return;
      const next = cloneContainersForMutation(containers);
      const parent = findContainerById(next, parentId);
      if (!parent) return;
      parent.containers = Array.isArray(parent.containers)
        ? parent.containers
        : [];
      const parentBlocksLen = Array.isArray(parent.blocks)
        ? parent.blocks.length
        : 0;
      nextContainer.slot = parentBlocksLen;
      nextContainer.slot_order = parent.containers.length;
      parent.containers.push(nextContainer);
      updateContainers(next);
      setCollapsedContainers((s) => ({
        ...(s || {}),
        [String(parentId)]: false,
      }));
    }
    setCollapsedContainers((prev) => ({ ...(prev || {}), [String(id)]: true }));
  }

  function moveContainerById(containerId, delta) {
    const id = String(containerId || "");
    if (!id) return;
    const d = Number(delta);
    if (!Number.isFinite(d) || !d) return;
    const path = findContainerPath(containers, id);
    if (!path) return;
    const next = cloneContainersForMutation(containers);
    const meta = getContainerMetaAtPath(next, path);
    if (!meta) return;
    const to = meta.index + d;
    if (to < 0 || to >= meta.parentList.length) return;
    const [item] = meta.parentList.splice(meta.index, 1);
    meta.parentList.splice(to, 0, item);
    if (path.length === 1) {
      for (let i = 0; i < next.length; i += 1) {
        const c = next[i];
        if (!c || typeof c !== "object") continue;
        c.slot = 0;
        c.slot_order = i;
      }
    } else {
      const parentPath = path.slice(0, -1);
      const parentMeta = getContainerMetaAtPath(next, parentPath);
      const parentBlocksLen = Array.isArray(parentMeta?.container?.blocks)
        ? parentMeta.container.blocks.length
        : 0;
      reindexContainerSlotsInList(meta.parentList, {
        blocksLen: parentBlocksLen,
      });
    }
    updateContainers(next);
  }

  function deleteContainerMoveContents(containerId) {
    const id = String(containerId || "");
    if (!id) return;
    const path = findContainerPath(containers, id);
    if (!path) return;
    const next = cloneContainersForMutation(containers);
    const meta = getContainerMetaAtPath(next, path);
    if (!meta?.container) return;
    const movedBlocks = Array.isArray(meta.container.blocks)
      ? meta.container.blocks
      : [];
    const movedChildren = Array.isArray(meta.container.containers)
      ? meta.container.containers
      : [];

    const deletedSlot = normalizeContainerSlot(meta.container.slot, 0);

    if (path.length === 1) {
      for (let i = 0; i < movedChildren.length; i += 1) {
        const child = movedChildren[i];
        if (!child || typeof child !== "object") continue;
        child.slot = 0;
        child.slot_order = i;
      }
    } else {
      const parentPath = path.slice(0, -1);
      const parentMeta = getContainerMetaAtPath(next, parentPath);
      const parentBlocksLen = Array.isArray(parentMeta?.container?.blocks)
        ? parentMeta.container.blocks.length
        : 0;
      for (let i = 0; i < movedChildren.length; i += 1) {
        const child = movedChildren[i];
        if (!child || typeof child !== "object") continue;
        child.slot = Math.max(0, Math.min(parentBlocksLen, deletedSlot));
        child.slot_order = i;
      }
    }

    meta.parentList.splice(meta.index, 1, ...movedChildren);
    if (path.length === 1) {
      for (let i = 0; i < next.length; i += 1) {
        const c = next[i];
        if (!c || typeof c !== "object") continue;
        c.slot = 0;
        c.slot_order = i;
      }
    } else {
      const parentPath = path.slice(0, -1);
      const parentMeta = getContainerMetaAtPath(next, parentPath);
      const parentBlocksLen = Array.isArray(parentMeta?.container?.blocks)
        ? parentMeta.container.blocks.length
        : 0;
      reindexContainerSlotsInList(meta.parentList, {
        blocksLen: parentBlocksLen,
      });
    }

    if (path.length === 1) {
      const nextRootBlocks = [...(section.blocks || []), ...movedBlocks];
      onUpdate(section.id, { containers: next, blocks: nextRootBlocks });
      return;
    }

    const parentPath = path.slice(0, -1);
    const parentMeta = getContainerMetaAtPath(next, parentPath);
    if (parentMeta?.container) {
      parentMeta.container.blocks = [
        ...(Array.isArray(parentMeta.container.blocks)
          ? parentMeta.container.blocks
          : []),
        ...movedBlocks,
      ];
    }
    onUpdate(section.id, { containers: next });
  }

  function deleteContainerAndContents(containerId) {
    const id = String(containerId || "");
    if (!id) return;
    const path = findContainerPath(containers, id);
    if (!path) return;
    const next = cloneContainersForMutation(containers);
    const meta = getContainerMetaAtPath(next, path);
    if (!meta) return;
    meta.parentList.splice(meta.index, 1);
    onUpdate(section.id, { containers: next });
  }

  function requestDeleteContainer(containerId) {
    const id = String(containerId || "");
    if (!id) return;
    const c = findContainerById(containers, id);
    const blockCount = countBlocksDeepInContainer(c);
    const containerCount = countContainersDeepInContainer(c);
    if (blockCount <= 0 && containerCount <= 0) {
      deleteContainerAndContents(id);
      return;
    }
    setDeleteContainerConfirm({ containerId: id, blockCount, containerCount });
  }

  function updateBlock(blockId, patch, containerId = null) {
    const bid = String(blockId || "");
    const cid = containerId == null ? "" : String(containerId);
    if (!cid) {
      onUpdate(section.id, {
        blocks: (section.blocks || []).map((b) =>
          String(b.id) === bid ? { ...b, ...patch } : b
        ),
      });
      return;
    }
    updateContainer(cid, {
      blocks: (findContainerById(containers, cid)?.blocks || []).map((b) =>
        String(b.id) === bid ? { ...b, ...patch } : b
      ),
    });
  }

  function deleteBlock(blockId, containerId = null) {
    const bid = String(blockId || "");
    const cid = containerId == null ? "" : String(containerId);
    if (!cid) {
      onUpdate(section.id, {
        blocks: (section.blocks || []).filter((b) => String(b.id) !== bid),
      });
      return;
    }
    updateContainer(cid, {
      blocks: (findContainerById(containers, cid)?.blocks || []).filter(
        (b) => String(b.id) !== bid
      ),
    });
  }

  function insertToken(blockId, token, containerId = null) {
    const bid = String(blockId || "");
    const cid = containerId == null ? "" : String(containerId);
    const list = !cid
      ? section.blocks || []
      : findContainerById(containers, cid)?.blocks || [];
    const b = list.find((x) => String(x.id) === bid);
    if (!b) return;
    updateBlock(
      blockId,
      { text: `${String(b.text || "")}${String(token || "")}` },
      containerId
    );
  }

  function addBlock(type, containerId = null) {
    const t = String(type || "");
    const id = newId("blk");
    const base =
      t === "text"
        ? {
            id,
            type: "text",
            tag: "p",
            align: "left",
            color: "",
            text: "",
            style_id: null,
            style_class: "",
            extra_class: "",
          }
        : t === "list"
        ? {
            id,
            type: "list",
            tag: "ul",
            align: "left",
            color: "",
            items: [""],
            style_id: null,
            style_class: "",
            extra_class: "",
          }
        : t === "image"
        ? { id, type: "image", url: "", alt: "", caption: "" }
        : t === "video"
        ? { id, type: "video", url: "", caption: "" }
        : t === "embed"
        ? { id, type: "embed", url: "", caption: "" }
        : t === "table"
        ? { id, type: "table", caption: "", csv: "" }
        : null;
    if (!base) return;

    const cid = containerId == null ? "" : String(containerId);
    if (!cid) {
      const next = [...(section.blocks || []), base];
      onUpdate(section.id, { blocks: next });
      setCollapsedBlocks((prev) => ({ ...(prev || {}), [String(id)]: true }));
      return;
    }
    const existing = findContainerById(containers, cid);
    const nextBlocks = [...(existing?.blocks || []), base];
    updateContainer(cid, { blocks: nextBlocks });
    setCollapsedBlocks((prev) => ({ ...(prev || {}), [String(id)]: true }));
  }

  function parseTable(csv) {
    const raw = String(csv || "").trim();
    if (!raw) return [];
    const lines = raw
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return [];
    const delimiter =
      lines.some((l) => l.includes(";")) && !lines.some((l) => l.includes(","))
        ? ";"
        : ",";
    return lines.slice(0, 20).map((l) =>
      l
        .split(delimiter)
        .map((c) => c.trim())
        .slice(0, 12)
    );
  }

  function maxDepthOfContainer(container) {
    const c = container && typeof container === "object" ? container : null;
    if (!c) return 0;
    const children = Array.isArray(c.containers) ? c.containers : [];
    if (!children.length) return 1;
    return (
      1 + Math.max(...children.map((child) => maxDepthOfContainer(child) || 1))
    );
  }

  function containerParentId(containerId) {
    const cid = String(containerId || "");
    if (!cid) return null;
    const path = findContainerPath(containers, cid);
    if (!path || path.length <= 1) return null;
    const parentPath = path.slice(0, -1);
    const meta = getContainerMetaAtPath(containers, parentPath);
    const parent = meta?.container || null;
    return parent?.id ? String(parent.id) : null;
  }

  function beginDragContainer(e, containerId) {
    const cid = String(containerId || "");
    if (!cid) return;
    const fromParentId = containerParentId(cid);
    setDragContainer({ from_parent_id: fromParentId, container_id: cid });
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(
        "application/x-omniscope-container",
        JSON.stringify({ from_parent_id: fromParentId, container_id: cid })
      );
      e.dataTransfer.setData("text/plain", cid);
    } catch {
      // ignore
    }
  }

  function resolveDragContainerData(e) {
    if (dragContainer?.container_id) return dragContainer;
    try {
      const raw = e?.dataTransfer?.getData?.(
        "application/x-omniscope-container"
      );
      if (raw) return JSON.parse(raw);
    } catch {
      // ignore
    }
    return null;
  }

  function reindexContainerSlotsInList(list, { blocksLen = null } = {}) {
    const arr = Array.isArray(list) ? list : [];
    const maxSlot = Number.isFinite(Number(blocksLen))
      ? Number(blocksLen)
      : null;

    const enriched = arr.map((c, i) => ({
      c,
      i,
      slot:
        maxSlot == null
          ? normalizeContainerSlot(c?.slot, 0)
          : Math.max(
              0,
              Math.min(maxSlot, normalizeContainerSlot(c?.slot, maxSlot))
            ),
    }));

    enriched.sort((a, b) => {
      if (a.slot !== b.slot) return a.slot - b.slot;
      return a.i - b.i;
    });

    for (let i = 0; i < enriched.length; i += 1) arr[i] = enriched[i].c;

    const seen = new Map();
    for (const c of arr) {
      if (!c || typeof c !== "object") continue;
      const s =
        maxSlot == null
          ? normalizeContainerSlot(c.slot, 0)
          : Math.max(
              0,
              Math.min(maxSlot, normalizeContainerSlot(c.slot, maxSlot))
            );
      c.slot = s;
      const n = seen.get(s) || 0;
      c.slot_order = n;
      seen.set(s, n + 1);
    }
  }

  function moveContainerTo({ containerId, toParentId, toIndex, toSlot }) {
    const cid = String(containerId || "");
    if (!cid) return;
    const destParentId = toParentId == null ? null : String(toParentId || "");
    const nextContainers = cloneContainersForMutation(containers);

    const curPath = findContainerPath(nextContainers, cid);
    if (!curPath) return;

    const destPath = destParentId
      ? findContainerPath(nextContainers, destParentId)
      : [];
    if (destParentId && (!destPath || !destPath.length)) return;

    if (destParentId) {
      if (destParentId === cid) return;
      const isDestInsideDragged =
        destPath.length >= curPath.length &&
        curPath.every((v, i) => destPath[i] === v);
      if (isDestInsideDragged) return;
    }

    const curParentPath = curPath.slice(0, -1);
    const curParentId =
      curParentPath.length > 0
        ? String(
            getContainerMetaAtPath(nextContainers, curParentPath)?.container
              ?.id || ""
          )
        : "";

    const draggedMeta = getContainerMetaAtPath(nextContainers, curPath);
    const dragged = draggedMeta?.container || null;
    const subtreeDepth = maxDepthOfContainer(dragged);

    const destDepth = destParentId ? destPath.length + 1 : 1;
    if (destDepth + subtreeDepth - 1 > MAX_CONTAINER_DEPTH) return;

    const fromList = draggedMeta?.parentList || null;
    const fromIndex = Number(draggedMeta?.index);
    if (!fromList || !Number.isFinite(fromIndex) || fromIndex < 0) return;
    const [item] = fromList.splice(fromIndex, 1);
    if (!item) return;

    let toList = null;
    let destBlocksLen = null;
    if (!destParentId) {
      toList = nextContainers;
    } else {
      const dest = findContainerById(nextContainers, destParentId);
      if (!dest) return;
      dest.containers = Array.isArray(dest.containers) ? dest.containers : [];
      destBlocksLen = Array.isArray(dest.blocks) ? dest.blocks.length : 0;
      toList = dest.containers;
    }

    let idx = Number.isFinite(Number(toIndex))
      ? Number(toIndex)
      : toList.length;
    if (idx < 0) idx = 0;
    if (idx > toList.length) idx = toList.length;
    if (
      String(curParentId || "") === String(destParentId || "") &&
      fromIndex < idx
    )
      idx -= 1;
    if (idx < 0) idx = 0;
    toList.splice(idx, 0, item);

    if (destParentId) {
      const requestedSlot = Number.isFinite(Number(toSlot))
        ? Math.max(
            0,
            Math.min(Number(destBlocksLen || 0), Math.floor(Number(toSlot)))
          )
        : null;
      const isSameParent =
        String(curParentId || "") === String(destParentId || "");
      const fallbackSlot = isSameParent
        ? normalizeContainerSlot(item.slot, Number(destBlocksLen || 0))
        : Number(destBlocksLen || 0);
      item.slot = requestedSlot == null ? fallbackSlot : requestedSlot;
      reindexContainerSlotsInList(toList, { blocksLen: destBlocksLen });
    } else {
      for (let i = 0; i < toList.length; i += 1) {
        const c = toList[i];
        if (!c || typeof c !== "object") continue;
        c.slot = 0;
        c.slot_order = i;
      }
    }

    updateContainers(nextContainers);
  }

  function handleContainerDrop(e, toParentId, options) {
    const data = resolveDragContainerData(e);
    const cid = String(data?.container_id || "");
    if (!cid) return false;
    e.preventDefault();
    e.stopPropagation();
    const opts =
      options && typeof options === "object" ? options : { toIndex: options };
    moveContainerTo({
      containerId: cid,
      toParentId: toParentId ?? null,
      toIndex: opts?.toIndex,
      toSlot: opts?.toSlot,
    });
    setDragContainer(null);
    return true;
  }

  function beginDragBlock(e, containerId, blockId) {
    const cid = containerId == null ? "" : String(containerId);
    const bid = String(blockId || "");
    if (!bid) return;
    setDragBlock({ from_container_id: cid || null, block_id: bid });
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(
        "application/json",
        JSON.stringify({ from_container_id: cid || null, block_id: bid })
      );
      e.dataTransfer.setData("text/plain", bid);
    } catch {
      // ignore
    }
  }

  function resolveDragData(e) {
    if (dragBlock?.block_id) return dragBlock;
    try {
      const raw = e?.dataTransfer?.getData?.("application/json");
      if (raw) return JSON.parse(raw);
    } catch {
      // ignore
    }
    return null;
  }

  function moveBlockTo({ fromContainerId, blockId, toContainerId, toIndex }) {
    const fromCid = fromContainerId == null ? null : String(fromContainerId);
    const toCid = toContainerId == null ? null : String(toContainerId);
    const bid = String(blockId || "");
    if (!bid) return;

    const root = [...(section.blocks || [])];
    const nextContainers = cloneContainersForMutation(containers);

    function getList(cid) {
      if (!cid) return root;
      const c = findContainerById(nextContainers, cid);
      return c ? c.blocks : null;
    }

    const fromList = getList(fromCid);
    const toList = getList(toCid);
    if (!fromList || !toList) return;

    const fromIndex = fromList.findIndex((b) => String(b?.id) === bid);
    if (fromIndex < 0) return;

    const [item] = fromList.splice(fromIndex, 1);
    let idx = Number.isFinite(Number(toIndex))
      ? Number(toIndex)
      : toList.length;
    if (idx < 0) idx = 0;
    if (idx > toList.length) idx = toList.length;
    if (String(fromCid || "") === String(toCid || "") && fromIndex < idx)
      idx -= 1;
    if (idx < 0) idx = 0;
    toList.splice(idx, 0, item);

    onUpdate(section.id, { blocks: root, containers: nextContainers });
  }

  function handleDrop(e, toContainerId, toIndex) {
    e.preventDefault();
    e.stopPropagation();
    const data = resolveDragData(e);
    if (!data?.block_id) return;
    moveBlockTo({
      fromContainerId: data.from_container_id ?? null,
      blockId: data.block_id,
      toContainerId,
      toIndex,
    });
    setDragBlock(null);
  }

  function renderBlockEditor(
    b,
    { containerId = null, index = 0, outsideSteps = false } = {}
  ) {
    if (!b) return null;
    const cid = containerId == null ? null : String(containerId);
    const type = String(b.type || "");
    const isText = type === "text";
    const isList = type === "list";
    const tag = isText
      ? String(b.tag || "p")
      : isList
      ? String(b.tag || "ul")
      : "";
    const align = isText || isList ? String(b.align || "left") : "left";
    const color = isText || isList ? String(b.color || "") : "";
    const styleId =
      isText || isList
        ? b.style_id == null || b.style_id === ""
          ? ""
          : String(b.style_id)
        : "";
    const styleClass = isText || isList ? String(b.style_class || "") : "";
    const styleExtra = isText || isList ? String(b.extra_class || "") : "";
    const extraClass =
      (isText || isList) && !styleExtra && !styleId ? styleClass : styleExtra;
    const iconFor =
      type === "image"
        ? IconImage
        : type === "video"
        ? IconVideo
        : type === "embed"
        ? IconLink
        : type === "table"
        ? IconTable
        : type === "list"
        ? IconAlignLeft
        : IconType;
    const labelFor =
      type === "image"
        ? "Image"
        : type === "video"
        ? "Vidéo"
        : type === "embed"
        ? "Média"
        : type === "table"
        ? "Tableau"
        : type === "list"
        ? "Liste"
        : "Texte";
    const BlockIcon = iconFor;
    const selectedStyle =
      (isText || isList) && styleId
        ? (styles || []).find((s) => String(s.id) === String(styleId))
        : null;
    const isBlockCollapsed = Boolean(collapsedBlocks?.[String(b.id)]);
    const summary = blockSummary(b);
    const blockTitle = String(b.name || "").trim();
    const styleBadgeLabel =
      selectedStyle?.name || styleClass || selectedStyle?.class_name || "";

    return (
      <div
        key={b.id}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDrop(e, cid, index)}
        style={{ minWidth: 0 }}
      >
        <div className={`BlockCard ${outsideSteps ? "BlockCard--dashed" : ""}`}>
          <div
            className="BlockHead"
            onContextMenu={(e) => openBlockMenu(e, b, cid, index)}
          >
            <div className="BlockHeadLeft">
              <button
                type="button"
                className="DragHandle"
                draggable
                onDragStart={(e) => beginDragBlock(e, cid, b.id)}
                onDragEnd={() => setDragBlock(null)}
                title="Déplacer"
                aria-label="Déplacer"
              >
                <span aria-hidden="true">⋮⋮</span>
              </button>
              <button
                type="button"
                className="CollapseToggle CollapseToggle--sm"
                onClick={() =>
                  setCollapsedBlocks((s) => ({
                    ...(s || {}),
                    [String(b.id)]: !Boolean(s?.[String(b.id)]),
                  }))
                }
                aria-label={isBlockCollapsed ? "Déplier" : "Replier"}
              >
                {isBlockCollapsed ? <IconChevronRight /> : <IconChevronDown />}
              </button>
              <div className="BlockHeadMeta">
                <div className="BlockMetaLine">
                  {editingBlockId === String(b.id) ? (
                    <input
                      ref={blockNameInputRef}
                      className="Input InlineRenameInput InlineRenameInput--inline"
                      value={blockNameDraft}
                      onChange={(e) => setBlockNameDraft(e.target.value)}
                      disabled={saving}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitRenameBlock(b.id, cid, blockNameDraft);
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setEditingBlockId(null);
                          setBlockNameDraft("");
                        }
                      }}
                      onBlur={() =>
                        commitRenameBlock(b.id, cid, blockNameDraft)
                      }
                      placeholder="Nom…"
                    />
                  ) : (
                    <button
                      type="button"
                      className={`InlineRename InlineRename--inline ${
                        blockTitle ? "" : "InlineRename--empty"
                      }`}
                      onClick={() => beginRenameBlock(b)}
                      disabled={saving}
                      title="Renommer"
                    >
                      <span className="OneLine" style={{ maxWidth: 220 }}>
                        {blockTitle || "Sans titre"}
                      </span>
                    </button>
                  )}
                  <span className="Muted" style={{ fontSize: 12 }}>
                    <span className="ButtonInline">
                      <BlockIcon /> <strong>{labelFor}</strong>
                    </span>
                  </span>
                  {isText || isList ? (
                    <span
                      className="Muted"
                      style={{ fontSize: 12 }}
                      title="Balise HTML"
                    >
                      &lt;{tag}&gt;
                    </span>
                  ) : null}
                  {(isText || isList) && styleBadgeLabel ? (
                    <span
                      className="Muted"
                      style={{ fontSize: 12 }}
                      title="Style"
                    >
                      <span className="ButtonInline">
                        <IconLayers /> <strong>{String(styleBadgeLabel)}</strong>
                      </span>
                    </span>
                  ) : null}
                </div>
                {isBlockCollapsed ? (
                  <div
                    className="Muted OneLine"
                    style={{ fontSize: 13, marginTop: 6 }}
                  >
                    {summary}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="Badges">
              <button
                type="button"
                className="Button Button--ghost Button--danger Button--icon"
                disabled={saving}
                onClick={() => deleteBlock(b.id, cid)}
                title="Supprimer l’élément"
                aria-label="Supprimer l’élément"
              >
                <IconTrash />
              </button>
            </div>
          </div>

          {isBlockCollapsed ? null : b.type === "text" ? (
            <>
              <div className="FormRow3">
                <div className="Field">
                  <label className="Label">Balise</label>
                  <select
                    className="Select"
                    value={tag}
                    onChange={(e) =>
                      updateBlock(b.id, { tag: e.target.value }, cid)
                    }
                    disabled={saving}
                  >
                    <option value="p">p</option>
                    <option value="span">span</option>
                    <option value="h2">h2</option>
                    <option value="h3">h3</option>
                    <option value="h4">h4</option>
                  </select>
                </div>
                <div className="Field">
                  <label className="Label">Alignement</label>
                  <div
                    className="Seg"
                    role="group"
                    aria-label="Alignement du texte"
                  >
                    <button
                      type="button"
                      className={`SegButton ${
                        align === "left" ? "is-active" : ""
                      }`}
                      onClick={() => updateBlock(b.id, { align: "left" }, cid)}
                      disabled={saving}
                    >
                      <IconAlignLeft />
                    </button>
                    <button
                      type="button"
                      className={`SegButton ${
                        align === "center" ? "is-active" : ""
                      }`}
                      onClick={() =>
                        updateBlock(b.id, { align: "center" }, cid)
                      }
                      disabled={saving}
                    >
                      <IconAlignCenter />
                    </button>
                    <button
                      type="button"
                      className={`SegButton ${
                        align === "right" ? "is-active" : ""
                      }`}
                      onClick={() => updateBlock(b.id, { align: "right" }, cid)}
                      disabled={saving}
                    >
                      <IconAlignRight />
                    </button>
                  </div>
                </div>
                <div className="Field">
                  <label className="Label">Couleur (optionnel)</label>
                  <div className="InputRow">
                    <input
                      className="Input"
                      value={color}
                      onChange={(e) =>
                        updateBlock(b.id, { color: e.target.value }, cid)
                      }
                      disabled={saving}
                    />
                    {showContrastIndicator ? (
                      <ContrastIndicator
                        textColor={color}
                        backgroundColor={effectiveBgHex}
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="FormGrid" style={{ marginTop: 10 }}>
                <div className="Field">
                  <label className="Label">Style</label>
                  <select
                    className="Select"
                    value={selectedStyle ? String(selectedStyle.id) : ""}
                    onChange={(e) => {
                      const nextId = e.target.value
                        ? Number(e.target.value)
                        : null;
                      const next =
                        (styles || []).find(
                          (s) => Number(s.id) === Number(nextId)
                        ) || null;
                      const base = next ? String(next.class_name || "") : "";
                      const combined = `${base} ${extraClass || ""}`.trim();
                      updateBlock(
                        b.id,
                        {
                          style_id: next ? Number(next.id) : null,
                          style_class: combined,
                          extra_class: extraClass || "",
                        },
                        cid
                      );
                    }}
                    disabled={saving}
                  >
                    <option value="">Aucun</option>
                    {(styles || []).map((st) => (
                      <option key={st.id} value={String(st.id)}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="Field">
                  <label className="Label">Classes CSS (en plus)</label>
                  <input
                    className="Input"
                    value={extraClass || ""}
                    onChange={(e) => {
                      const nextExtra = e.target.value;
                      const base = selectedStyle
                        ? String(selectedStyle.class_name || "")
                        : "";
                      const combined = `${base} ${nextExtra}`.trim();
                      updateBlock(
                        b.id,
                        { extra_class: nextExtra, style_class: combined },
                        cid
                      );
                    }}
                    disabled={saving}
                    placeholder="ex: text-sm text-muted"
                  />
                </div>
              </div>

              <div className="Field" style={{ marginTop: 14 }}>
                <label className="Label">Texte</label>
                <textarea
                  className="Textarea"
                  rows={6}
                  value={b.text || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { text: e.target.value }, cid)
                  }
                  onKeyDown={(e) =>
                    handleTextareaFormatShortcuts(
                      e,
                      (nextValue) =>
                        updateBlock(b.id, { text: nextValue }, cid),
                      { enabled: formatShortcutsEnabled }
                    )
                  }
                  disabled={saving}
                />
              </div>

              {showDynamicTokens ? (
                <div
                  className="Badges"
                  style={{ justifyContent: "flex-start", marginTop: 10 }}
                >
                  <button
                    type="button"
                    className="TokenButton"
                    disabled={saving}
                    onClick={() => insertToken(b.id, "{{section_number}}", cid)}
                    title="Insérer le token"
                  >
                    <code>{"{{section_number}}"}</code>
                  </button>
                  <button
                    type="button"
                    className="TokenButton"
                    disabled={saving}
                    onClick={() => insertToken(b.id, "{{project_title}}", cid)}
                    title="Insérer le token"
                  >
                    <code>{"{{project_title}}"}</code>
                  </button>
                </div>
              ) : null}
            </>
          ) : type === "list" ? (
            <>
              <div className="FormRow3">
                <div className="Field">
                  <label className="Label">Balise</label>
                  <select
                    className="Select"
                    value={tag}
                    onChange={(e) =>
                      updateBlock(b.id, { tag: e.target.value }, cid)
                    }
                    disabled={saving}
                  >
                    <option value="ul">ul</option>
                    <option value="ol">ol</option>
                  </select>
                </div>
                <div className="Field">
                  <label className="Label">Alignement</label>
                  <div
                    className="Seg"
                    role="group"
                    aria-label="Alignement de la liste"
                  >
                    <button
                      type="button"
                      className={`SegButton ${
                        align === "left" ? "is-active" : ""
                      }`}
                      onClick={() => updateBlock(b.id, { align: "left" }, cid)}
                      disabled={saving}
                    >
                      <IconAlignLeft />
                    </button>
                    <button
                      type="button"
                      className={`SegButton ${
                        align === "center" ? "is-active" : ""
                      }`}
                      onClick={() =>
                        updateBlock(b.id, { align: "center" }, cid)
                      }
                      disabled={saving}
                    >
                      <IconAlignCenter />
                    </button>
                    <button
                      type="button"
                      className={`SegButton ${
                        align === "right" ? "is-active" : ""
                      }`}
                      onClick={() => updateBlock(b.id, { align: "right" }, cid)}
                      disabled={saving}
                    >
                      <IconAlignRight />
                    </button>
                  </div>
                </div>
                <div className="Field">
                  <label className="Label">Couleur (optionnel)</label>
                  <div className="InputRow">
                    <input
                      className="Input"
                      value={color}
                      onChange={(e) =>
                        updateBlock(b.id, { color: e.target.value }, cid)
                      }
                      disabled={saving}
                    />
                    {showContrastIndicator ? (
                      <ContrastIndicator
                        textColor={color}
                        backgroundColor={effectiveBgHex}
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="FormGrid" style={{ marginTop: 10 }}>
                <div className="Field">
                  <label className="Label">Style</label>
                  <select
                    className="Select"
                    value={selectedStyle ? String(selectedStyle.id) : ""}
                    onChange={(e) => {
                      const nextId = e.target.value
                        ? Number(e.target.value)
                        : null;
                      const next =
                        (styles || []).find(
                          (s) => Number(s.id) === Number(nextId)
                        ) || null;
                      const base = next ? String(next.class_name || "") : "";
                      const combined = `${base} ${extraClass || ""}`.trim();
                      updateBlock(
                        b.id,
                        {
                          style_id: next ? Number(next.id) : null,
                          style_class: combined,
                          extra_class: extraClass || "",
                        },
                        cid
                      );
                    }}
                    disabled={saving}
                  >
                    <option value="">Aucun</option>
                    {(styles || []).map((st) => (
                      <option key={st.id} value={String(st.id)}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="Field">
                  <label className="Label">Classes CSS (en plus)</label>
                  <input
                    className="Input"
                    value={extraClass || ""}
                    onChange={(e) => {
                      const nextExtra = e.target.value;
                      const base = selectedStyle
                        ? String(selectedStyle.class_name || "")
                        : "";
                      const combined = `${base} ${nextExtra}`.trim();
                      updateBlock(
                        b.id,
                        { extra_class: nextExtra, style_class: combined },
                        cid
                      );
                    }}
                    disabled={saving}
                    placeholder="ex: list-disc pl-5"
                  />
                </div>
              </div>

              <div className="Field" style={{ marginTop: 14 }}>
                <label className="Label">Éléments (1 par ligne)</label>
                <textarea
                  className="Textarea"
                  rows={8}
                  value={
                    listDraftById?.[String(b.id)] ??
                    (Array.isArray(b.items) ? b.items.join("\n") : "")
                  }
                  onChange={(e) => {
                    const next = String(e.target.value || "");
                    setListDraftById((s) => ({
                      ...(s || {}),
                      [String(b.id)]: next,
                    }));
                    const lines = next.split(/\r?\n/);
                    const items = lines
                      .map((l) => l.trim())
                      .filter((l) => l !== "");
                    updateBlock(b.id, { items }, cid);
                  }}
                  onKeyDown={(e) =>
                    handleTextareaFormatShortcuts(
                      e,
                      (nextValue) => {
                        setListDraftById((s) => ({
                          ...(s || {}),
                          [String(b.id)]: nextValue,
                        }));
                        const lines = String(nextValue || "").split(/\r?\n/);
                        const items = lines
                          .map((l) => l.trim())
                          .filter((l) => l !== "");
                        updateBlock(b.id, { items }, cid);
                      },
                      { enabled: formatShortcutsEnabled }
                    )
                  }
                  onBlur={() => {
                    setListDraftById((s) => {
                      if (
                        !s ||
                        !Object.prototype.hasOwnProperty.call(s, String(b.id))
                      )
                        return s;
                      const next = { ...(s || {}) };
                      delete next[String(b.id)];
                      return next;
                    });
                  }}
                  disabled={saving}
                  placeholder={"Ex:\n- Point 1\n- Point 2"}
                />
              </div>
            </>
          ) : type === "image" ? (
            <>
              <div className="FormGrid">
                <div className="Field">
                  <label className="Label">Image</label>
                  <div className="InputRow">
                    <input
                      className="Input"
                      value={String(b.url || "")}
                      onChange={(e) =>
                        updateBlock(
                          b.id,
                          {
                            url: e.target.value,
                            media_id: null,
                            media_name: "",
                          },
                          cid
                        )
                      }
                      disabled={saving}
                      placeholder="Aucune image"
                    />
                    <button
                      type="button"
                      className="Button Button--ghost Button--resources"
                      onClick={() =>
                        setImagePicker({
                          mode: "block_image",
                          blockId: b.id,
                          containerId: cid,
                        })
                      }
                      disabled={saving}
                    >
                      <span className="ButtonInline">
                        <IconFolder /> Source
                      </span>
                    </button>
                    {String(b.url || "").trim() || b.media_id ? (
                      <button
                        type="button"
                        className="Button Button--ghost Button--danger"
                        disabled={saving}
                        onClick={() =>
                          updateBlock(
                            b.id,
                            {
                              url: "",
                              media_id: null,
                              media_name: "",
                              alt: "",
                            },
                            cid
                          )
                        }
                      >
                        Retirer
                      </button>
                    ) : null}
                  </div>
                  {showImagePreview &&
                  resolveMediaSrc({ mediaId: b.media_id, url: b.url }) ? (
                    <img
                      src={resolveMediaSrc({ mediaId: b.media_id, url: b.url })}
                      alt=""
                      className="ImageInlinePreview"
                      style={{ marginTop: 10, height: 180, objectFit: "cover" }}
                      loading="lazy"
                    />
                  ) : null}
                </div>
                <div className="Field">
                  <label className="Label">Alt (accessibilité)</label>
                  <input
                    className="Input"
                    value={b.alt || ""}
                    onChange={(e) =>
                      updateBlock(b.id, { alt: e.target.value }, cid)
                    }
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="Field">
                <label className="Label">Légende (optionnel)</label>
                <input
                  className="Input"
                  value={b.caption || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { caption: e.target.value }, cid)
                  }
                  disabled={saving}
                />
              </div>
            </>
          ) : type === "video" ? (
            <>
              <div className="Field">
                <label className="Label">
                  URL de la vidéo (YouTube/Vimeo/fichier…)
                </label>
                <input
                  className="Input"
                  value={b.url || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { url: e.target.value }, cid)
                  }
                  disabled={saving}
                />
              </div>
              <div className="Field">
                <label className="Label">Légende (optionnel)</label>
                <input
                  className="Input"
                  value={b.caption || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { caption: e.target.value }, cid)
                  }
                  disabled={saving}
                />
              </div>
              {String(b.url || "").trim() ? (
                <div className="Hint">
                  L’embed automatique est “à venir”. Pour l’instant, l’URL sera
                  utilisée par le futur rendu côté site.
                </div>
              ) : null}
            </>
          ) : type === "embed" ? (
            <>
              <div className="Field">
                <label className="Label">
                  URL du média (PDF, iframe, map, etc.)
                </label>
                <input
                  className="Input"
                  value={b.url || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { url: e.target.value }, cid)
                  }
                  disabled={saving}
                />
              </div>
              <div className="Field">
                <label className="Label">Légende (optionnel)</label>
                <input
                  className="Input"
                  value={b.caption || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { caption: e.target.value }, cid)
                  }
                  disabled={saving}
                />
              </div>
            </>
          ) : type === "table" ? (
            <>
              <div className="Field">
                <label className="Label">Légende (optionnel)</label>
                <input
                  className="Input"
                  value={b.caption || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { caption: e.target.value }, cid)
                  }
                  disabled={saving}
                />
              </div>
              <div className="Field">
                <label className="Label">
                  Données CSV (lignes = lignes, séparateur = “,” ou “;”)
                </label>
                <textarea
                  className="Input"
                  rows={5}
                  value={b.csv || ""}
                  onChange={(e) =>
                    updateBlock(b.id, { csv: e.target.value }, cid)
                  }
                  onKeyDown={(e) =>
                    handleTextareaFormatShortcuts(
                      e,
                      (nextValue) => updateBlock(b.id, { csv: nextValue }, cid),
                      { enabled: formatShortcutsEnabled }
                    )
                  }
                  disabled={saving}
                />
                {String(b.csv || "").trim() ? (
                  <div
                    className="Notice"
                    style={{ marginTop: 10, overflowX: "auto" }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 13,
                      }}
                    >
                      <tbody>
                        {parseTable(b.csv).map((row, ridx) => (
                          <tr key={ridx}>
                            {row.map((cell, cidx) => (
                              <td
                                key={cidx}
                                style={{
                                  border: "1px solid var(--border)",
                                  padding: "8px 10px",
                                  color: "var(--text)",
                                  background:
                                    "color-mix(in srgb, var(--panel2) 40%, transparent)",
                                }}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="Muted">Type non reconnu.</div>
          )}
        </div>
      </div>
    );
  }

  function renderContainerCard(
    c,
    { depth = 1, parentId = null, indexInParent = 0, siblingsCount = 0 } = {}
  ) {
    const cid = String(c?.id || "");
    if (!cid) return null;
    const isCollapsed = Boolean(collapsedContainers?.[cid]);
    const bgPct = Math.max(18, Math.min(58, 18 + depth * 8));
    const borderPct = Math.max(18, Math.min(46, 34 - depth * 2));
    const title = String(c?.name || "Conteneur");
    const count = countBlocksDeepInContainer(c);
    const children = Array.isArray(c?.containers) ? c.containers : [];
    const blocks = Array.isArray(c?.blocks) ? c.blocks : [];
    const blocksLen = blocks.length;
    const siblingsBySlot = Array.from({ length: blocksLen + 1 }, () => []);
    const childIndexById = new Map();
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      const id = String(child?.id || "");
      if (id) childIndexById.set(id, i);
      const s = Math.max(
        0,
        Math.min(blocksLen, normalizeContainerSlot(child?.slot, blocksLen))
      );
      siblingsBySlot[s].push(child);
    }

    function renderContainerInsertZone(slot, toIndexOverride) {
      if (!dragContainer?.container_id) return null;
      const toIndex = Number.isFinite(Number(toIndexOverride))
        ? Number(toIndexOverride)
        : null;
      return (
        <div
          key={`ctr-slot-${cid}-${slot}-${
            toIndex == null ? "x" : String(toIndex)
          }`}
          className="Muted"
          style={{
            padding: "10px 12px",
            borderRadius: "var(--radius-lg)",
            border: "1px dashed var(--border)",
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            handleContainerDrop(e, cid, { toSlot: slot, toIndex });
          }}
        >
          Dépose un sous-conteneur ici.
        </div>
      );
    }

    function startIndexForSlot(slot) {
      const s = Number(slot);
      if (!Number.isFinite(s)) return children.length;
      for (let i = s; i < siblingsBySlot.length; i += 1) {
        const first = siblingsBySlot[i]?.[0] || null;
        const id = String(first?.id || "");
        if (id) return childIndexById.get(id) ?? children.length;
      }
      return children.length;
    }
    return (
      <div
        key={cid}
        className="BlockCard"
        style={{
          borderStyle: "dashed",
          borderColor: `color-mix(in srgb, var(--primary) ${borderPct}%, var(--border))`,
          borderWidth: 2,
          borderLeftWidth: depth > 1 ? 4 : 2,
          background: `color-mix(in srgb, var(--panel2) ${bgPct}%, transparent)`,
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          if (
            handleContainerDrop(e, parentId, {
              toIndex: indexInParent,
              toSlot: parentId ? c?.slot : null,
            })
          )
            return;
          handleDrop(e, cid, (c.blocks || []).length);
        }}
      >
        <div
          className="BlockHead"
          style={{ marginBottom: isCollapsed ? 0 : 10 }}
          onContextMenu={(e) => openContainerMenu(e, cid)}
        >
          <div className="BlockHeadLeft">
            <button
              type="button"
              className="DragHandle"
              draggable={!saving}
              onDragStart={(e) => beginDragContainer(e, cid)}
              onDragEnd={() => setDragContainer(null)}
              title="Déplacer le conteneur"
              aria-label="Déplacer le conteneur"
              disabled={saving}
            >
              <span aria-hidden="true">⋮⋮</span>
            </button>
            <button
              type="button"
              className="CollapseToggle CollapseToggle--sm"
              onClick={() =>
                setCollapsedContainers((s) => {
                  const cur = s || {};
                  const currentlyCollapsed = Boolean(cur?.[cid]);
                  const nextCollapsed = !currentlyCollapsed;
                  if (
                    ui.container_open_mode === "single" &&
                    nextCollapsed === false
                  ) {
                    const next = {};
                    for (const id of allContainerIds) next[id] = true;
                    next[cid] = false;
                    return next;
                  }
                  return { ...cur, [cid]: nextCollapsed };
                })
              }
              aria-label={isCollapsed ? "Déplier" : "Replier"}
            >
              {isCollapsed ? <IconChevronRight /> : <IconChevronDown />}
            </button>
            <div className="BlockHeadMeta">
              {editingContainerId === cid ? (
                <input
                  className="Input"
                  style={{ padding: "8px 10px", fontWeight: 650 }}
                  value={containerNameDraft}
                  autoFocus
                  onChange={(e) => setContainerNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      updateContainer(cid, { name: containerNameDraft });
                      setEditingContainerId(null);
                      setContainerNameDraft("");
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setEditingContainerId(null);
                      setContainerNameDraft("");
                    }
                  }}
                  onBlur={() => {
                    updateContainer(cid, { name: containerNameDraft });
                    setEditingContainerId(null);
                    setContainerNameDraft("");
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="Button Button--ghost"
                  style={{ padding: "8px 10px", justifyContent: "flex-start" }}
                  onClick={() => {
                    setEditingContainerId(cid);
                    setContainerNameDraft(title);
                  }}
                  disabled={saving}
                  title="Renommer"
                >
                  <span className="ButtonInline">
                    <IconLayoutGrid /> {title}
                  </span>
                </button>
              )}
              {isCollapsed ? (
                <div
                  className="Muted OneLine"
                  style={{ fontSize: 13, marginTop: 6 }}
                >
                  {count} élément(s)
                </div>
              ) : null}
            </div>
          </div>
          <div className="Badges" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              className="BadgeButton"
              disabled={saving}
              onClick={(e) => openContainerMenu(e, cid)}
              onContextMenu={(e) => openContainerMenu(e, cid)}
              title="Actions"
              aria-label="Actions"
            >
              ⋯
            </button>
          </div>
        </div>

        {isCollapsed ? null : (
          <>
            <div
              className="BlockList"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                if (handleContainerDrop(e, cid, { toSlot: blocksLen })) return;
                handleDrop(e, cid, (c.blocks || []).length);
              }}
            >
              {renderContainerInsertZone(0, startIndexForSlot(0))}
              {siblingsBySlot[0].map((child) => {
                const childId = String(child?.id || "");
                const childIndex = childId
                  ? childIndexById.get(childId) ?? 0
                  : 0;
                return (
                  <Fragment key={`ctr0_${childId || childIndex}`}>
                    {renderContainerCard(child, {
                      depth: depth + 1,
                      parentId: cid,
                      indexInParent: childIndex,
                      siblingsCount: children.length,
                    })}
                    {renderContainerInsertZone(0, childIndex + 1)}
                  </Fragment>
                );
              })}
              {blocks.map((b, idx) => (
                <Fragment key={String(b?.id || idx)}>
                  {renderBlockEditor(b, { containerId: cid, index: idx })}
                  {renderContainerInsertZone(
                    idx + 1,
                    startIndexForSlot(idx + 1)
                  )}
                  {siblingsBySlot[idx + 1].map((child) => {
                    const childId = String(child?.id || "");
                    const childIndex = childId
                      ? childIndexById.get(childId) ?? 0
                      : 0;
                    return (
                      <Fragment key={`ctr_${idx + 1}_${childId || childIndex}`}>
                        {renderContainerCard(child, {
                          depth: depth + 1,
                          parentId: cid,
                          indexInParent: childIndex,
                          siblingsCount: children.length,
                        })}
                        {renderContainerInsertZone(idx + 1, childIndex + 1)}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
              {blocks.length === 0 ? (
                <div className="Muted">
                  Dépose un élément ici (drag & drop) ou ajoute-en un.
                </div>
              ) : null}
            </div>

            <div
              className="Badges"
              style={{ justifyContent: "flex-start", marginTop: 10 }}
            >
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => {
                  setBlockPickerContainerId(cid);
                  setBlockPickerOpen(true);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> Ajouter un élément
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving || depth >= MAX_CONTAINER_DEPTH}
                onClick={() => addContainer(cid)}
                title={
                  depth >= MAX_CONTAINER_DEPTH
                    ? `Profondeur max: ${MAX_CONTAINER_DEPTH}`
                    : "Ajouter un sous-conteneur"
                }
              >
                <span className="ButtonInline">
                  <IconLayoutGrid /> Sous-conteneur
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="SectionCard"
      style={{ "--accent": accent }}
    >
      <div className="SectionHead" onContextMenu={openSectionMenu}>
        <div className="SectionMeta">
          <div className="SectionTitle">
            <button
              type="button"
              className="DragHandle"
              draggable={!saving}
              onDragStart={(e) => {
                const t = e?.target;
                if (t?.closest?.("input, textarea, select, a")) {
                  e.preventDefault();
                  return;
                }
                onSectionDragStart?.();
              }}
              onDragEnd={() => onSectionDragEnd?.()}
              disabled={saving}
              title="Déplacer la section"
              aria-label="Déplacer la section"
            >
              <span aria-hidden="true">⋮⋮</span>
            </button>
            <button
              type="button"
              className="CollapseToggle"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? "Déplier" : "Replier"}
            >
              {collapsed ? <IconChevronRight /> : <IconChevronDown />}
            </button>
            <span className="SectionIcon" aria-hidden="true">
              <TplIcon />
            </span>
            {editingSectionTitle ? (
              <input
                ref={sectionTitleInputRef}
                className="Input InlineRenameInput"
                value={sectionTitleDraft}
                onChange={(e) => setSectionTitleDraft(e.target.value)}
                disabled={saving}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRenameSection(sectionTitleDraft);
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditingSectionTitle(false);
                    setSectionTitleDraft("");
                  }
                }}
                onBlur={() => commitRenameSection(sectionTitleDraft)}
              />
            ) : (
              <button
                type="button"
                className="InlineRename"
                onClick={beginRenameSection}
                disabled={saving}
                title="Renommer"
              >
                <span className="OneLine" style={{ maxWidth: "100%" }}>
                  #{index + 1} — {section.title || "Section"}
                </span>
              </button>
            )}
          </div>
          {hint ? <div className="Muted">{hint}</div> : null}
          {collapsed ? (
            <div
              className="Muted OneLine"
              style={{ fontSize: 13, marginTop: 6 }}
            >
              {templateKey === "steps"
                ? `${steps.length} étape(s)${
                    steps.length
                      ? ` — ${oneLine(
                          steps[0]?.title || steps[0]?.description || ""
                        )}`
                      : ""
                  }`
                : templateKey === "banner"
                ? `${
                    section.background_image_url || section.background_media_id
                      ? `Bannière — ${oneLine(
                          resolveMediaName({
                            mediaId: section.background_media_id,
                            mediaName: section.background_media_name,
                            url: section.background_image_url,
                          })
                        )}`
                      : "Bannière (image)"
                  }`
                : templateKey === "classic"
                ? `${classicCounts.total} élément(s)${
                    classicCounts.total && classicCounts.firstBlock
                      ? ` — ${blockSummary(classicCounts.firstBlock)}`
                      : classicCounts.containerCount
                      ? " — conteneurs"
                      : ""
                  }`
                : `${(section.blocks || []).length} bloc(s)${
                    (section.blocks || []).length
                      ? ` — ${blockSummary((section.blocks || [])[0])}`
                      : ""
                  }`}
            </div>
          ) : null}
        </div>
        <div className="Badges">
          <button
            type="button"
            className="Button Button--ghost Button--danger Button--icon"
            disabled={saving}
            onClick={() => onDelete(section.id)}
            title="Supprimer la section"
            aria-label="Supprimer la section"
          >
            <IconTrash />
          </button>
        </div>
      </div>

      {collapsed ? null : (
        <>
          <div className="FormGrid">
            {templateKey === "classic" ? (
              <div className="Field">
                <label className="LabelStrong LabelStrong--lg">
                  Direction des éléments
                </label>
                <DirectionPicker
                  value={String(settings.direction || "column")}
                  onChange={(dir) => updateSettings({ direction: dir })}
                  disabled={saving}
                  ariaLabel="Direction des éléments de la section"
                />
              </div>
            ) : null}
            <div
              className="Field"
              style={
                templateKey === "classic" ? undefined : { gridColumn: "1 / -1" }
              }
            >
              <label className="LabelStrong LabelStrong--lg">
                Arrière-plan de la section
              </label>
              <div
                className="Seg"
                role="group"
                aria-label="Background de section"
              >
                <button
                  type="button"
                  className={`SegButton ${
                    bgType === "none" ? "is-active" : ""
                  }`}
                  onClick={() =>
                    onUpdate(section.id, { background_type: "none" })
                  }
                >
                  <span className="ButtonInline">
                    <IconBan /> Rien
                  </span>
                </button>
                <button
                  type="button"
                  className={`SegButton ${
                    bgType === "color" ? "is-active" : ""
                  }`}
                  onClick={() =>
                    onUpdate(section.id, {
                      background_type: "color",
                      background_color:
                        (section.background_color || "").trim() || "#0b1220",
                    })
                  }
                >
                  <span className="ButtonInline">
                    <IconDroplet /> Couleur
                  </span>
                </button>
                <button
                  type="button"
                  className={`SegButton ${
                    bgType === "image" ? "is-active" : ""
                  }`}
                  onClick={() =>
                    onUpdate(section.id, { background_type: "image" })
                  }
                >
                  <span className="ButtonInline">
                    <IconImage /> Image
                  </span>
                </button>
                <button
                  type="button"
                  className={`SegButton ${
                    bgType === "background" ? "is-active" : ""
                  }`}
                  onClick={() =>
                    onUpdate(section.id, {
                      background_type: "background",
                      background:
                        String(section.background || "").trim() ||
                        "linear-gradient(135deg, #0b1220, #0b0f14)",
                    })
                  }
                >
                  <span className="ButtonInline">
                    <IconFileText /> Custom
                  </span>
                </button>
              </div>

              {bgType === "color" ? (
                <div className="FormGrid" style={{ marginTop: 10 }}>
                  <div className="Field">
                    <label className="Label">Couleur (hex)</label>
                    <input
                      className="Input"
                      value={section.background_color || ""}
                      onChange={(e) =>
                        onUpdate(section.id, {
                          background_color: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              ) : null}

              {bgType === "background" ? (
                <div className="Field" style={{ marginTop: 10 }}>
                  <label className="Label">CSS background</label>
                  <textarea
                    className="Textarea"
                    rows={3}
                    value={String(section.background || "")}
                    onChange={(e) =>
                      onUpdate(section.id, {
                        background_type: "background",
                        background: e.target.value,
                      })
                    }
                    disabled={saving}
                    placeholder="ex: linear-gradient(...), url(...) center/cover no-repeat"
                  />
                </div>
              ) : null}

              {bgType === "image" ? (
                <div className="Field" style={{ marginTop: 10 }}>
                  <label className="Label">Image</label>
                  <div className="InputRow">
                    <input
                      className="Input"
                      value={String(section.background_image_url || "")}
                      onChange={(e) =>
                        onUpdate(section.id, {
                          background_type: "image",
                          background_image_url: e.target.value,
                          background_media_id: null,
                          background_media_name: "",
                        })
                      }
                      disabled={saving}
                      placeholder="Aucune image"
                    />
                    <button
                      type="button"
                      className="Button Button--ghost Button--resources"
                      onClick={() => setImagePicker({ mode: "section_bg" })}
                      disabled={saving}
                    >
                      <span className="ButtonInline">
                        <IconFolder /> Source
                      </span>
                    </button>
                    {String(section.background_image_url || "").trim() ||
                    section.background_media_id ? (
                      <button
                        type="button"
                        className="Button Button--ghost Button--danger"
                        disabled={saving}
                        onClick={() =>
                          onUpdate(section.id, {
                            background_type: "none",
                            background_image_url: "",
                            background_media_id: null,
                            background_media_name: "",
                          })
                        }
                      >
                        Retirer
                      </button>
                    ) : null}
                  </div>
                  {showImagePreview &&
                  resolveMediaSrc({
                    mediaId: section.background_media_id,
                    url: section.background_image_url,
                  }) ? (
                    <img
                      src={resolveMediaSrc({
                        mediaId: section.background_media_id,
                        url: section.background_image_url,
                      })}
                      alt=""
                      className="ImageInlinePreview"
                      style={{
                        marginTop: 10,
                        height:
                          templateKey === "banner"
                            ? Number(settings.height_px || 420)
                            : 180,
                        objectFit: String(settings.fit || "cover"),
                      }}
                      loading="lazy"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {templateKey === "banner" ? (
            <div className="RoleBox" style={{ marginTop: 12 }}>
              <div className="LabelStrong">Options Bannière</div>
              <div className="FormGrid" style={{ marginTop: 10 }}>
                <div className="Field">
                  <label className="Label">Hauteur (px)</label>
                  <input
                    className="Input"
                    inputMode="numeric"
                    value={bannerHeightDraft}
                    onChange={(e) => setBannerHeightDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitBannerHeight(bannerHeightDraft);
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setBannerHeightDraft(String(settings.height_px ?? 420));
                      }
                    }}
                    onBlur={() => commitBannerHeight(bannerHeightDraft)}
                  />
                </div>
                <div className="Field">
                  <label className="Label">Rendu</label>
                  <select
                    className="Select"
                    value={String(settings.fit ?? "cover")}
                    onChange={(e) => updateSettings({ fit: e.target.value })}
                  >
                    <option value="cover">Cover</option>
                    <option value="contain">Contain</option>
                  </select>
                </div>
              </div>
              <div className="FormGrid" style={{ marginTop: 10 }}>
                <div className="Field">
                  <label className="Label">Background repeat</label>
                  <select
                    className="Select"
                    value={String(settings.banner_repeat ?? "no-repeat")}
                    onChange={(e) =>
                      updateSettings({ banner_repeat: e.target.value })
                    }
                  >
                    <option value="no-repeat">No repeat</option>
                    <option value="repeat">Repeat</option>
                    <option value="repeat-x">Repeat X</option>
                    <option value="repeat-y">Repeat Y</option>
                  </select>
                </div>
              </div>
              <div className="FormGrid" style={{ marginTop: 10 }}>
                <div className="Field">
                  <label className="Label">Texte alternatif</label>
                  <input
                    className="Input"
                    value={String(settings.banner_alt ?? "")}
                    onChange={(e) =>
                      updateSettings({ banner_alt: e.target.value })
                    }
                  />
                </div>
                <div className="Field">
                  <label className="Label">Légende</label>
                  <input
                    className="Input"
                    value={String(settings.banner_caption ?? "")}
                    onChange={(e) =>
                      updateSettings({ banner_caption: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
          ) : null}

          {templateKey !== "classic" &&
          templateKey !== "steps" &&
          templateKey !== "banner" ? (
            <div className="RoleBox" style={{ marginTop: 12 }}>
              <div className="LabelStrong">
                Options {tpl?.label || templateKey}
              </div>
              {templateKey === "grid" ? (
                <>
                  <div className="FormGrid" style={{ marginTop: 10 }}>
                    <div className="Field">
                      <label className="Label">Colonnes</label>
                      <select
                        className="Select"
                        value={String(settings.columns ?? 2)}
                        onChange={(e) =>
                          updateSettings({
                            columns: Number(e.target.value) || 2,
                          })
                        }
                      >
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                    </div>
                  </div>
                  <div className="Hint">
                    Astuce: utilise 1 bloc texte par colonne (ou image/vidéo).
                  </div>
                </>
              ) : null}

              {templateKey === "cards" ? (
                <>
                  <div className="FormGrid" style={{ marginTop: 10 }}>
                    <div className="Field">
                      <label className="Label">Cartes par ligne</label>
                      <select
                        className="Select"
                        value={String(settings.columns ?? 3)}
                        onChange={(e) =>
                          updateSettings({
                            columns: Number(e.target.value) || 3,
                          })
                        }
                      >
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                    </div>
                  </div>
                  <div className="Hint">Les blocs deviennent des cartes.</div>
                </>
              ) : null}

              {templateKey === "slider" ? (
                <>
                  <div
                    className="Badges"
                    style={{ justifyContent: "flex-start", marginTop: 10 }}
                  >
                    <button
                      type="button"
                      className={`TogglePill ${
                        settings.autoplay ? "is-on" : ""
                      }`}
                      onClick={() =>
                        updateSettings({ autoplay: !settings.autoplay })
                      }
                    >
                      {settings.autoplay ? "Autoplay ON" : "Autoplay OFF"}
                    </button>
                  </div>
                  <div className="FormGrid" style={{ marginTop: 10 }}>
                    <div className="Field">
                      <label className="Label">Intervalle (ms)</label>
                      <input
                        className="Input"
                        inputMode="numeric"
                        value={String(settings.interval_ms ?? 3500)}
                        onChange={(e) =>
                          updateSettings({
                            interval_ms: Number(e.target.value) || 3500,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="Hint">
                    Prévois surtout des blocs Image/Vidéo.
                  </div>
                </>
              ) : null}

              {templateKey === "hero" ? (
                <>
                  <div className="FormGrid" style={{ marginTop: 10 }}>
                    <div className="Field">
                      <label className="Label">Layout</label>
                      <select
                        className="Select"
                        value={String(settings.layout ?? "left")}
                        onChange={(e) =>
                          updateSettings({ layout: e.target.value })
                        }
                      >
                        <option value="left">Texte à gauche</option>
                        <option value="right">Texte à droite</option>
                      </select>
                    </div>
                    <div className="Field">
                      <label className="Label">CTA label</label>
                      <input
                        className="Input"
                        value={String(settings.cta_label ?? "")}
                        onChange={(e) =>
                          updateSettings({ cta_label: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="Field">
                    <label className="Label">CTA URL</label>
                    <input
                      className="Input"
                      value={String(settings.cta_url ?? "")}
                      onChange={(e) =>
                        updateSettings({ cta_url: e.target.value })
                      }
                    />
                  </div>
                  <div className="Hint">
                    Conseil: 1 titre (h2) + 1 paragraphe.
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {templateKey === "banner" ? null : (
            <div className="Field">
              <div className="LabelStrong LabelStrong--lg">
                {templateKey === "steps" ? "Étapes" : "Blocs"}
              </div>
              <div className="BlockList">
                {templateKey === "steps" ? (
                  <>
                    {steps.map((st, sidx) => {
                      const isStepCollapsed = Boolean(
                        collapsedBlocks?.[String(st.id)]
                      );
                      const stepSummary =
                        oneLine(
                          `${st.title}${
                            st.description ? ` — ${st.description}` : ""
                          }`
                        ) || `Étape ${sidx + 1}`;
                      return (
                        <div key={st.id} className="BlockCard">
                          <div
                            className="BlockHead"
                            onContextMenu={(e) => openStepMenu(e, st.id)}
                          >
                            <div className="BlockHeadLeft">
                              <button
                                type="button"
                                className="CollapseToggle CollapseToggle--sm"
                                onClick={() =>
                                  setCollapsedBlocks((s) => ({
                                    ...(s || {}),
                                    [String(st.id)]: !Boolean(
                                      s?.[String(st.id)]
                                    ),
                                  }))
                                }
                                aria-label={
                                  isStepCollapsed ? "Déplier" : "Replier"
                                }
                              >
                                {isStepCollapsed ? (
                                  <IconChevronRight />
                                ) : (
                                  <IconChevronDown />
                                )}
                              </button>
                              <div className="BlockHeadMeta">
                                <div
                                  className="BlockMetaLine"
                                  style={{ alignItems: "center" }}
                                >
                                  <span className="Muted" style={{ fontSize: 12 }}>
                                    <span className="ButtonInline">
                                      <IconSteps /> <strong>Étape {sidx + 1}</strong>
                                    </span>
                                  </span>
                                  <span
                                    className="Muted"
                                    style={{ fontSize: 12 }}
                                  >
                                    Titre:
                                  </span>
                                  <button
                                    type="button"
                                    className="InlineRename InlineRename--inline"
                                    onClick={() => beginRenameStep(st.id)}
                                    disabled={saving}
                                    title="Modifier le titre"
                                  >
                                    <span
                                      className="OneLine"
                                      style={{ maxWidth: 260 }}
                                    >
                                      {String(st.title || "").trim()
                                        ? String(st.title || "").trim()
                                        : "—"}
                                    </span>
                                  </button>
                                </div>
                                {isStepCollapsed ? (
                                  <div
                                    className="Muted OneLine"
                                    style={{ fontSize: 13, marginTop: 6 }}
                                  >
                                    {stepSummary}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="Badges">
                              <button
                                type="button"
                                className="Button Button--ghost Button--danger Button--icon"
                                disabled={saving}
                                onClick={() =>
                                  updateSteps(
                                    steps.filter((x) => x.id !== st.id)
                                  )
                                }
                                title="Supprimer l’étape"
                                aria-label="Supprimer l’étape"
                              >
                                <IconTrash />
                              </button>
                            </div>
                          </div>

                          {isStepCollapsed ? null : (
                            <>
                              <div className="FormGrid">
                                <div className="Field">
                                  <label className="LabelStrong LabelStrong--lg">
                                    Titre
                                  </label>
                                  <input
                                    ref={
                                      renamingStepId === String(st.id)
                                        ? stepTitleInputRef
                                        : null
                                    }
                                    className="Input"
                                    value={st.title}
                                    onChange={(e) =>
                                      updateSteps(
                                        steps.map((x) =>
                                          x.id === st.id
                                            ? { ...x, title: e.target.value }
                                            : x
                                        )
                                      )
                                    }
                                    onBlur={() => {
                                      if (renamingStepId === String(st.id))
                                        setRenamingStepId(null);
                                    }}
                                    disabled={saving}
                                  />
                                </div>
                              </div>
                              <div className="Field" style={{ marginTop: 10 }}>
                                <label className="Label">Description</label>
                                <textarea
                                  className="Textarea"
                                  rows={6}
                                  value={st.description}
                                  onChange={(e) =>
                                    updateSteps(
                                      steps.map((x) =>
                                        x.id === st.id
                                          ? {
                                              ...x,
                                              description: e.target.value,
                                            }
                                          : x
                                      )
                                    )
                                  }
                                  onKeyDown={(e) =>
                                    handleTextareaFormatShortcuts(
                                      e,
                                      (nextValue) =>
                                        updateSteps(
                                          steps.map((x) =>
                                            x.id === st.id
                                              ? {
                                                  ...x,
                                                  description: nextValue,
                                                }
                                              : x
                                          )
                                        ),
                                      { enabled: formatShortcutsEnabled }
                                    )
                                  }
                                  disabled={saving}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}

                    <div className="Muted" style={{ marginTop: 6 }}>
                      Hors étapes
                    </div>
                    {containers.map((c, cidx) =>
                      renderContainerCard(c, {
                        depth: 1,
                        parentId: null,
                        indexInParent: cidx,
                        siblingsCount: containers.length,
                      })
                    )}
                    {dragContainer?.container_id ? (
                      <div
                        className="Muted"
                        style={{
                          padding: "10px 12px",
                          borderRadius: "var(--radius-lg)",
                          border: "1px dashed var(--border)",
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          handleContainerDrop(e, null, containers.length);
                        }}
                      >
                        Dépose un conteneur ici.
                      </div>
                    ) : null}
                    <div
                      className="BlockList"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, null, rootBlocks.length)}
                      style={{ minWidth: 0 }}
                    >
                      {rootBlocks.map((b, idx) =>
                        renderBlockEditor(b, {
                          containerId: null,
                          index: idx,
                          outsideSteps: true,
                        })
                      )}
                      {rootBlocks.length === 0 && containers.length === 0 ? (
                        <div className="Muted">
                          Aucun contenu hors étapes. Ajoute-en un.
                        </div>
                      ) : null}
                      {rootBlocks.length === 0 && containers.length > 0 ? (
                        <div className="Muted">
                          Aucun élément hors étapes (hors conteneurs).
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : templateKey === "classic" ? (
                  <>
                    {dragContainer?.container_id ? (
                      <div
                        className="Muted"
                        style={{
                          padding: "10px 12px",
                          borderRadius: "var(--radius-lg)",
                          border: "1px dashed var(--border)",
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          handleContainerDrop(e, null, 0);
                        }}
                      >
                        Dépose un conteneur ici.
                      </div>
                    ) : null}
                    {containers.map((c, cidx) => (
                      <Fragment key={String(c?.id || cidx)}>
                        {renderContainerCard(c, {
                          depth: 1,
                          parentId: null,
                          indexInParent: cidx,
                          siblingsCount: containers.length,
                        })}
                        {dragContainer?.container_id ? (
                          <div
                            className="Muted"
                            style={{
                              padding: "10px 12px",
                              borderRadius: "var(--radius-lg)",
                              border: "1px dashed var(--border)",
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              handleContainerDrop(e, null, cidx + 1);
                            }}
                          >
                            Dépose un conteneur ici.
                          </div>
                        ) : null}
                      </Fragment>
                    ))}
                    {(containers || []).length ? (
                      <div className="Muted" style={{ marginTop: 10 }}>
                        Hors conteneur
                      </div>
                    ) : null}
                    <div
                      className="BlockList"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, null, rootBlocks.length)}
                      style={{ minWidth: 0 }}
                    >
                      {rootBlocks.map((b, idx) =>
                        renderBlockEditor(b, { containerId: null, index: idx })
                      )}
                      {rootBlocks.length === 0 && containers.length === 0 ? (
                        <div className="Muted">
                          Aucun élément. Ajoute un élément ou un conteneur.
                        </div>
                      ) : null}
                      {rootBlocks.length === 0 && containers.length > 0 ? (
                        <div className="Muted">
                          Aucun élément hors conteneur.
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="BlockList"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, null, rootBlocks.length)}
                      style={{ minWidth: 0 }}
                    >
                      {rootBlocks.map((b, idx) =>
                        renderBlockEditor(b, { containerId: null, index: idx })
                      )}
                      {rootBlocks.length === 0 ? (
                        <div className="Muted">Aucun bloc. Ajoute-en un.</div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {templateKey === "banner" ? null : templateKey === "steps" ? (
            <div
              className="Badges"
              style={{ justifyContent: "flex-start", marginTop: 10 }}
            >
              <button
                type="button"
                className="Button Button--ghost Button--stepsPrimary"
                disabled={saving}
                onClick={() => {
                  const id = newId("step");
                  updateSteps([
                    ...(steps || []),
                    { id, title: `Étape ${steps.length + 1}`, description: "" },
                  ]);
                  setCollapsedBlocks((prev) => ({
                    ...(prev || {}),
                    [String(id)]: true,
                  }));
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> Ajouter une étape
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => addContainer(null)}
              >
                <span className="ButtonInline">
                  <IconLayoutGrid /> Ajouter un conteneur hors étapes
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => {
                  setBlockPickerContainerId(null);
                  setBlockPickerOpen(true);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> Ajouter un contenu hors étapes
                </span>
              </button>
            </div>
          ) : templateKey === "classic" ? (
            <div
              className="Badges"
              style={{ justifyContent: "flex-start", marginTop: 10 }}
            >
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => addContainer(null)}
              >
                <span className="ButtonInline">
                  <IconLayoutGrid /> Ajouter un conteneur
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => {
                  setBlockPickerContainerId(null);
                  setBlockPickerOpen(true);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> Ajouter un élément
                </span>
              </button>
            </div>
          ) : (
            <div
              className="Badges"
              style={{ justifyContent: "flex-start", marginTop: 10 }}
            >
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => {
                  setBlockPickerContainerId(null);
                  setBlockPickerOpen(true);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> Ajouter un élément
                </span>
              </button>
            </div>
          )}

          {blockPickerOpen ? (
            <BlockPickerModal
              onClose={() => {
                setBlockPickerOpen(false);
                setBlockPickerContainerId(null);
              }}
              onPick={(t) => {
                addBlock(t, blockPickerContainerId);
                setBlockPickerOpen(false);
                setBlockPickerContainerId(null);
              }}
            />
          ) : null}

          {containerClassesModal ? (
            <Modal
              title="Classes CSS du conteneur"
              icon={<IconTag />}
              onClose={() => setContainerClassesModal(null)}
              footer={
                <>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() => setContainerClassesModal(null)}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="Button"
                    onClick={() => {
                      const cid = String(
                        containerClassesModal.containerId || ""
                      );
                      updateContainer(cid, {
                        extra_class: String(
                          containerClassesModal.value || ""
                        ).trim(),
                      });
                      setContainerClassesModal(null);
                    }}
                    disabled={saving}
                  >
                    Enregistrer
                  </button>
                </>
              }
            >
              <div className="Field">
                <label className="Label">Classes CSS</label>
                <input
                  className="Input"
                  value={String(containerClassesModal.value || "")}
                  onChange={(e) =>
                    setContainerClassesModal((m) =>
                      m ? { ...m, value: e.target.value } : m
                    )
                  }
                  placeholder="ex: gap-6 items-center md:flex-row"
                  autoFocus
                  disabled={saving}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const cid = String(
                        containerClassesModal.containerId || ""
                      );
                      updateContainer(cid, {
                        extra_class: String(
                          containerClassesModal.value || ""
                        ).trim(),
                      });
                      setContainerClassesModal(null);
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setContainerClassesModal(null);
                    }
                  }}
                />
                <div className="Hint">Sépare les classes par des espaces.</div>
              </div>
            </Modal>
          ) : null}

          {deleteContainerConfirm ? (
            <Modal
              title="Supprimer le conteneur ?"
              icon={<IconAlertTriangle />}
              onClose={() => setDeleteContainerConfirm(null)}
              footer={
                <>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() => setDeleteContainerConfirm(null)}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() => {
                      const cid = String(
                        deleteContainerConfirm?.containerId || ""
                      );
                      setDeleteContainerConfirm(null);
                      deleteContainerMoveContents(cid);
                    }}
                  >
                    Supprimer (garder les éléments)
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost Button--danger"
                    onClick={() => {
                      const cid = String(
                        deleteContainerConfirm?.containerId || ""
                      );
                      setDeleteContainerConfirm(null);
                      deleteContainerAndContents(cid);
                    }}
                  >
                    Supprimer conteneur + éléments
                  </button>
                </>
              }
            >
              <div className="Muted">
                Ce conteneur contient{" "}
                <strong>
                  {Number(deleteContainerConfirm.blockCount) || 0}
                </strong>{" "}
                élément(s)
                {Number(deleteContainerConfirm.containerCount) ? (
                  <>
                    {" "}
                    et{" "}
                    <strong>
                      {Number(deleteContainerConfirm.containerCount) || 0}
                    </strong>{" "}
                    sous-conteneur(s)
                  </>
                ) : null}
                .
              </div>
            </Modal>
          ) : null}

          {imagePicker ? (
            <ProjectResourcesImagePickerModal
              siteId={Number(siteId)}
              projectId={Number(projectId)}
              apiBaseUrl={apiBaseUrl}
              assetsBaseUrl={assetsBaseUrl}
              assets={projectImageAssets}
              onClose={() => setImagePicker(null)}
              onPick={(asset) => {
                const url = mediaAssetSourceValue(asset, {
                  apiBaseUrl,
                  siteId: Number(siteId),
                  assetsBaseUrl,
                });
                if (imagePicker.mode === "section_bg") {
                  onUpdate(section.id, {
                    background_type: "image",
                    background_image_url: url,
                    background_media_id: Number(asset.id),
                    background_media_name: String(
                      asset.original_name || asset.note || ""
                    ),
                  });
                }
                if (imagePicker.mode === "block_image") {
                  const cid =
                    imagePicker.containerId == null
                      ? null
                      : String(imagePicker.containerId);
                  const list = !cid
                    ? section.blocks || []
                    : findContainerById(containers, cid)?.blocks || [];
                  const current =
                    list.find(
                      (x) => String(x.id) === String(imagePicker.blockId)
                    ) || null;
                  const nextAlt = String(current?.alt || "").trim()
                    ? current.alt
                    : String(asset.note || asset.original_name || "");
                  updateBlock(
                    imagePicker.blockId,
                    {
                      url,
                      media_id: Number(asset.id),
                      media_name: String(
                        asset.original_name || asset.note || ""
                      ),
                      alt: nextAlt,
                    },
                    cid
                  );
                }
                setImagePicker(null);
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function ProjectResourcesImagePickerModal({
  siteId,
  projectId,
  apiBaseUrl,
  assetsBaseUrl,
  assets: assetsOverride,
  onClose,
  onPick,
}) {
  const [q, setQ] = useState("");
  const hasOverride = Array.isArray(assetsOverride);
  const [loading, setLoading] = useState(!hasOverride);
  const [error, setError] = useState("");
  const [assets, setAssets] = useState(hasOverride ? assetsOverride : []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!hasOverride) return;
    setLoading(false);
    setError("");
    setAssets(assetsOverride || []);
  }, [assetsOverride, hasOverride]);

  useEffect(() => {
    if (hasOverride) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await api.mediaList(Number(siteId));
      if (!mounted) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error || "Chargement impossible");
        return;
      }
      setAssets(res.assets || []);
    })();
    return () => {
      mounted = false;
    };
  }, [siteId, hasOverride]);

  const imagesForProject = useMemo(() => {
    const pid = Number(projectId);
    const query = String(q || "")
      .trim()
      .toLowerCase();
    const list = (assets || [])
      .filter((a) => a && !Boolean(a.is_hidden))
      .filter((a) => String(a.kind || "") === "image")
      .filter((a) =>
        (a.links || []).some(
          (l) => l.type === "project" && Number(l.id) === pid
        )
      );

    const filtered = query
      ? list.filter((a) => {
          const hay = [a.note, a.original_name, a.mime_type, a.variant].map(
            (x) => String(x || "").toLowerCase()
          );
          return hay.some((s) => s.includes(query));
        })
      : list;

    function pinnedInfo(a) {
      const link =
        (a.links || []).find(
          (l) => l.type === "project" && Number(l.id) === pid
        ) || null;
      const isPinned = Boolean(link?.is_pinned);
      const t = link?.pinned_at
        ? new Date(String(link.pinned_at)).getTime()
        : 0;
      return { isPinned, t: Number.isFinite(t) ? t : 0 };
    }

    return filtered.slice().sort((a, b) => {
      const pa = pinnedInfo(a);
      const pb = pinnedInfo(b);
      if (pa.isPinned !== pb.isPinned) return pa.isPinned ? -1 : 1;
      if (pa.t !== pb.t) return pb.t - pa.t;
      return Number(b.id) - Number(a.id);
    });
  }, [assets, projectId, q]);

  async function handleUploadFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploadError("");
    setUploading(true);
    const res = await api.mediaUpload(Number(siteId), files, {
      project_id: Number(projectId),
    });
    setUploading(false);
    if (!res.ok) {
      setUploadError(res.error || "Upload impossible");
      return;
    }
    const nextAssets = res.assets || [];
    setAssets(nextAssets);
    if (
      typeof onPick === "function" &&
      Array.isArray(res.created) &&
      res.created[0]?.id
    ) {
      const createdId = Number(res.created[0].id);
      const createdAsset =
        nextAssets.find((a) => Number(a?.id) === createdId) || null;
      if (createdAsset) onPick(createdAsset);
    }
  }

  return (
    <Modal
      title="Choisir une image (Source)"
      icon={<IconImage />}
      onClose={onClose}
      footer={
        <button
          type="button"
          className="Button Button--ghost"
          onClick={onClose}
        >
          Fermer
        </button>
      }
    >
      {error ? (
        <div className="Error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="FormGrid">
        <div className="Field">
          <label className="Label">Rechercher</label>
          <input
            className="Input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, note, type…"
          />
        </div>
      </div>
      <div
        className="Badges"
        style={{ justifyContent: "flex-start", marginBottom: 10 }}
      >
        <button
          type="button"
          className="Button Button--ghost Button--resources"
          onClick={() => fileInputRef.current?.click?.()}
          disabled={uploading}
        >
          <span className="ButtonInline">
            <IconPlus /> {uploading ? "Upload…" : "Uploader un fichier"}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            handleUploadFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {normalizeAssetsBaseUrl(assetsBaseUrl) ? (
          <span className="Badge">Chemin enregistré: img/…</span>
        ) : (
          <span className="Badge">Chemin enregistré: URL média</span>
        )}
      </div>

      {loading ? <div className="Muted">Chargement…</div> : null}
      {uploadError ? (
        <div className="Error" role="alert" style={{ marginBottom: 10 }}>
          {uploadError}
        </div>
      ) : null}
      {!loading && imagesForProject.length === 0 ? (
        <div className="Hint">
          Aucune image trouvée pour ce projet. Associe d’abord des médias à ce
          projet dans le module <strong>Ressources</strong> ou upload ici.
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        {imagesForProject.map((a) => {
          const src = mediaInlineUrl(apiBaseUrl, Number(siteId), Number(a.id));
          return (
            <div
              key={a.id}
              className="Tile"
              style={{ padding: 0, overflow: "hidden" }}
            >
              <button
                type="button"
                className="TileButton"
                style={{ padding: 0 }}
                onClick={() => onPick?.(a)}
              >
                <img
                  src={src}
                  alt={a.note || a.original_name || ""}
                  className="MediaThumb"
                  loading="lazy"
                />
                <div style={{ padding: 12 }}>
                  <div className="TileTitle OneLine">
                    {a.note || a.original_name || `Image #${a.id}`}
                  </div>
                  <div className="Muted OneLine" style={{ fontSize: 12 }}>
                    {a.original_name}{" "}
                    {a.variant && a.variant !== "original"
                      ? `— ${a.variant}`
                      : ""}
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function BlockPickerModal({ onPick, onClose }) {
  const items = [
    {
      key: "text",
      label: "Texte",
      hint: "Balise + tokens",
      icon: IconType,
      accent: "#7c3aed",
    },
    {
      key: "list",
      label: "Liste",
      hint: "Puces (ul)",
      icon: IconAlignLeft,
      accent: "#0ea5e9",
    },
    {
      key: "image",
      label: "Image",
      hint: "URL + alt + légende",
      icon: IconImage,
      accent: "#10b981",
    },
    {
      key: "video",
      label: "Vidéo",
      hint: "YouTube/Vimeo…",
      icon: IconVideo,
      accent: "#f59e0b",
    },
    {
      key: "embed",
      label: "Média",
      hint: "PDF / iframe…",
      icon: IconLink,
      accent: "#06b6d4",
    },
    {
      key: "table",
      label: "Tableau",
      hint: "CSV simple",
      icon: IconTable,
      accent: "#ec4899",
    },
  ];

  return (
    <Modal
      title="Ajouter un élément"
      icon={<IconPlus />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      <div className="Muted" style={{ marginBottom: 10 }}>
        Choisis le type d’élément à insérer dans la section.
      </div>
      <div className="TemplateGrid" role="list">
        {items.map((it) => {
          const Ico = it.icon || IconType;
          return (
            <button
              key={it.key}
              type="button"
              className="TemplateButton"
              onClick={() => onPick(it.key)}
              style={{ "--accent": it.accent || "#7c3aed" }}
            >
              <div className="TemplateButtonIcon" aria-hidden="true">
                <Ico />
              </div>
              <div className="TemplateButtonText">
                <div className="TemplateButtonTitle">{it.label}</div>
                <div className="TemplateButtonHint">{it.hint}</div>
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function SectionPickerModal({ templates, onPick, onClose }) {
  return (
    <Modal
      title="Ajouter une section"
      icon={<IconPlus />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      <div className="Muted" style={{ marginBottom: 10 }}>
        Choisis un template de section. Tu pourras ensuite personnaliser les
        blocs et le background.
      </div>
      <div className="TemplateGrid" role="list">
        {(templates || []).map((t) => {
          const Ico = t.icon || IconType;
          return (
            <button
              key={t.key}
              type="button"
              className="TemplateButton"
              onClick={() => onPick(t.key)}
              style={{ "--accent": t.accent || "#7c3aed" }}
            >
              <div className="TemplateButtonIcon" aria-hidden="true">
                <Ico />
              </div>
              <div className="TemplateButtonText">
                <div className="TemplateButtonTitle">{t.label}</div>
                <div className="TemplateButtonHint">{t.hint}</div>
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function TagPickerModal({ tags, selectedIds, onToggle, onClose, onManage }) {
  const [q, setQ] = useState("");

  const selected = useMemo(
    () => new Set((selectedIds || []).map((x) => Number(x))),
    [selectedIds]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return tags || [];
    return (tags || []).filter((t) =>
      String(t.name || "")
        .toLowerCase()
        .includes(query)
    );
  }, [q, tags]);

  return (
    <Modal
      title="Choisir des tags"
      icon={<IconTag />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onManage}
          >
            <span className="ButtonInline">
              <IconEdit /> Gérer les tags
            </span>
          </button>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      <div className="FormGrid">
        <div className="Field">
          <label className="Label">Rechercher</label>
          <input
            className="Input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom du tag…"
          />
        </div>
      </div>

      <div className="Field" style={{ marginTop: 12 }}>
        <div className="Label">Tags existants</div>
        <div className="CheckGrid" role="group" aria-label="Tags existants">
          {filtered.map((t) => (
            <label
              key={t.id}
              className="Check"
              style={t.color ? { "--accent": t.color } : undefined}
            >
              <input
                type="checkbox"
                checked={selected.has(Number(t.id))}
                onChange={() => onToggle?.(t.id)}
              />
              <span className="CheckMain">
                <span className="ColorDot" aria-hidden="true" />
                <span style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ fontSize: 16 }}>
                    {t.name}
                  </div>
                </span>
              </span>
            </label>
          ))}
          {filtered.length === 0 ? (
            <div className="Muted">Aucun tag.</div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function CategoryPickerModal({
  categories,
  selectedIds,
  onToggle,
  onClose,
  onManage,
}) {
  const [q, setQ] = useState("");

  const selected = useMemo(
    () => new Set((selectedIds || []).map((x) => Number(x))),
    [selectedIds]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return categories || [];
    return (categories || []).filter((c) =>
      String(c.name || "")
        .toLowerCase()
        .includes(query)
    );
  }, [q, categories]);

  return (
    <Modal
      title="Choisir des catégories"
      icon={<IconFolder />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onManage}
          >
            <span className="ButtonInline">
              <IconEdit /> Gérer les catégories
            </span>
          </button>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      <div className="FormGrid">
        <div className="Field">
          <label className="Label">Rechercher</label>
          <input
            className="Input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom de la catégorie…"
          />
        </div>
      </div>

      <div className="Field" style={{ marginTop: 12 }}>
        <div className="Label">Catégories existantes</div>
        <div
          className="CheckGrid"
          role="group"
          aria-label="Catégories existantes"
        >
          {filtered.map((c) => (
            <label key={c.id} className="Check">
              <input
                type="checkbox"
                checked={selected.has(Number(c.id))}
                onChange={() => onToggle?.(c.id)}
              />
              <span className="CheckMain">
                <span style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ fontSize: 16 }}>
                    {c.name}
                  </div>
                </span>
              </span>
            </label>
          ))}
          {filtered.length === 0 ? (
            <div className="Muted">Aucune catégorie.</div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function TagsManager({ siteId, tags, onClose, onRefresh }) {
  const [draft, setDraft] = useState({ name: "", color: "#7c3aed" });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  return (
    <Modal
      title="Tags"
      icon={<IconEdit />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      {error ? (
        <div className="Error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="Badges" style={{ justifyContent: "flex-start" }}>
        <button
          type="button"
          className="Button"
          onClick={() => {
            setError("");
            setCreating(true);
          }}
        >
          <span className="ButtonInline">
            <IconPlus /> Créer un tag
          </span>
        </button>
      </div>

      <div className="List" role="list">
        {(tags || []).map((t) => (
          <div key={t.id} className="ListItem" role="listitem">
            <div style={{ minWidth: 0 }}>
              <div className="ProjectName">
                <span
                  className="ColorDot"
                  aria-hidden="true"
                  style={{ "--accent": t.color }}
                />
                <span className="OneLine" style={{ minWidth: 0 }}>
                  {t.name}
                </span>
              </div>
            </div>
            <div className="Badges">
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setEditing({ ...t })}
              >
                <span className="ButtonInline">
                  <IconEdit /> Modifier
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--danger Button--icon"
                onClick={async () => {
                  await api.projectTagDelete({
                    site_id: Number(siteId),
                    id: Number(t.id),
                  });
                  onRefresh();
                }}
                title="Supprimer"
                aria-label="Supprimer"
              >
                <IconTrash />
              </button>
            </div>
          </div>
        ))}
        {(tags || []).length === 0 ? (
          <div className="Muted">Aucun tag.</div>
        ) : null}
      </div>

      {creating ? (
        <Modal
          title="Créer un tag"
          icon={<IconPlus />}
          onClose={() => setCreating(false)}
          submitOnEnter
          onSubmit={async () => {
            setError("");
            const res = await api.projectTagCreate({
              site_id: Number(siteId),
              name: draft.name,
              color: draft.color,
            });
            if (!res.ok) {
              setError(res.error || "Erreur");
              return;
            }
            setDraft({ name: "", color: "#7c3aed" });
            setCreating(false);
            onRefresh();
          }}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setCreating(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  setError("");
                  const res = await api.projectTagCreate({
                    site_id: Number(siteId),
                    name: draft.name,
                    color: draft.color,
                  });
                  if (!res.ok) {
                    setError(res.error || "Erreur");
                    return;
                  }
                  setDraft({ name: "", color: "#7c3aed" });
                  setCreating(false);
                  onRefresh();
                }}
              >
                Créer
              </button>
            </>
          }
        >
          {error ? (
            <div className="Error" role="alert">
              {error}
            </div>
          ) : null}
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Nom</label>
              <input
                className="Input"
                value={draft.name}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, name: e.target.value }))
                }
                placeholder="Nouveau tag"
              />
            </div>
            <div className="Field">
              <label className="Label">Couleur</label>
              <ColorPicker
                value={draft.color}
                onChange={(color) => setDraft((d) => ({ ...d, color }))}
                placeholder="#7c3aed"
              />
            </div>
          </div>
        </Modal>
      ) : null}

      {editing ? (
        <Modal
          title="Modifier un tag"
          icon={<IconEdit />}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setEditing(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  const res = await api.projectTagUpdate({
                    site_id: Number(siteId),
                    id: Number(editing.id),
                    name: editing.name,
                    color: editing.color,
                  });
                  if (!res.ok) return;
                  setEditing(null);
                  onRefresh();
                }}
              >
                Enregistrer
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Nom</label>
              <input
                className="Input"
                value={editing.name}
                onChange={(e) =>
                  setEditing((t) => ({ ...t, name: e.target.value }))
                }
              />
            </div>
            <div className="Field">
              <label className="Label">Couleur</label>
              <ColorPicker
                value={editing.color}
                onChange={(color) => setEditing((t) => ({ ...t, color }))}
                placeholder="#7c3aed"
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}

function CategoriesManager({ siteId, categories, onClose, onRefresh }) {
  const [draft, setDraft] = useState({ name: "" });
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  return (
    <Modal
      title="Catégories"
      icon={<IconFolder />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      {error ? (
        <div className="Error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="FormGrid">
        <div className="Field">
          <label className="Label">Nom</label>
          <input
            className="Input"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Portfolio"
          />
        </div>
      </div>

      <div className="Badges" style={{ justifyContent: "flex-start" }}>
        <button
          type="button"
          className="Button"
          onClick={async () => {
            setError("");
            const res = await api.projectCategoryCreate({
              site_id: Number(siteId),
              name: draft.name,
            });
            if (!res.ok) {
              setError(res.error || "Erreur");
              return;
            }
            setDraft({ name: "" });
            onRefresh();
          }}
        >
          <span className="ButtonInline">
            <IconPlus /> Ajouter
          </span>
        </button>
      </div>

      <div className="List" role="list">
        {(categories || []).map((c) => (
          <div key={c.id} className="ListItem" role="listitem">
            <div style={{ minWidth: 0 }}>
              <div className="UserName" style={{ fontSize: 16 }}>
                {c.name}
              </div>
            </div>
            <div className="Badges">
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setEditing({ ...c })}
              >
                <span className="ButtonInline">
                  <IconEdit /> Modifier
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--danger Button--icon"
                onClick={async () => {
                  await api.projectCategoryDelete({
                    site_id: Number(siteId),
                    id: Number(c.id),
                  });
                  onRefresh();
                }}
                title="Supprimer"
                aria-label="Supprimer"
              >
                <IconTrash />
              </button>
            </div>
          </div>
        ))}
        {(categories || []).length === 0 ? (
          <div className="Muted">Aucune catégorie.</div>
        ) : null}
      </div>

      {editing ? (
        <Modal
          title="Modifier une catégorie"
          icon={<IconEdit />}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setEditing(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  const res = await api.projectCategoryUpdate({
                    site_id: Number(siteId),
                    id: Number(editing.id),
                    name: editing.name,
                  });
                  if (!res.ok) return;
                  setEditing(null);
                  onRefresh();
                }}
              >
                Enregistrer
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Nom</label>
              <input
                className="Input"
                value={editing.name}
                onChange={(e) =>
                  setEditing((c) => ({ ...c, name: e.target.value }))
                }
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}

function StylesManager({ siteId, styles, onClose, onRefresh }) {
  const [draft, setDraft] = useState({ name: "", class_name: "" });
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  return (
    <Modal
      title="Styles"
      icon={<IconLayers />}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="Button Button--ghost"
            onClick={onClose}
          >
            Fermer
          </button>
        </>
      }
    >
      {error ? (
        <div className="Error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="Muted" style={{ marginBottom: 10 }}>
        Un style correspond à une classe CSS (ou liste de classes) utilisée plus
        tard lors du rendu côté site.
      </div>

      <div className="FormGrid">
        <div className="Field">
          <label className="Label">Nom</label>
          <input
            className="Input"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Texte plus petit"
          />
        </div>
        <div className="Field">
          <label className="Label">Classe CSS</label>
          <input
            className="Input"
            value={draft.class_name}
            onChange={(e) =>
              setDraft((d) => ({ ...d, class_name: e.target.value }))
            }
            placeholder="text-sm"
          />
        </div>
      </div>

      <div className="Badges" style={{ justifyContent: "flex-start" }}>
        <button
          type="button"
          className="Button"
          onClick={async () => {
            setError("");
            const res = await api.projectStyleCreate({
              site_id: Number(siteId),
              name: draft.name,
              class_name: draft.class_name,
            });
            if (!res.ok) {
              setError(res.error || "Erreur");
              return;
            }
            setDraft({ name: "", class_name: "" });
            onRefresh();
          }}
        >
          <span className="ButtonInline">
            <IconPlus /> Ajouter
          </span>
        </button>
      </div>

      <div className="List" role="list">
        {(styles || []).map((st) => (
          <div key={st.id} className="ListItem" role="listitem">
            <div style={{ minWidth: 0 }}>
              <div className="UserName">{st.name}</div>
              <div className="Muted">{st.class_name}</div>
            </div>
            <div className="Badges">
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setEditing({ ...st })}
              >
                <span className="ButtonInline">
                  <IconEdit /> Modifier
                </span>
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--danger Button--icon"
                onClick={async () => {
                  await api.projectStyleDelete({
                    site_id: Number(siteId),
                    id: Number(st.id),
                  });
                  onRefresh();
                }}
                title="Supprimer"
                aria-label="Supprimer"
              >
                <IconTrash />
              </button>
            </div>
          </div>
        ))}
        {(styles || []).length === 0 ? (
          <div className="Muted">Aucun style.</div>
        ) : null}
      </div>

      {editing ? (
        <Modal
          title="Modifier un style"
          icon={<IconEdit />}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setEditing(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  const res = await api.projectStyleUpdate({
                    site_id: Number(siteId),
                    id: Number(editing.id),
                    name: editing.name,
                    class_name: editing.class_name,
                  });
                  if (!res.ok) return;
                  setEditing(null);
                  onRefresh();
                }}
              >
                Enregistrer
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Nom</label>
              <input
                className="Input"
                value={editing.name}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, name: e.target.value }))
                }
              />
            </div>
            <div className="Field">
              <label className="Label">Classe CSS</label>
              <input
                className="Input"
                value={editing.class_name}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, class_name: e.target.value }))
                }
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
